// src/api/client.ts
import { isPaginatedResponse } from "@plcy-io/types";
import axios from "axios";
import axiosRetry from "axios-retry";

// src/logging/FrontendLogger.ts
var LOG_LEVEL_RANK = {
  debug: 0,
  info: 1,
  warn: 2,
  error: 3
};
var DEFAULT_REDACT_FIELDS = [
  "email",
  "ssn",
  "socialSecurityNumber",
  "phone",
  "phoneNumber",
  "creditCard",
  "cardNumber",
  "password",
  "secret",
  "token"
];
var DEFAULT_SAMPLING_RATES = {
  error: 1,
  warn: 1,
  info: 0.1,
  debug: 0.1
};
var FrontendLogger = class {
  endpoint;
  batchSize;
  flushIntervalMs;
  minShipLevel;
  samplingRates;
  redactFields;
  maxRetries;
  component;
  defaultContext;
  batch = [];
  flushTimer = null;
  destroyed = false;
  /**
   * Create a new frontend logger
   *
   * @param config - Logger configuration
   */
  constructor(configOrComponent = {}) {
    const config = typeof configOrComponent === "string" ? { component: configOrComponent } : configOrComponent;
    this.endpoint = config.endpoint ?? "/api/v1/logs/ingest";
    this.batchSize = config.batchSize ?? 50;
    this.flushIntervalMs = config.flushIntervalMs ?? 5e3;
    this.maxRetries = config.maxRetries ?? 3;
    this.redactFields = config.redactFields ?? DEFAULT_REDACT_FIELDS;
    this.samplingRates = {
      ...DEFAULT_SAMPLING_RATES,
      ...config.samplingRates
    };
    if (config.minShipLevel !== void 0) {
      this.minShipLevel = config.minShipLevel;
    } else {
      const envLevel = import.meta["env"]?.["VITE_FRONTEND_LOG_MIN_LEVEL"];
      if (envLevel !== void 0 && envLevel in LOG_LEVEL_RANK) {
        this.minShipLevel = envLevel;
      } else if (import.meta["env"]?.["PROD"]) {
        this.minShipLevel = "warn";
      } else {
        this.minShipLevel = "debug";
      }
    }
    if (config.component !== void 0) {
      this.component = config.component;
    }
    this.defaultContext = config.defaultContext ?? {};
    this.scheduleFlush();
  }
  /**
   * Log error message
   *
   * @param message - Error message (supports printf-style format strings)
   * @param args - Printf-style args or a single Record context object
   *
   * @public
   */
  error(message, ...args) {
    this.log("error", message, ...args);
  }
  /**
   * Log warning message
   *
   * @param message - Warning message (supports printf-style format strings)
   * @param args - Printf-style args or a single Record context object
   *
   * @public
   */
  warn(message, ...args) {
    this.log("warn", message, ...args);
  }
  /**
   * Log info message
   *
   * @param message - Info message (supports printf-style format strings)
   * @param args - Printf-style args or a single Record context object
   *
   * @public
   */
  info(message, ...args) {
    this.log("info", message, ...args);
  }
  /**
   * Log debug message (development only)
   *
   * @remarks
   * Debug logs are only shown in development console and are subject to
   * severity filtering / sampling before being sent to the backend.
   *
   * @param message - Debug message (supports printf-style format strings)
   * @param args - Printf-style args or a single Record context object
   *
   * @public
   */
  debug(message, ...args) {
    if (import.meta["env"]?.["DEV"]) {
      this.log("debug", message, ...args);
    }
  }
  /**
   * Flush remaining batched entries and stop the periodic timer.
   *
   * @remarks
   * Call this during SPA cleanup (route change, component unmount) to ensure
   * no log entries are silently dropped.
   *
   * @public
   */
  async destroy() {
    this.destroyed = true;
    this.stopFlushTimer();
    await this.flushBatch();
  }
  // ---------------------------------------------------------------------------
  // Private implementation
  // ---------------------------------------------------------------------------
  /**
   * Internal logging implementation
   *
   * @param level - Log level
   * @param message - Log message
   * @param optionsOrArg - Optional internal options or first caller arg
   * @param rest - Remaining caller args
   * @internal
   */
  log(level, message, optionsOrArg, ...rest) {
    let args;
    if (optionsOrArg !== void 0) {
      args = [optionsOrArg, ...rest];
    } else {
      args = rest;
    }
    let context;
    if (args.length === 1 && args[0] !== null && typeof args[0] === "object" && !(args[0] instanceof Error) && !Array.isArray(args[0])) {
      context = args[0];
      args = [];
    }
    const mergedContext = {
      ...this.defaultContext,
      ...context
    };
    if (this.component !== void 0) {
      mergedContext.component = this.component;
    }
    this.logToConsole(level, message, mergedContext, args);
    if (!import.meta["env"]?.["PROD"]) {
      return;
    }
    if (LOG_LEVEL_RANK[level] < LOG_LEVEL_RANK[this.minShipLevel]) {
      return;
    }
    const rate = this.samplingRates[level] ?? 1;
    if (Math.random() >= rate) {
      return;
    }
    const redactedContext = this.redactContext(mergedContext);
    const entry = {
      level,
      message,
      timestamp: (/* @__PURE__ */ new Date()).toISOString(),
      context: redactedContext
    };
    this.addToBatch(entry);
  }
  /**
   * Add an entry to the batch and flush if the batch is full.
   *
   * @internal
   */
  addToBatch(entry) {
    this.batch.push(entry);
    if (this.batch.length >= this.batchSize) {
      void this.flushBatch();
    }
  }
  /**
   * Schedule the next periodic flush.
   *
   * @internal
   */
  scheduleFlush() {
    this.flushTimer = setInterval(() => {
      void this.flushBatch();
    }, this.flushIntervalMs);
  }
  /**
   * Clear the flush timer.
   *
   * @internal
   */
  stopFlushTimer() {
    if (this.flushTimer !== null) {
      clearInterval(this.flushTimer);
      this.flushTimer = null;
    }
  }
  /**
   * Send all buffered entries to the backend and clear the batch.
   *
   * @internal
   */
  async flushBatch() {
    if (this.batch.length === 0) {
      return;
    }
    const entries = this.batch.splice(0, this.batch.length);
    await this.sendBatchWithRetry(entries, 0);
  }
  /**
   * Send a batch with exponential-backoff retry.
   *
   * @param entries - Log entries to send
   * @param attempt - Zero-based attempt number
   * @internal
   */
  async sendBatchWithRetry(entries, attempt) {
    let body;
    try {
      body = JSON.stringify(entries);
    } catch (serializationError) {
      const errorMsg = serializationError instanceof Error ? serializationError.message : String(serializationError);
      console.error(
        "[FrontendLogger] Failed to serialize log batch:",
        errorMsg
      );
      return;
    }
    try {
      const response = await fetch(this.endpoint, {
        method: "POST",
        headers: {
          "Content-Type": "application/json"
        },
        body
      });
      if (!response.ok) {
        throw new Error(
          `HTTP ${response.status} ${response.statusText ?? ""}`
        );
      }
    } catch (error) {
      const errorMsg = error instanceof Error ? error.message : String(error);
      if (attempt < this.maxRetries - 1) {
        if (this.destroyed) {
          return;
        }
        const delayMs = Math.pow(2, attempt) * 1e3;
        await new Promise((resolve) => setTimeout(resolve, delayMs));
        await this.sendBatchWithRetry(entries, attempt + 1);
      } else {
        console.error(
          "[FrontendLogger] Failed to send log batch after",
          this.maxRetries,
          "attempts:",
          errorMsg,
          { endpoint: this.endpoint, count: entries.length }
        );
      }
    }
  }
  /**
   * Log to browser console
   *
   * @param level - Log level
   * @param message - Log message
   * @param context - Log context (unredacted)
   * @param args - Printf-style args
   * @internal
   */
  logToConsole(level, message, context, args = []) {
    const consoleMethod = console[level] ?? console.log;
    if (args.length > 0) {
      consoleMethod(message, ...args, context);
    } else {
      consoleMethod(message, context);
    }
  }
  /**
   * Recursively redact configured PII field names from a context object.
   *
   * @remarks
   * Returns a deep clone with sensitive values replaced by '[REDACTED]'.
   * The original object is not mutated.
   *
   * @param obj - Object to redact
   * @returns Redacted copy
   * @internal
   */
  redactContext(obj, seen = /* @__PURE__ */ new WeakSet()) {
    if (seen.has(obj)) return {};
    seen.add(obj);
    const result = {};
    for (const key of Object.keys(obj)) {
      const value = obj[key];
      if (this.redactFields.includes(key)) {
        result[key] = "[REDACTED]";
      } else if (value !== null && typeof value === "object" && !Array.isArray(value) && !(value instanceof Error)) {
        result[key] = this.redactContext(
          value,
          seen
        );
      } else {
        result[key] = value;
      }
    }
    return result;
  }
};

// src/utils/storage.ts
var logger = new FrontendLogger({ component: "frontend-common:storage" });
var STORAGE_KEYS = {
  TENANT_ID: "plcy-active-tenant-id",
  THEME_MODE: "plcy-theme-mode",
  SIDEBAR_OPEN: "plcy-sidebar-open",
  CORRELATION_ID: "plcy-correlation-id"
};
var StorageError = class extends Error {
  /**
   * Storage key that caused the error
   *
   * @public
   */
  key;
  /**
   * Original error that caused the failure
   *
   * @public
   */
  cause;
  /**
   * Create Storage Error
   *
   * @param message - Error message
   * @param key - Storage key
   * @param cause - Original error
   *
   * @public
   */
  constructor(message, key, cause) {
    super(message);
    this.name = "StorageError";
    this.key = key;
    this.cause = cause;
  }
};
function isLocalStorageAvailable() {
  try {
    const testKey = "__plcy_storage_test__";
    localStorage.setItem(testKey, testKey);
    localStorage.removeItem(testKey);
    return true;
  } catch {
    return false;
  }
}
function getItem(key) {
  if (!isLocalStorageAvailable()) {
    logger.debug("localStorage not available");
    return null;
  }
  try {
    return localStorage.getItem(key);
  } catch (error) {
    logger.debug("Error reading from localStorage", { key, error });
    throw new StorageError(
      `Failed to read from localStorage: ${key}`,
      key,
      error
    );
  }
}
function setItem(key, value) {
  if (!isLocalStorageAvailable()) {
    logger.debug("localStorage not available, cannot set item");
    return;
  }
  try {
    localStorage.setItem(key, value);
    logger.debug("Saved to localStorage", { key });
  } catch (error) {
    logger.debug("Error writing to localStorage", { key, error });
    throw new StorageError(
      `Failed to write to localStorage: ${key}`,
      key,
      error
    );
  }
}
function removeItem(key) {
  if (!isLocalStorageAvailable()) {
    logger.debug("localStorage not available, cannot remove item");
    return;
  }
  try {
    localStorage.removeItem(key);
    logger.debug("Removed from localStorage", { key });
  } catch (error) {
    logger.debug("Error removing from localStorage", { key, error });
    throw new StorageError(
      `Failed to remove from localStorage: ${key}`,
      key,
      error
    );
  }
}
function getTenantId() {
  return getItem(STORAGE_KEYS.TENANT_ID);
}
function setTenantId(tenantId) {
  setItem(STORAGE_KEYS.TENANT_ID, tenantId);
}
function removeTenantId() {
  removeItem(STORAGE_KEYS.TENANT_ID);
}
function getThemeMode(defaultMode = "system") {
  const stored = getItem(STORAGE_KEYS.THEME_MODE);
  if (stored === "light" || stored === "dark" || stored === "system") {
    return stored;
  }
  return defaultMode;
}
function setThemeMode(mode) {
  setItem(STORAGE_KEYS.THEME_MODE, mode);
}
function getSidebarOpen(defaultOpen = true) {
  const stored = getItem(STORAGE_KEYS.SIDEBAR_OPEN);
  if (stored !== null) {
    try {
      const parsed = JSON.parse(stored);
      if (typeof parsed === "boolean") {
        return parsed;
      }
    } catch (error) {
      logger.debug("Error parsing sidebar state", { error });
    }
  }
  return defaultOpen;
}
function setSidebarOpen(isOpen) {
  setItem(STORAGE_KEYS.SIDEBAR_OPEN, JSON.stringify(isOpen));
}
function getCorrelationId() {
  let correlationId = getItem(STORAGE_KEYS.CORRELATION_ID);
  if (correlationId === null) {
    correlationId = crypto.randomUUID();
    setItem(STORAGE_KEYS.CORRELATION_ID, correlationId);
  }
  return correlationId;
}
function refreshCorrelationId() {
  const newId = crypto.randomUUID();
  setItem(STORAGE_KEYS.CORRELATION_ID, newId);
  return newId;
}
function clearAllStorage() {
  logger.debug("Clearing all application storage");
  removeTenantId();
  removeItem(STORAGE_KEYS.THEME_MODE);
  removeItem(STORAGE_KEYS.SIDEBAR_OPEN);
  removeItem(STORAGE_KEYS.CORRELATION_ID);
}

// src/api/client.ts
var logger2 = new FrontendLogger({ component: "frontend-common:api" });
var ApiError = class extends Error {
  /**
   * HTTP status code
   *
   * @public
   */
  status;
  /**
   * Error code (e.g., 'VALIDATION_ERROR', 'NOT_FOUND')
   *
   * @public
   */
  code;
  /**
   * Response data from API
   *
   * @public
   */
  data;
  /**
   * Field-level validation errors
   *
   * @public
   */
  fieldErrors;
  /**
   * Canonical wire-level error details (the envelope's `error.details`
   * field), preserved verbatim so consumers reading `.details` on the
   * caught {@link ApiError} see the same payload the server sent.
   *
   * @public
   */
  details;
  /**
   * Create API Error
   *
   * @param message - Error message
   * @param status - HTTP status code
   * @param code - Error code
   * @param data - Optional response data
   * @param fieldErrors - Optional field-level errors
   * @param details - Optional canonical wire-level error details
   *
   * @public
   */
  constructor(message, status, code, data, fieldErrors, details) {
    super(message);
    this.name = "ApiError";
    this.status = status;
    if (code !== void 0) {
      this.code = code;
    }
    if (data !== void 0) {
      this.data = data;
    }
    if (fieldErrors !== void 0) {
      this.fieldErrors = fieldErrors;
    }
    if (details !== void 0) {
      this.details = details;
    } else if (data !== void 0) {
      this.details = data;
    }
  }
};
function isApiError(error) {
  return error instanceof ApiError;
}
function isCanonicalErrorEnvelope(data) {
  return typeof data === "object" && data !== null && data.success === false && typeof data.error === "object" && data.error !== null;
}
function tryGetViteEnv() {
  try {
    const globalEnv = globalThis["__VITE_ENV__"];
    if (globalEnv !== void 0) {
      return globalEnv;
    }
    try {
      const getEnv = new Function(
        'return typeof import.meta !== "undefined" && import.meta.env'
      );
      const env = getEnv();
      if (env) {
        return env;
      }
    } catch {
    }
    return void 0;
  } catch {
    return void 0;
  }
}
function getBaseUrl(configUrl) {
  if (configUrl !== void 0 && configUrl !== "") {
    return configUrl;
  }
  const env = tryGetViteEnv();
  if (env !== void 0) {
    const envUrl = env["VITE_API_BASE_URL"];
    if (typeof envUrl === "string" && envUrl !== "") {
      return envUrl;
    }
    if (env["DEV"] === true) {
      return "";
    }
  }
  return "";
}
function createApiClient(config = {}) {
  const {
    baseURL,
    timeout = 3e4,
    retries = 3,
    onAuthFailure,
    headers: customHeaders = {}
  } = config;
  const client = axios.create({
    baseURL: getBaseUrl(baseURL),
    timeout,
    headers: {
      "Content-Type": "application/json",
      ...customHeaders
    },
    withCredentials: true
    // Enable HttpOnly cookies
  });
  axiosRetry(client, {
    retries,
    retryDelay: (retryCount) => axiosRetry.exponentialDelay(retryCount),
    retryCondition: (error) => {
      const status = error.response?.status;
      if (status !== void 0 && (status === 401 || status === 403)) {
        return false;
      }
      const is5xxError = status !== void 0 && status >= 500;
      return axiosRetry.isNetworkOrIdempotentRequestError(error) || is5xxError;
    }
  });
  client.interceptors.request.use(
    (requestConfig) => {
      const tenantId = getTenantId();
      const correlationId = getCorrelationId();
      const newHeaders = requestConfig.headers;
      if (tenantId !== null && tenantId !== "") {
        newHeaders["X-Tenant-ID"] = tenantId;
      }
      newHeaders["X-Correlation-ID"] = correlationId;
      const updatedConfig = {
        ...requestConfig,
        headers: newHeaders
      };
      const method = updatedConfig.method?.toUpperCase() ?? "GET";
      const url = updatedConfig.url ?? "";
      logger2.debug("Request", {
        method,
        url,
        tenant: tenantId ?? "none",
        correlation: correlationId
      });
      return updatedConfig;
    },
    (error) => {
      logger2.debug("Request interceptor error", { error });
      const rejection = error instanceof Error ? error : new Error("Request interceptor failed");
      return Promise.reject(rejection);
    }
  );
  let isRefreshing = false;
  let refreshSubscribers = [];
  const onRefreshed = (error) => {
    refreshSubscribers.forEach((callback) => {
      callback(error);
    });
    refreshSubscribers = [];
  };
  const addRefreshSubscriber = (callback) => {
    refreshSubscribers.push(callback);
  };
  client.interceptors.response.use(
    (response) => {
      const method = response.config.method?.toUpperCase() ?? "GET";
      const url = response.config.url ?? "";
      const { status } = response;
      logger2.debug("Response", { method, url, status });
      return response;
    },
    async (error) => {
      if (error instanceof ApiError) {
        return Promise.reject(error);
      }
      if (!axios.isAxiosError(error)) {
        const errorMessage = error instanceof Error ? error.message : "Unknown error";
        logger2.debug("Non-axios error", { errorMessage });
        return Promise.reject(new Error(String(error)));
      }
      const axiosError = error;
      const originalRequest = axiosError.config;
      const requestUrl = originalRequest.url ?? "";
      const isAuthVerification = requestUrl.includes("/auth/verify") || requestUrl.includes("/auth/refresh");
      if (axiosError.response?.status === 401 && !originalRequest._retry && !isAuthVerification) {
        logger2.debug("Received 401 - attempting token refresh", { requestUrl });
        if (isRefreshing) {
          logger2.debug("Token refresh already in progress, queuing request");
          return new Promise((resolve, reject) => {
            addRefreshSubscriber((refreshError) => {
              if (refreshError !== void 0) {
                reject(refreshError);
              } else {
                resolve(client.request(originalRequest));
              }
            });
          });
        }
        isRefreshing = true;
        originalRequest._retry = true;
        try {
          logger2.debug("Calling refresh token endpoint");
          const refreshUrl = getBaseUrl(baseURL) !== "" ? `${getBaseUrl(baseURL)}/api/v1/auth/refresh` : "/api/v1/auth/refresh";
          const response = await fetch(refreshUrl, {
            method: "POST",
            credentials: "include"
          });
          if (response.ok) {
            logger2.debug(
              "Token refreshed successfully, retrying original request"
            );
            isRefreshing = false;
            onRefreshed();
            return await client.request(originalRequest);
          }
          logger2.debug("Token refresh failed with status", {
            status: response.status
          });
          isRefreshing = false;
          onRefreshed(new Error("Token refresh failed"));
          if (onAuthFailure !== void 0) {
            logger2.debug("Calling onAuthFailure callback");
            onAuthFailure();
          }
          return Promise.reject(axiosError);
        } catch (refreshError) {
          logger2.debug("Token refresh threw error", { refreshError });
          isRefreshing = false;
          onRefreshed(
            refreshError instanceof Error ? refreshError : new Error("Token refresh failed")
          );
          if (onAuthFailure !== void 0) {
            logger2.debug("Calling onAuthFailure callback after error");
            onAuthFailure();
          }
          return Promise.reject(axiosError);
        }
      }
      if (axiosError.response !== void 0) {
        const { status } = axiosError.response;
        const data = axiosError.response.data ?? {};
        logger2.debug("Response error", { status, data });
        if (isCanonicalErrorEnvelope(data)) {
          const message2 = data.error.message ?? `API request failed with status ${status.toString()}`;
          throw new ApiError(
            message2,
            status,
            data.error.code,
            data,
            data.errors,
            data.error.details
          );
        }
        const message = typeof data.message === "string" ? data.message : `API request failed with status ${status.toString()}`;
        const { code } = data;
        const fieldErrors = data.errors;
        throw new ApiError(message, status, code, data, fieldErrors);
      }
      if (axiosError.request !== void 0) {
        logger2.debug("Network error: no response received", {
          message: axiosError.message
        });
        throw new ApiError(
          "Network error - no response received",
          0,
          "NETWORK_ERROR"
        );
      }
      logger2.debug("Request setup error", { message: axiosError.message });
      throw new ApiError(axiosError.message, 0, "REQUEST_ERROR");
    }
  );
  return client;
}
function unwrap(response) {
  if (response === null || typeof response !== "object") {
    throw new Error(
      "apiClient.unwrap: expected an object envelope, received a non-object response"
    );
  }
  const obj = response;
  if (obj["success"] === false) {
    const rawError = obj["error"];
    if (typeof rawError !== "object" || rawError === null) {
      throw new Error(
        "apiClient.unwrap: non-canonical error envelope (missing `error`)"
      );
    }
    const wireError = rawError;
    throw new ApiError(
      wireError.message ?? "API request failed",
      0,
      wireError.code,
      wireError.details,
      void 0,
      wireError.details
    );
  }
  if (obj["success"] === true && "data" in obj) {
    return obj["data"];
  }
  if (isPaginatedResponse(response)) {
    return response;
  }
  throw new Error(
    "apiClient.unwrap: received a non-canonical envelope shape (expected ApiResponse or PaginatedResponse)"
  );
}
function isScalarFilterValue(value) {
  return typeof value === "string" || typeof value === "number" || typeof value === "boolean";
}
function buildListUrl(base, opts) {
  const { pagination, sort, filters, paramNames } = opts;
  const params = new URLSearchParams();
  if (pagination !== void 0) {
    params.set("page", String(pagination.page));
    params.set("pageSize", String(pagination.pageSize));
  }
  if (sort !== void 0) {
    const sortByParam = paramNames?.["sortBy"] ?? "sortBy";
    const sortOrderParam = paramNames?.["sortOrder"] ?? "sortOrder";
    params.set(sortByParam, sort.field);
    params.set(sortOrderParam, sort.direction);
  }
  if (filters !== void 0) {
    for (const [key, value] of Object.entries(filters)) {
      if (value === null || value === void 0) {
        continue;
      }
      if (Array.isArray(value)) {
        for (const item of value) {
          if (!isScalarFilterValue(item)) {
            throw new Error(
              `apiClient.buildListUrl: filter "${key}" contains a non-scalar array item (${typeof item}); only string|number|boolean are supported`
            );
          }
          params.append(key, String(item));
        }
      } else if (!isScalarFilterValue(value)) {
        throw new Error(
          `apiClient.buildListUrl: filter "${key}" is a non-scalar value (${typeof value}); nested/range objects are not supported \u2014 pass discrete scalar keys instead`
        );
      } else {
        params.append(key, String(value));
      }
    }
  }
  const query = params.toString();
  return query === "" ? base : `${base}?${query}`;
}
var apiClient = Object.assign(createApiClient(), {
  unwrap,
  buildListUrl
});
function normalizeEmptyEnvelope(data) {
  if (data === void 0 || data === null || data === "") {
    return { success: true, data: void 0 };
  }
  return data;
}
function createApiClientPort(client = apiClient) {
  return {
    get: async (url) => (await client.get(url)).data,
    post: async (url, body) => {
      const { data } = await client.post(url, body);
      return normalizeEmptyEnvelope(data);
    },
    patch: async (url, body) => {
      const { data } = await client.patch(url, body);
      return normalizeEmptyEnvelope(data);
    },
    put: async (url, body) => {
      const { data } = await client.put(url, body);
      return normalizeEmptyEnvelope(data);
    },
    delete: async (url) => {
      const { data } = await client.delete(url);
      return normalizeEmptyEnvelope(data);
    },
    unwrap,
    buildListUrl
  };
}
var apiClientPort = createApiClientPort();

// src/api/query-client.ts
import { QueryClient } from "@tanstack/react-query";
function mergeConfig(base, overrides) {
  return {
    ...base,
    ...overrides,
    defaultOptions: {
      ...base.defaultOptions,
      ...overrides.defaultOptions,
      queries: {
        ...base.defaultOptions?.queries,
        ...overrides.defaultOptions?.queries
      },
      mutations: {
        ...base.defaultOptions?.mutations,
        ...overrides.defaultOptions?.mutations
      }
    }
  };
}
function createQueryClient(overrides = {}) {
  const base = {
    defaultOptions: {
      queries: {
        staleTime: 3e4,
        retry: 1,
        refetchOnWindowFocus: false
      }
    }
  };
  return new QueryClient(mergeConfig(base, overrides));
}
function createModuleQueryClient(overrides = {}) {
  const base = {
    defaultOptions: {
      queries: {
        staleTime: 3e4,
        retry: 2,
        refetchOnWindowFocus: false
      },
      mutations: {
        retry: 1
      }
    }
  };
  return new QueryClient(mergeConfig(base, overrides));
}
function createStoryQueryClient(overrides = {}) {
  const base = {
    defaultOptions: {
      queries: {
        retry: false,
        staleTime: Infinity,
        refetchOnWindowFocus: false
      }
    }
  };
  return new QueryClient(mergeConfig(base, overrides));
}

// src/contexts/AuthContext.tsx
import React, {
  createContext,
  useContext,
  useState,
  useCallback,
  useEffect,
  useMemo
} from "react";
import { jsx } from "react/jsx-runtime";
function isMfaRequiredApiResponse(value) {
  return typeof value === "object" && value !== null && "mfaRequired" in value && value.mfaRequired === true;
}
var AuthContext = createContext(null);
function AuthProvider({
  children,
  onAuthFailure,
  initialUser = null,
  initialCapabilities = null
}) {
  const [authState, setAuthState] = useState(() => {
    console.log(
      "[AuthContext] Initial state - initialUser:",
      initialUser?.name ?? "null"
    );
    return {
      user: initialUser,
      capabilities: initialUser !== null ? initialCapabilities : null,
      isAuthenticated: initialUser !== null,
      isLoading: initialUser === null,
      // Load if no initial user
      error: null
    };
  });
  React.useEffect(() => {
    console.log("[AuthContext] State changed:", {
      userName: authState.user?.name ?? "null",
      isAuthenticated: authState.isAuthenticated,
      isLoading: authState.isLoading,
      hasError: authState.error !== null
    });
  }, [authState]);
  const verificationAttemptedRef = React.useRef(false);
  useEffect(() => {
    console.log(
      "[AuthContext] Verification effect triggered, initialUser:",
      initialUser?.name ?? "null"
    );
    if (initialUser !== null) {
      console.log("[AuthContext] Skipping verification - initialUser provided");
      return;
    }
    if (verificationAttemptedRef.current) {
      console.log("[AuthContext] Skipping verification - already attempted");
      return;
    }
    verificationAttemptedRef.current = true;
    console.log("[AuthContext] Starting verification...");
    const verifyAuthentication = async () => {
      try {
        console.log("[AuthContext] Calling /api/v1/auth/verify...");
        const response = await apiClient.get(
          "/api/v1/auth/verify"
        );
        console.log("[AuthContext] Verify response:", {
          id: response.data.user.id,
          email: response.data.user.email,
          name: response.data.user.name,
          tenantId: response.data.user.tenantId,
          hasCapabilities: response.data.capabilities !== void 0
        });
        if (response.data.user.tenantId !== void 0) {
          setTenantId(response.data.user.tenantId);
        } else {
          removeTenantId();
        }
        const user = {
          id: response.data.user.id,
          email: response.data.user.email,
          name: response.data.user.name,
          roles: response.data.user.roles,
          permissions: void 0,
          // Permissions not returned on verify (use capabilities instead)
          tenantId: response.data.user.tenantId
        };
        const capabilities = response.data.capabilities ?? null;
        console.log("[AuthContext] Setting authenticated user:", user.name);
        setAuthState({
          user,
          capabilities,
          isAuthenticated: true,
          isLoading: false,
          error: null
        });
      } catch (error) {
        console.log("[AuthContext] Verify error:", error);
        const isRateLimited = isApiError(error) && error.status === 429;
        if (isRateLimited) {
          console.log("[AuthContext] Rate limited - keeping current state");
          setAuthState((prev) => ({
            ...prev,
            isLoading: false,
            error: new Error("Too many requests. Please wait and try again.")
          }));
          return;
        }
        console.log("[AuthContext] Setting unauthenticated state");
        removeTenantId();
        setAuthState({
          user: null,
          capabilities: null,
          isAuthenticated: false,
          isLoading: false,
          error: null
        });
      }
    };
    void verifyAuthentication();
  }, [initialUser]);
  const login = useCallback(
    async (email, password) => {
      console.log("[AuthContext] Login attempt for:", email);
      setAuthState((prev) => ({ ...prev, isLoading: true, error: null }));
      try {
        const response = await apiClient.post("/api/v1/auth/login", {
          email,
          password
        });
        if (isMfaRequiredApiResponse(response.data)) {
          setAuthState((prev) => ({ ...prev, isLoading: false, error: null }));
          const {
            method,
            challengeId,
            cooldownSeconds,
            phoneMasked,
            reverificationRequired
          } = response.data;
          return {
            success: false,
            mfaRequired: true,
            method,
            challengeId,
            cooldownSeconds,
            phoneMasked,
            reverificationRequired
          };
        }
        console.log("[AuthContext] Login response received:", {
          success: response.data.success,
          userName: response.data.user.name
        });
        const userData = response.data.user;
        if (!response.data.success || userData === null) {
          const error = new Error("Invalid login response format from server");
          console.log("[AuthContext] Login failed - invalid response");
          setAuthState((prev) => ({
            ...prev,
            isLoading: false,
            error
          }));
          return { success: false, error };
        }
        const user = {
          id: userData.id,
          email: userData.email,
          name: userData.name ?? "",
          roles: userData.roles ?? [],
          permissions: userData.permissions,
          tenantId: userData.tenantId
        };
        if (user.tenantId !== void 0) {
          setTenantId(user.tenantId);
        } else {
          removeTenantId();
        }
        const capabilities = response.data.capabilities ?? null;
        console.log("[AuthContext] Login success - setting user:", user.name);
        setAuthState({
          user,
          capabilities,
          isAuthenticated: true,
          isLoading: false,
          error: null
        });
        return { success: true, data: user };
      } catch (error) {
        console.log("[AuthContext] Login error:", error);
        const loginError = error instanceof Error ? error : new Error("Unexpected error during login");
        setAuthState((prev) => ({
          ...prev,
          isLoading: false,
          error: loginError
        }));
        return { success: false, error: loginError };
      }
    },
    []
  );
  const completeMfaLogin = useCallback((session) => {
    const user = {
      id: session.user.id,
      email: session.user.email,
      name: session.user.name ?? "",
      roles: session.user.roles ?? [],
      permissions: session.user.permissions,
      tenantId: session.user.tenantId
    };
    if (user.tenantId !== void 0) {
      setTenantId(user.tenantId);
    } else {
      removeTenantId();
    }
    setAuthState({
      user,
      capabilities: session.capabilities ?? null,
      isAuthenticated: true,
      isLoading: false,
      error: null
    });
  }, []);
  const logout = useCallback(async () => {
    setAuthState((prev) => ({ ...prev, isLoading: true, error: null }));
    try {
      await apiClient.post("/api/v1/auth/logout");
      clearAllStorage();
      setAuthState({
        user: null,
        capabilities: null,
        isAuthenticated: false,
        isLoading: false,
        error: null
      });
      return { success: true, data: void 0 };
    } catch (error) {
      clearAllStorage();
      setAuthState({
        user: null,
        capabilities: null,
        isAuthenticated: false,
        isLoading: false,
        error: null
      });
      if (isApiError(error)) {
        return {
          success: false,
          error: new Error(`Logout failed: ${error.message}`)
        };
      }
      return {
        success: false,
        error: error instanceof Error ? error : new Error("Unknown error during logout")
      };
    }
  }, []);
  const refreshToken = useCallback(async () => {
    console.log(
      "[AuthContext] Manual refreshToken called, current user:",
      authState.user?.name ?? "null"
    );
    setAuthState((prev) => ({ ...prev, isLoading: true, error: null }));
    try {
      console.log("[AuthContext] Calling /api/v1/auth/refresh...");
      await apiClient.post("/api/v1/auth/refresh");
      console.log("[AuthContext] Refresh successful, preserving user state");
      setAuthState((prev) => {
        console.log(
          "[AuthContext] After refresh - preserving user:",
          prev.user?.name ?? "null"
        );
        return {
          ...prev,
          isLoading: false,
          error: null
        };
      });
      return { success: true, data: void 0 };
    } catch (error) {
      console.log("[AuthContext] Refresh error:", error);
      const refreshError = error instanceof Error ? error : new Error("Token refresh failed");
      setAuthState((prev) => ({
        ...prev,
        isLoading: false,
        error: refreshError
      }));
      if (onAuthFailure !== void 0) {
        console.log("[AuthContext] Calling onAuthFailure callback");
        onAuthFailure();
      }
      return { success: false, error: refreshError };
    }
  }, [onAuthFailure, authState.user?.name]);
  const hasPermission = useCallback(
    (permission) => {
      if (authState.user === null) {
        return false;
      }
      return authState.user.permissions?.includes(permission) ?? false;
    },
    [authState.user]
  );
  const hasRole = useCallback(
    (role) => {
      if (authState.user === null) {
        return false;
      }
      return authState.user.roles.includes(role);
    },
    [authState.user]
  );
  const hasAnyRole = useCallback(
    (roles) => {
      const { user } = authState;
      if (user === null) {
        return false;
      }
      return roles.some((role) => user.roles.includes(role));
    },
    [authState]
  );
  const hasAllPermissions = useCallback(
    (permissions) => {
      if (authState.user === null) {
        return false;
      }
      const userPermissions = authState.user.permissions ?? [];
      return permissions.every(
        (permission) => userPermissions.includes(permission)
      );
    },
    [authState.user]
  );
  const contextValue = useMemo(
    () => ({
      ...authState,
      capabilities: authState.capabilities,
      login,
      completeMfaLogin,
      logout,
      refreshToken,
      hasPermission,
      hasRole,
      hasAnyRole,
      hasAllPermissions
    }),
    [
      authState,
      login,
      completeMfaLogin,
      logout,
      refreshToken,
      hasPermission,
      hasRole,
      hasAnyRole,
      hasAllPermissions
    ]
  );
  return /* @__PURE__ */ jsx(AuthContext.Provider, { value: contextValue, children });
}
function useAuth() {
  const context = useContext(AuthContext);
  if (context === null) {
    throw new Error("useAuth must be used within an AuthProvider");
  }
  return context;
}
function useAuthState() {
  const { user, capabilities, isAuthenticated, isLoading, error } = useAuth();
  return { user, capabilities, isAuthenticated, isLoading, error };
}
function useUser() {
  const { user } = useAuth();
  return user;
}

// src/contexts/TenantContext.tsx
import React2, {
  createContext as createContext2,
  useContext as useContext2,
  useState as useState2,
  useCallback as useCallback2,
  useEffect as useEffect2,
  useMemo as useMemo2
} from "react";
import { jsx as jsx2 } from "react/jsx-runtime";
var TenantContext = createContext2(null);
function TenantProvider({
  children,
  initialTenant = null,
  initialAvailableTenants = [],
  onTenantRequired
}) {
  const { isAuthenticated, user } = useAuth();
  const [tenantState, setTenantState] = useState2(() => ({
    tenant: initialTenant,
    availableTenants: initialAvailableTenants,
    // Will be set by useEffect once loading completes
    isLoading: false,
    error: null
  }));
  const loadAttemptedRef = React2.useRef(false);
  const effectiveIsLoading = tenantState.isLoading || isAuthenticated && initialTenant === null && tenantState.tenant === null && !loadAttemptedRef.current;
  useEffect2(() => {
    if (!isAuthenticated) {
      loadAttemptedRef.current = false;
      setTenantState({
        tenant: null,
        availableTenants: [],
        isLoading: false,
        error: null
      });
      return;
    }
    if (initialTenant !== null) {
      return;
    }
    if (loadAttemptedRef.current) {
      return;
    }
    loadAttemptedRef.current = true;
    const loadTenant = async () => {
      setTenantState((prev) => ({ ...prev, isLoading: true, error: null }));
      try {
        const storedTenantId = getTenantId();
        const tenantId = storedTenantId ?? user?.tenantId;
        const availableResponse = await apiClient.get("/api/v1/tenants/available").catch(() => ({ data: { tenants: [] } }));
        const responseData = availableResponse.data;
        const tenantsArray = Array.isArray(
          responseData.tenants
        ) ? responseData.tenants : Array.isArray(
          responseData.data?.tenants
        ) ? responseData.data.tenants : [];
        const availableTenants = tenantsArray.map((t) => ({
          id: t.id,
          name: t.name,
          slug: t.slug,
          status: t.status,
          tier: t.tier,
          features: t.features,
          settings: t.settings,
          logoUrl: t.logoUrl,
          logoIconUrl: t.logoIconUrl,
          logoDarkUrl: t.logoDarkUrl,
          faviconUrl: t.faviconUrl,
          primaryColor: t.primaryColor,
          secondaryColor: t.secondaryColor
        }));
        if (tenantId !== void 0) {
          const response = await apiClient.get(
            `/api/v1/tenants/${tenantId}`
          );
          console.log("[TenantContext] Raw tenant response:", response.data);
          const tenantData = response.data.data ?? response.data;
          console.log("[TenantContext] Unwrapped tenant data:", tenantData);
          const tenant = {
            id: tenantData.id,
            name: tenantData.name,
            slug: tenantData.slug,
            status: tenantData.status,
            tier: tenantData.tier,
            features: tenantData.features,
            settings: tenantData.settings,
            logoUrl: tenantData.logoUrl,
            logoIconUrl: tenantData.logoIconUrl,
            logoDarkUrl: tenantData.logoDarkUrl,
            faviconUrl: tenantData.faviconUrl,
            primaryColor: tenantData.primaryColor,
            secondaryColor: tenantData.secondaryColor
          };
          setTenantId(tenant.id);
          setTenantState({
            tenant,
            availableTenants,
            isLoading: false,
            error: null
          });
        } else if (availableTenants.length === 1) {
          const tenant = availableTenants[0];
          if (tenant !== void 0) {
            setTenantId(tenant.id);
            setTenantState({
              tenant,
              availableTenants,
              isLoading: false,
              error: null
            });
          }
        } else if (availableTenants.length > 1) {
          setTenantState({
            tenant: null,
            availableTenants,
            isLoading: false,
            error: null
          });
          if (onTenantRequired !== void 0) {
            onTenantRequired();
          }
        } else {
          setTenantState({
            tenant: null,
            availableTenants: [],
            isLoading: false,
            error: new Error("No tenants available for this user")
          });
        }
      } catch (error) {
        setTenantState((prev) => ({
          ...prev,
          isLoading: false,
          error: error instanceof Error ? error : new Error("Failed to load tenant")
        }));
      }
    };
    void loadTenant();
  }, [isAuthenticated, user?.tenantId, initialTenant]);
  const switchTenant = useCallback2(
    async (tenantId) => {
      setTenantState((prev) => ({ ...prev, isLoading: true, error: null }));
      try {
        const response = await apiClient.get(
          `/api/v1/tenants/${tenantId}`
        );
        const tenant = {
          id: response.data.id,
          name: response.data.name,
          slug: response.data.slug,
          status: response.data.status,
          tier: response.data.tier,
          features: response.data.features,
          settings: response.data.settings,
          logoUrl: response.data.logoUrl,
          logoIconUrl: response.data.logoIconUrl,
          logoDarkUrl: response.data.logoDarkUrl,
          faviconUrl: response.data.faviconUrl,
          primaryColor: response.data.primaryColor,
          secondaryColor: response.data.secondaryColor
        };
        setTenantId(tenant.id);
        setTenantState((prev) => ({
          ...prev,
          tenant,
          isLoading: false,
          error: null
        }));
        return { success: true, data: tenant };
      } catch (error) {
        const switchError = error instanceof Error ? error : new Error("Failed to switch tenant");
        setTenantState((prev) => ({
          ...prev,
          isLoading: false,
          error: switchError
        }));
        return { success: false, error: switchError };
      }
    },
    []
  );
  const reloadTenant = useCallback2(async () => {
    const currentTenantId = tenantState.tenant?.id ?? getTenantId();
    if (currentTenantId === null) {
      const error = new Error("No tenant to reload");
      return { success: false, error };
    }
    return switchTenant(currentTenantId);
  }, [tenantState.tenant?.id, switchTenant]);
  const hasFeature = useCallback2(
    (feature) => {
      if (tenantState.tenant === null) {
        return false;
      }
      return tenantState.tenant.features.includes(feature);
    },
    [tenantState.tenant]
  );
  const getSetting = useCallback2(
    (key, defaultValue) => {
      if (tenantState.tenant === null) {
        return defaultValue;
      }
      const value = tenantState.tenant.settings[key];
      if (value === void 0) {
        return defaultValue;
      }
      return value;
    },
    [tenantState.tenant]
  );
  const canSwitchTenants = tenantState.availableTenants.length > 1;
  const contextValue = useMemo2(
    () => ({
      ...tenantState,
      // Use effectiveIsLoading to close the race condition window
      isLoading: effectiveIsLoading,
      switchTenant,
      reloadTenant,
      hasFeature,
      getSetting,
      canSwitchTenants
    }),
    [
      tenantState,
      effectiveIsLoading,
      switchTenant,
      reloadTenant,
      hasFeature,
      getSetting,
      canSwitchTenants
    ]
  );
  return /* @__PURE__ */ jsx2(TenantContext.Provider, { value: contextValue, children });
}
function useTenant() {
  const context = useContext2(TenantContext);
  if (context === null) {
    throw new Error("useTenant must be used within a TenantProvider");
  }
  return context;
}
function useTenantState() {
  const { tenant, availableTenants, isLoading, error } = useTenant();
  return { tenant, availableTenants, isLoading, error };
}
function useCurrentTenant() {
  const { tenant } = useTenant();
  return tenant;
}
function useTenantFeature(feature) {
  const { hasFeature } = useTenant();
  return hasFeature(feature);
}

// src/contexts/TenantBrandingContext.tsx
import {
  createContext as createContext3,
  useContext as useContext3,
  useState as useState3,
  useEffect as useEffect3,
  useMemo as useMemo3
} from "react";
import { jsx as jsx3 } from "react/jsx-runtime";
var DEFAULT_BRANDING = Object.freeze({
  logoUrl: "/logo.svg",
  logoDarkUrl: "/logo-dark.svg",
  logoIconUrl: "/logo-icon.svg",
  faviconUrl: "/favicon.svg",
  primaryColor: "#1976d2",
  secondaryColor: "#9c27b0",
  tenantName: "Plcy.io"
});
var TenantBrandingContext = createContext3(
  null
);
function mergeBrandingWithDefaults(apiBranding) {
  if (apiBranding === null) {
    return { ...DEFAULT_BRANDING };
  }
  const getValueOrDefault = (value, defaultValue) => {
    if (value === void 0 || value === null || value === "") {
      return defaultValue;
    }
    return value;
  };
  return {
    logoUrl: getValueOrDefault(apiBranding.logoUrl, DEFAULT_BRANDING.logoUrl),
    logoIconUrl: getValueOrDefault(
      apiBranding.logoIconUrl,
      DEFAULT_BRANDING.logoIconUrl
    ),
    logoDarkUrl: getValueOrDefault(
      apiBranding.logoDarkUrl,
      DEFAULT_BRANDING.logoDarkUrl
    ),
    faviconUrl: getValueOrDefault(
      apiBranding.faviconUrl,
      DEFAULT_BRANDING.faviconUrl
    ),
    primaryColor: getValueOrDefault(
      apiBranding.primaryColor,
      DEFAULT_BRANDING.primaryColor
    ),
    secondaryColor: getValueOrDefault(
      apiBranding.secondaryColor,
      DEFAULT_BRANDING.secondaryColor
    ),
    tenantName: getValueOrDefault(
      apiBranding.tenantName,
      DEFAULT_BRANDING.tenantName
    )
  };
}
function TenantBrandingProvider({
  children,
  initialBranding,
  skipFetch = false
}) {
  const [branding, setBranding] = useState3(
    initialBranding ?? { ...DEFAULT_BRANDING }
  );
  const [isLoading, setIsLoading] = useState3(!initialBranding && !skipFetch);
  const [error, setError] = useState3(null);
  useEffect3(() => {
    if (initialBranding !== void 0 || skipFetch) {
      return;
    }
    const fetchBranding = async () => {
      try {
        const response = await apiClient.get(
          "/api/v1/tenant/branding"
        );
        const apiData = response.data.data;
        const mergedBranding = mergeBrandingWithDefaults(apiData);
        setBranding(mergedBranding);
        setError(null);
      } catch (fetchError) {
        const normalizedError = fetchError instanceof Error ? fetchError : new Error("Failed to fetch branding");
        setError(normalizedError);
        setBranding({ ...DEFAULT_BRANDING });
      } finally {
        setIsLoading(false);
      }
    };
    void fetchBranding();
  }, [initialBranding, skipFetch]);
  const contextValue = useMemo3(
    () => ({
      ...branding,
      isLoading,
      error
    }),
    [branding, isLoading, error]
  );
  return /* @__PURE__ */ jsx3(TenantBrandingContext.Provider, { value: contextValue, children });
}
function useTenantBranding() {
  const context = useContext3(TenantBrandingContext);
  if (context === null) {
    throw new Error(
      "useTenantBranding must be used within a TenantBrandingProvider"
    );
  }
  return context;
}

// src/contexts/PersonaContext.tsx
import {
  createContext as createContext4,
  useContext as useContext4,
  useState as useState4,
  useCallback as useCallback3,
  useEffect as useEffect4,
  useMemo as useMemo4
} from "react";
import { jsx as jsx4 } from "react/jsx-runtime";
var PERSONA_STORAGE_KEY = "plcy-persona";
var DEFAULT_PERSONA = "wholesaler";
var ALL_PERSONAS = [
  "underwriter",
  "wholesaler",
  "accounting",
  "customerService"
];
var PersonaContext = createContext4(null);
function isValidPersona(value) {
  return typeof value === "string" && ALL_PERSONAS.includes(value);
}
function getStoredPersona() {
  try {
    const stored = localStorage.getItem(PERSONA_STORAGE_KEY);
    if (stored !== null && isValidPersona(stored)) {
      return stored;
    }
    return null;
  } catch {
    return null;
  }
}
function storePersona(persona) {
  try {
    localStorage.setItem(PERSONA_STORAGE_KEY, persona);
  } catch {
  }
}
function removeStoredPersona() {
  try {
    localStorage.removeItem(PERSONA_STORAGE_KEY);
  } catch {
  }
}
function PersonaProvider({
  children,
  defaultPersona = DEFAULT_PERSONA
}) {
  const [persona, setPersonaState] = useState4(() => {
    const stored = getStoredPersona();
    return stored ?? defaultPersona;
  });
  const [isOverride, setIsOverride] = useState4(() => {
    const stored = getStoredPersona();
    return stored !== null && stored !== defaultPersona;
  });
  useEffect4(() => {
    const stored = getStoredPersona();
    if (stored !== null) {
      const timeoutId = setTimeout(() => {
        setPersonaState(stored);
        setIsOverride(stored !== defaultPersona);
      }, 0);
      return () => clearTimeout(timeoutId);
    }
    return void 0;
  }, [defaultPersona]);
  const setPersona = useCallback3(
    (newPersona) => {
      setPersonaState(newPersona);
      storePersona(newPersona);
      setIsOverride(newPersona !== defaultPersona);
    },
    [defaultPersona]
  );
  const resetPersona = useCallback3(() => {
    setPersonaState(defaultPersona);
    removeStoredPersona();
    setIsOverride(false);
  }, [defaultPersona]);
  const contextValue = useMemo4(
    () => ({
      persona,
      availablePersonas: [...ALL_PERSONAS],
      isOverride,
      setPersona,
      resetPersona
    }),
    [persona, isOverride, setPersona, resetPersona]
  );
  return /* @__PURE__ */ jsx4(PersonaContext.Provider, { value: contextValue, children });
}
function usePersonaContext() {
  const context = useContext4(PersonaContext);
  if (context === null) {
    throw new Error("usePersonaContext must be used within a PersonaProvider");
  }
  return context;
}

// src/contexts/UnsavedChangesContext.tsx
import {
  createContext as createContext5,
  useCallback as useCallback4,
  useContext as useContext5,
  useMemo as useMemo5,
  useState as useState5
} from "react";
import { jsx as jsx5 } from "react/jsx-runtime";
var DEFAULT_CONFIRM_MESSAGE = "You have unsaved changes. Are you sure you want to leave?";
var UnsavedChangesContext = createContext5(
  null
);
function defaultConfirmImpl(message) {
  if (typeof window === "undefined") {
    return true;
  }
  return window.confirm(message);
}
function UnsavedChangesProvider({
  children,
  confirmImpl
}) {
  const [dirtyKeys, setDirtyKeys] = useState5(/* @__PURE__ */ new Set());
  const setDirty = useCallback4((key, isDirty) => {
    setDirtyKeys((prev) => {
      const alreadyDirty = prev.has(key);
      if (isDirty === alreadyDirty) {
        return prev;
      }
      const next = new Set(prev);
      if (isDirty) {
        next.add(key);
      } else {
        next.delete(key);
      }
      return next;
    });
  }, []);
  const hasUnsavedChanges = dirtyKeys.size > 0;
  const confirmNavigation = useCallback4(
    (message = DEFAULT_CONFIRM_MESSAGE) => {
      if (!hasUnsavedChanges) {
        return true;
      }
      const confirm = confirmImpl ?? defaultConfirmImpl;
      return confirm(message);
    },
    [hasUnsavedChanges, confirmImpl]
  );
  const contextValue = useMemo5(
    () => ({ hasUnsavedChanges, setDirty, confirmNavigation }),
    [hasUnsavedChanges, setDirty, confirmNavigation]
  );
  return /* @__PURE__ */ jsx5(UnsavedChangesContext.Provider, { value: contextValue, children });
}
function useUnsavedChanges() {
  const context = useContext5(UnsavedChangesContext);
  if (context === null) {
    throw new Error(
      "useUnsavedChanges must be used within an UnsavedChangesProvider"
    );
  }
  return context;
}

// src/contexts/WebSocketTransportContext.tsx
import { createContext as createContext6, useContext as useContext6, useMemo as useMemo6 } from "react";
import { jsx as jsx6 } from "react/jsx-runtime";
var logger3 = new FrontendLogger({
  component: "frontend-common:WebSocketTransportContext"
});
var NO_OP_TRANSPORT = {
  subscribe(_namespace, _event, _handler) {
    return () => {
    };
  },
  get connectionState() {
    return "disconnected";
  }
};
var WebSocketTransportContext = createContext6(NO_OP_TRANSPORT);
WebSocketTransportContext.displayName = "WebSocketTransportContext";
function WebSocketTransportProvider({
  transport,
  children
}) {
  const value = useMemo6(
    () => transport ?? NO_OP_TRANSPORT,
    [transport]
  );
  logger3.debug("WebSocketTransportProvider mounted", {
    hasTransport: transport !== void 0,
    connectionState: value.connectionState
  });
  return /* @__PURE__ */ jsx6(WebSocketTransportContext.Provider, { value, children });
}
function useWebSocketTransport() {
  return useContext6(WebSocketTransportContext);
}
var MockWebSocketTransport = class {
  _connectionState = "connected";
  _handlers = /* @__PURE__ */ new Map();
  /** Always reports `'connected'` by default. Use {@link setConnectionState} to override. */
  get connectionState() {
    return this._connectionState;
  }
  /**
   * Override the reported connection state.
   *
   * @remarks
   * Use this in stories that test the disconnected-badge UI or error states.
   *
   * @param state - New connection state to report.
   */
  setConnectionState(state) {
    this._connectionState = state;
  }
  /**
   * Register a handler for `event` on `namespace`.
   *
   * @param namespace - Namespace path (e.g. `'/monitoring'`).
   * @param event - Event name (e.g. `'security:violation'`).
   * @param handler - Callback to invoke when {@link emit} is called.
   * @returns Unsubscribe function.
   */
  subscribe(namespace, event, handler) {
    const key = `${namespace}::${event}`;
    let handlers = this._handlers.get(key);
    if (handlers === void 0) {
      handlers = /* @__PURE__ */ new Set();
      this._handlers.set(key, handlers);
    }
    handlers.add(handler);
    return () => {
      handlers?.delete(handler);
    };
  }
  /**
   * Emit a test event to all registered handlers.
   *
   * @remarks
   * Call this from Storybook play functions to drive the component under test.
   *
   * @param namespace - Namespace path (must match the one passed to `subscribe`).
   * @param event - Event name (must match the one passed to `subscribe`).
   * @param payload - Raw payload delivered to each handler (unvalidated —
   *   the type guard inside the hook is what validates it, just as the real
   *   socket does).
   */
  emit(namespace, event, payload) {
    const key = `${namespace}::${event}`;
    const handlers = this._handlers.get(key);
    if (handlers === void 0) {
      return;
    }
    for (const handler of handlers) {
      handler(payload);
    }
  }
  /**
   * Remove all registered handlers and reset connection state to
   * `'connected'`.
   *
   * @remarks
   * Call this in story `loaders` or `beforeEach` blocks to ensure a clean
   * slate between story renders.
   */
  reset() {
    this._handlers.clear();
    this._connectionState = "connected";
  }
};

// src/contexts/AppProviders.tsx
import { QueryClientProvider } from "@tanstack/react-query";
import { BrowserRouter, MemoryRouter } from "react-router-dom";
import { jsx as jsx7 } from "react/jsx-runtime";
function RouterWrapper({
  mode,
  initialEntries,
  basename,
  children
}) {
  if (mode === "memory") {
    return /* @__PURE__ */ jsx7(MemoryRouter, { initialEntries, children });
  }
  if (mode === "browser") {
    return basename !== void 0 ? /* @__PURE__ */ jsx7(BrowserRouter, { basename, children }) : /* @__PURE__ */ jsx7(BrowserRouter, { children });
  }
  return children;
}
RouterWrapper.displayName = "RouterWrapper";
function AppProviders({
  children,
  queryClient,
  themeWrapper: ThemeWrapper,
  router = "browser",
  initialEntries = ["/"],
  basename,
  reactFlowWrapper: ReactFlowWrapper,
  reactFlow = true,
  tenancyMode = "host",
  includeTenant: includeTenantProp,
  includePersona: includePersonaProp,
  includeBranding: includeBrandingProp,
  skipBrandingFetch = false,
  onAuthFailure,
  onTenantRequired,
  initialUser,
  initialCapabilities,
  initialTenant,
  initialAvailableTenants = [],
  defaultPersona
}) {
  const modeDefaults = tenancyMode === "platform" ? { tenant: false, branding: true, persona: false } : tenancyMode === "single-tenant" || tenancyMode === "multi-tenant" ? { tenant: true, branding: true, persona: false } : { tenant: true, branding: true, persona: true };
  const includeTenant = includeTenantProp ?? modeDefaults.tenant;
  const includePersona = includePersonaProp ?? modeDefaults.persona;
  const includeBranding = includeBrandingProp ?? modeDefaults.branding;
  let content = children;
  if (reactFlow && ReactFlowWrapper !== void 0) {
    content = /* @__PURE__ */ jsx7(ReactFlowWrapper, { children: content });
  }
  content = /* @__PURE__ */ jsx7(UnsavedChangesProvider, { children: content });
  if (includePersona) {
    content = defaultPersona !== void 0 ? /* @__PURE__ */ jsx7(PersonaProvider, { defaultPersona, children: content }) : /* @__PURE__ */ jsx7(PersonaProvider, { children: content });
  }
  content = /* @__PURE__ */ jsx7(
    RouterWrapper,
    {
      mode: router,
      initialEntries,
      basename,
      children: content
    }
  );
  content = /* @__PURE__ */ jsx7(QueryClientProvider, { client: queryClient, children: content });
  if (includeTenant) {
    const tenantProps = {
      initialTenant: initialTenant ?? null,
      initialAvailableTenants: initialAvailableTenants ?? []
    };
    if (onTenantRequired !== void 0) {
      tenantProps.onTenantRequired = onTenantRequired;
    }
    content = /* @__PURE__ */ jsx7(TenantProvider, { ...tenantProps, children: content });
  }
  if (includeBranding) {
    content = /* @__PURE__ */ jsx7(TenantBrandingProvider, { skipFetch: skipBrandingFetch, children: content });
  }
  {
    const authProps = {
      initialUser: initialUser ?? null,
      initialCapabilities: initialCapabilities ?? null
    };
    if (onAuthFailure !== void 0) {
      authProps.onAuthFailure = onAuthFailure;
    }
    content = /* @__PURE__ */ jsx7(AuthProvider, { ...authProps, children: content });
  }
  if (ThemeWrapper !== void 0) {
    content = /* @__PURE__ */ jsx7(ThemeWrapper, { children: content });
  }
  return content;
}
AppProviders.displayName = "AppProviders";

// src/hooks/useApi.ts
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";

// src/utils/error-handler.ts
import axios2 from "axios";
var STATUS_MESSAGES = {
  400: "Invalid request. Please check your input.",
  401: "Please log in to continue.",
  403: "You do not have permission to perform this action.",
  404: "The requested resource was not found.",
  409: "A conflict occurred. The resource may have been modified.",
  422: "The provided data is invalid.",
  429: "Too many requests. Please try again later.",
  500: "An unexpected error occurred. Please try again.",
  502: "Service temporarily unavailable. Please try again.",
  503: "Service is currently unavailable. Please try again later.",
  504: "Request timed out. Please try again."
};
function normalizeError(error) {
  if (isApiError(error)) {
    const result = {
      message: error.message,
      status: error.status,
      isRecoverable: error.status >= 500 || error.status === 429,
      requiresAuth: error.status === 401,
      isPermissionError: error.status === 403
    };
    if (error.code !== void 0) {
      result.code = error.code;
    }
    if (error.fieldErrors !== void 0) {
      result.fieldErrors = error.fieldErrors;
    }
    return result;
  }
  if (axios2.isAxiosError(error)) {
    return normalizeAxiosError(error);
  }
  if (error instanceof Error) {
    return {
      message: error.message,
      status: 0,
      isRecoverable: false,
      requiresAuth: false,
      isPermissionError: false
    };
  }
  return {
    message: "An unexpected error occurred",
    status: 0,
    isRecoverable: false,
    requiresAuth: false,
    isPermissionError: false
  };
}
function normalizeAxiosError(error) {
  if (error.code === "ECONNABORTED") {
    return {
      message: "Request timed out. Please try again.",
      status: 0,
      code: "TIMEOUT",
      isRecoverable: true,
      requiresAuth: false,
      isPermissionError: false
    };
  }
  if (error.response === void 0) {
    return {
      message: "Network error. Please check your connection.",
      status: 0,
      code: "NETWORK_ERROR",
      isRecoverable: true,
      requiresAuth: false,
      isPermissionError: false
    };
  }
  const { status, data } = error.response;
  const serverMessage = data.message;
  const { code } = data;
  const fieldErrors = data.errors;
  const message = serverMessage ?? STATUS_MESSAGES[status] ?? "Request failed";
  const result = {
    message,
    status,
    isRecoverable: status >= 500 || status === 429,
    requiresAuth: status === 401,
    isPermissionError: status === 403
  };
  if (code !== void 0) {
    result.code = code;
  }
  if (fieldErrors !== void 0) {
    result.fieldErrors = fieldErrors;
  }
  return result;
}
function getErrorMessage(error) {
  return normalizeError(error).message;
}
function isAuthError(error) {
  if (isApiError(error)) {
    return error.status === 401;
  }
  if (axios2.isAxiosError(error)) {
    return error.response?.status === 401;
  }
  return false;
}
function isPermissionError(error) {
  if (isApiError(error)) {
    return error.status === 403;
  }
  if (axios2.isAxiosError(error)) {
    return error.response?.status === 403;
  }
  return false;
}
function isValidationError(error) {
  if (isApiError(error)) {
    return error.status === 400 || error.status === 422;
  }
  if (axios2.isAxiosError(error)) {
    const status = error.response?.status;
    return status === 400 || status === 422;
  }
  return false;
}
function isNotFoundError(error) {
  if (isApiError(error)) {
    return error.status === 404;
  }
  if (axios2.isAxiosError(error)) {
    return error.response?.status === 404;
  }
  return false;
}
function isNetworkError(error) {
  if (isApiError(error)) {
    return error.status === 0 && error.code === "NETWORK_ERROR";
  }
  if (axios2.isAxiosError(error)) {
    return error.response === void 0 && error.code !== "ECONNABORTED";
  }
  return false;
}
function getFieldErrors(error) {
  if (isApiError(error)) {
    return error.fieldErrors ?? {};
  }
  if (axios2.isAxiosError(error)) {
    const data = error.response?.data;
    return data?.errors ?? {};
  }
  return {};
}
function getFieldError(error, field) {
  const fieldErrors = getFieldErrors(error);
  const errors = fieldErrors[field];
  return errors !== void 0 && errors.length > 0 ? errors[0] : void 0;
}

// src/hooks/useApi.ts
function useApiQuery(queryKey, url, options = {}) {
  const { enabled = true, staleTime = 3e4, ...restOptions } = options;
  return useQuery({
    queryKey,
    queryFn: async () => {
      const response = await apiClient.get(url);
      return response.data;
    },
    enabled,
    staleTime,
    ...restOptions
  });
}
function useApiMutation(mutationFn, options = {}) {
  const queryClient = useQueryClient();
  const { invalidateQueries, onSuccess, ...restOptions } = options;
  return useMutation({
    mutationFn,
    onSuccess: async (data, variables, context, mutation) => {
      if (invalidateQueries !== void 0 && invalidateQueries.length > 0) {
        await Promise.all(
          invalidateQueries.map(
            (key) => queryClient.invalidateQueries({ queryKey: key })
          )
        );
      }
      if (onSuccess !== void 0) {
        onSuccess(data, variables, context, mutation);
      }
    },
    ...restOptions
  });
}
function usePaginatedQuery(queryKey, url, params = {}, options = {}) {
  const { page = 1, pageSize = 20, sortBy, sortOrder } = params;
  const queryParams = new URLSearchParams();
  queryParams.set("page", page.toString());
  queryParams.set("pageSize", pageSize.toString());
  if (sortBy !== void 0) {
    queryParams.set("sortBy", sortBy);
  }
  if (sortOrder !== void 0) {
    queryParams.set("sortOrder", sortOrder);
  }
  const fullUrl = `${url}?${queryParams.toString()}`;
  const fullQueryKey = [...queryKey, { page, pageSize, sortBy, sortOrder }];
  return useApiQuery(fullQueryKey, fullUrl, options);
}
function useApiError(error) {
  if (error === null || error === void 0) {
    return null;
  }
  return normalizeError(error);
}

// src/hooks/usePermission.ts
import { useMemo as useMemo7 } from "react";
function usePermission(permission) {
  const { hasPermission, isLoading, isAuthenticated } = useAuth();
  return useMemo7(
    () => ({
      allowed: hasPermission(permission),
      isLoading,
      isAuthenticated
    }),
    [hasPermission, permission, isLoading, isAuthenticated]
  );
}
function usePermissions(permissions) {
  const { hasAllPermissions, isLoading, isAuthenticated } = useAuth();
  return useMemo7(
    () => ({
      allowed: hasAllPermissions(permissions),
      isLoading,
      isAuthenticated
    }),
    [hasAllPermissions, permissions, isLoading, isAuthenticated]
  );
}
function useRole(role) {
  const { hasRole, isLoading, isAuthenticated } = useAuth();
  return useMemo7(
    () => ({
      allowed: hasRole(role),
      isLoading,
      isAuthenticated
    }),
    [hasRole, role, isLoading, isAuthenticated]
  );
}
function useRoles(roles) {
  const { hasAnyRole, isLoading, isAuthenticated } = useAuth();
  return useMemo7(
    () => ({
      allowed: hasAnyRole(roles),
      isLoading,
      isAuthenticated
    }),
    [hasAnyRole, roles, isLoading, isAuthenticated]
  );
}
function useIsAdmin() {
  return useRoles(["SYSTEM_ADMIN", "TENANT_ADMIN"]);
}
function useIsSystemAdmin() {
  return useRole("SYSTEM_ADMIN");
}
function useIsTenantAdmin() {
  return useRole("TENANT_ADMIN");
}

// src/hooks/useCapabilities.ts
import { useCallback as useCallback5, useMemo as useMemo8 } from "react";
function useCapabilities() {
  const { capabilities: rawCapabilities, isLoading } = useAuth();
  const capabilities = rawCapabilities;
  const hasCapability = useCallback5(
    (module, action) => {
      if (capabilities == null) {
        return false;
      }
      const moduleActions = capabilities[module];
      return moduleActions?.includes(action) ?? false;
    },
    [capabilities]
  );
  const hasAnyCapability = useCallback5(
    (module, actions) => {
      if (capabilities == null) {
        return false;
      }
      const moduleActions = capabilities[module];
      if (moduleActions === void 0) {
        return false;
      }
      return actions.some((action) => moduleActions.includes(action));
    },
    [capabilities]
  );
  const hasAllCapabilities = useCallback5(
    (module, actions) => {
      if (capabilities == null) {
        return false;
      }
      const moduleActions = capabilities[module];
      if (moduleActions === void 0) {
        return false;
      }
      return actions.every((action) => moduleActions.includes(action));
    },
    [capabilities]
  );
  const getModuleActions = useCallback5(
    (module) => {
      if (capabilities == null) {
        return [];
      }
      return capabilities[module] ?? [];
    },
    [capabilities]
  );
  return useMemo8(
    () => ({
      capabilities,
      hasCapability,
      hasAnyCapability,
      hasAllCapabilities,
      getModuleActions,
      isLoading
    }),
    [
      capabilities,
      hasCapability,
      hasAnyCapability,
      hasAllCapabilities,
      getModuleActions,
      isLoading
    ]
  );
}

// src/hooks/usePrefersReducedMotion.ts
import { useEffect as useEffect5, useState as useState6 } from "react";
var REDUCED_MOTION_QUERY = "(prefers-reduced-motion: reduce)";
function usePrefersReducedMotion() {
  const [reducedMotion, setReducedMotion] = useState6(
    () => (
      // Guard `matchMedia` itself, not just `window`: jsdom (and SSR) define
      // `window` but may not implement `matchMedia`. This hook backs AnimatedLogo,
      // which main's AppHeader now renders in every portal, so any host test env
      // without a matchMedia polyfill would otherwise crash. Default to no reduced
      // motion when the API is unavailable.
      typeof window !== "undefined" && typeof window.matchMedia === "function" && window.matchMedia(REDUCED_MOTION_QUERY).matches
    )
  );
  useEffect5(() => {
    if (typeof window === "undefined" || typeof window.matchMedia !== "function") {
      return void 0;
    }
    const mediaQueryList = window.matchMedia(REDUCED_MOTION_QUERY);
    const handleChange = (event) => setReducedMotion(event.matches);
    mediaQueryList.addEventListener("change", handleChange);
    return () => mediaQueryList.removeEventListener("change", handleChange);
  }, []);
  return reducedMotion;
}

// src/hooks/useDebouncedValue.ts
import { useEffect as useEffect6, useState as useState7 } from "react";
function useDebouncedValue(value, delayMs) {
  const [debounced, setDebounced] = useState7(() => value);
  useEffect6(() => {
    const timer = setTimeout(() => {
      setDebounced(() => value);
    }, delayMs);
    return () => {
      clearTimeout(timer);
    };
  }, [value, delayMs]);
  return debounced;
}

// src/hooks/useUnsavedChangesGuard.ts
import { useEffect as useEffect7, useId } from "react";
function useUnsavedChangesGuard(isDirty) {
  const { setDirty, confirmNavigation, hasUnsavedChanges } = useUnsavedChanges();
  const guardKey = useId();
  useEffect7(() => {
    setDirty(guardKey, isDirty);
  }, [guardKey, isDirty, setDirty]);
  useEffect7(() => {
    return () => setDirty(guardKey, false);
  }, [guardKey, setDirty]);
  return { hasUnsavedChanges, confirmNavigation };
}

// src/hooks/index.ts
import {
  FormProvider,
  Controller,
  useController,
  useWatch,
  useFieldArray
} from "react-hook-form";

// src/hooks/useWebSocketSubscription.ts
import { useEffect as useEffect8, useState as useState8, useRef } from "react";
var logger4 = new FrontendLogger({
  component: "frontend-common:useWebSocketSubscription"
});
function useWebSocketSubscription(options) {
  const {
    namespace = "/monitoring",
    eventName,
    typeGuard,
    onMessage,
    enabled = true
  } = options;
  const transport = useWebSocketTransport();
  const [connectionState, setConnectionState] = useState8(() => transport.connectionState);
  const onMessageRef = useRef(onMessage);
  const typeGuardRef = useRef(typeGuard);
  useEffect8(() => {
    onMessageRef.current = onMessage;
    typeGuardRef.current = typeGuard;
  });
  useEffect8(() => {
    if (!enabled) {
      return void 0;
    }
    logger4.debug("useWebSocketSubscription: subscribing", {
      namespace,
      event: eventName
    });
    const unsubscribe = transport.subscribe(
      namespace,
      eventName,
      (raw) => {
        setConnectionState(transport.connectionState);
        if (!typeGuardRef.current(raw)) {
          logger4.warn("useWebSocketSubscription: payload failed type guard", {
            namespace,
            event: eventName,
            payload: raw
          });
          return;
        }
        logger4.debug("useWebSocketSubscription: payload received", {
          namespace,
          event: eventName
        });
        onMessageRef.current(raw);
      }
    );
    return () => {
      logger4.debug("useWebSocketSubscription: unsubscribing", {
        namespace,
        event: eventName
      });
      unsubscribe();
    };
  }, [transport, namespace, eventName, enabled]);
  const latestConnectionState = transport.connectionState;
  if (latestConnectionState !== connectionState) {
    setConnectionState(latestConnectionState);
  }
  return {
    connectionState,
    isConnected: connectionState === "connected"
  };
}

// src/config/portalPresets.ts
var PORTAL_PRESETS = {
  shell: {
    displayName: "Plcy Platform",
    tagline: "One platform for policy operations",
    accentColor: "#1976d2",
    accentContrastText: "#ffffff",
    accessHint: "Sign in with your Plcy account",
    defaultRedirect: "/"
  },
  admin: {
    displayName: "Admin Console",
    tagline: "Platform administration & governance",
    accentColor: "#6366f1",
    accentContrastText: "#000000",
    accessHint: "Staff and administrators only",
    defaultRedirect: "/dashboard"
  },
  producer: {
    displayName: "Producer Portal",
    tagline: "Quote, bind, and manage your book",
    accentColor: "#6366f1",
    accentContrastText: "#000000",
    accessHint: "For appointed producers & agencies",
    defaultRedirect: "/dashboard"
  },
  tenant: {
    displayName: "Tenant Workspace",
    tagline: "Your organization's insurance workspace",
    accentColor: "#7c3aed",
    accentContrastText: "#ffffff",
    accessHint: "Sign in to your organization",
    defaultRedirect: "/dashboard"
  },
  watchtower: {
    displayName: "Watchtower",
    tagline: "Observability command center",
    accentColor: "#0891b2",
    accentContrastText: "#000000",
    accessHint: "Operators & SREs only",
    defaultRedirect: "/watchtower"
  }
};

// src/components/LoadingSpinner.tsx
import React6 from "react";
import { jsx as jsx8, jsxs } from "react/jsx-runtime";
var SIZES = {
  small: 16,
  medium: 24,
  large: 40
};
var SPINNER_KEYFRAMES = `
@keyframes plcy-spinner-spin {
  from {
    transform: rotate(0deg);
  }
  to {
    transform: rotate(360deg);
  }
}
`;
var keyframesInjected = false;
function injectKeyframes() {
  if (keyframesInjected || typeof document === "undefined") {
    return;
  }
  const style = document.createElement("style");
  style.textContent = SPINNER_KEYFRAMES;
  document.head.appendChild(style);
  keyframesInjected = true;
}
function LoadingSpinner({
  size = "medium",
  color = "currentColor",
  className = "",
  label = "Loading"
}) {
  React6.useEffect(() => {
    injectKeyframes();
  }, []);
  const dimension = SIZES[size];
  const borderWidth = size === "small" ? 2 : size === "medium" ? 3 : 4;
  const spinnerStyle = {
    display: "inline-block",
    width: dimension,
    height: dimension,
    border: `${borderWidth}px solid transparent`,
    borderTopColor: color,
    borderRadius: "50%",
    animation: "plcy-spinner-spin 0.8s linear infinite"
  };
  return /* @__PURE__ */ jsxs(
    "div",
    {
      role: "status",
      "aria-label": label,
      className,
      style: {
        display: "inline-flex",
        alignItems: "center",
        justifyContent: "center"
      },
      children: [
        /* @__PURE__ */ jsx8("span", { style: spinnerStyle, "aria-hidden": "true" }),
        /* @__PURE__ */ jsx8(
          "span",
          {
            style: {
              position: "absolute",
              width: 1,
              height: 1,
              padding: 0,
              margin: -1,
              overflow: "hidden",
              clip: "rect(0, 0, 0, 0)",
              whiteSpace: "nowrap",
              border: 0
            },
            children: label
          }
        )
      ]
    }
  );
}
function FullPageSpinner({
  label = "Loading",
  ...props
}) {
  return /* @__PURE__ */ jsx8(
    "div",
    {
      style: {
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        minHeight: "100vh",
        width: "100%"
      },
      children: /* @__PURE__ */ jsx8(LoadingSpinner, { size: "large", label, ...props })
    }
  );
}

// src/components/ErrorBoundary.tsx
import { Component } from "react";
import { Fragment, jsx as jsx9, jsxs as jsxs2 } from "react/jsx-runtime";
var logger5;
function getLogger() {
  logger5 ??= new FrontendLogger({
    component: "frontend-common:ErrorBoundary"
  });
  return logger5;
}
var ErrorBoundary = class extends Component {
  constructor(props) {
    super(props);
    this.state = {
      hasError: false,
      error: null
    };
  }
  static getDerivedStateFromError(error) {
    return {
      hasError: true,
      error
    };
  }
  componentDidCatch(error, errorInfo) {
    const { onError } = this.props;
    getLogger().error(error.message, { error, errorInfo });
    if (onError !== void 0) {
      onError(error, errorInfo);
    }
  }
  componentDidUpdate(prevProps) {
    const { resetKey } = this.props;
    const { hasError } = this.state;
    if (hasError && prevProps.resetKey !== resetKey) {
      this.resetError();
    }
  }
  resetError = () => {
    this.setState({
      hasError: false,
      error: null
    });
  };
  render() {
    const { children, fallback } = this.props;
    const { hasError, error } = this.state;
    if (hasError && error !== null) {
      if (fallback === void 0) {
        return /* @__PURE__ */ jsx9(DefaultErrorFallback, { error, resetError: this.resetError });
      }
      if (typeof fallback === "function") {
        return fallback({ error, resetError: this.resetError });
      }
      return fallback;
    }
    return children;
  }
};
function DefaultErrorFallback({
  error,
  resetError
}) {
  const isDev = import.meta.env?.DEV === true;
  return /* @__PURE__ */ jsxs2(
    "div",
    {
      role: "alert",
      style: {
        padding: 24,
        margin: 16,
        borderRadius: 8,
        backgroundColor: "var(--plcy-error-surface, #fef2f2)",
        border: `1px solid color-mix(in srgb, var(--plcy-error-main, #d32f2f) 30%, transparent)`,
        fontFamily: "system-ui, sans-serif"
      },
      children: [
        /* @__PURE__ */ jsx9(
          "h2",
          {
            style: {
              margin: "0 0 16px 0",
              fontSize: 18,
              fontWeight: 600,
              color: "var(--plcy-error-on-surface, #991b1b)"
            },
            children: "Something went wrong"
          }
        ),
        /* @__PURE__ */ jsx9(
          "pre",
          {
            style: {
              margin: "0 0 16px 0",
              padding: 12,
              backgroundColor: "var(--plcy-bg-paper, #ffffff)",
              border: `1px solid var(--plcy-border-default, #e2e8f0)`,
              borderRadius: 4,
              fontSize: 14,
              color: "var(--plcy-text-primary, #374151)",
              overflow: "auto",
              whiteSpace: "pre-wrap",
              wordBreak: "break-word"
            },
            children: error.message
          }
        ),
        isDev && error.stack && /* @__PURE__ */ jsxs2(Fragment, { children: [
          /* @__PURE__ */ jsx9(
            "div",
            {
              style: {
                margin: "0 0 8px 0",
                fontSize: 12,
                fontWeight: 600,
                color: "var(--plcy-text-secondary, #6b7280)",
                textTransform: "uppercase"
              },
              children: "Stack Trace (Development Only):"
            }
          ),
          /* @__PURE__ */ jsx9(
            "pre",
            {
              style: {
                margin: "0 0 16px 0",
                padding: 12,
                backgroundColor: "var(--plcy-bg-subtle, #f9fafb)",
                border: `1px solid var(--plcy-border-default, #e2e8f0)`,
                borderRadius: 4,
                fontSize: 12,
                color: "var(--plcy-text-primary, #374151)",
                overflow: "auto",
                maxHeight: 400,
                whiteSpace: "pre",
                fontFamily: "ui-monospace, monospace"
              },
              children: error.stack
            }
          )
        ] }),
        /* @__PURE__ */ jsx9(
          "button",
          {
            onClick: resetError,
            style: {
              padding: "8px 16px",
              fontSize: 14,
              fontWeight: 500,
              color: "var(--plcy-primary-contrast, #ffffff)",
              backgroundColor: "var(--plcy-error-main, #dc2626)",
              border: "none",
              borderRadius: 6,
              cursor: "pointer"
            },
            children: "Try again"
          }
        )
      ]
    }
  );
}
function withErrorBoundary(WrappedComponent, errorBoundaryProps = {}) {
  const displayName = WrappedComponent.displayName ?? (WrappedComponent.name !== "" ? WrappedComponent.name : "Component");
  const ComponentWithErrorBoundary = (props) => /* @__PURE__ */ jsx9(ErrorBoundary, { ...errorBoundaryProps, children: /* @__PURE__ */ jsx9(WrappedComponent, { ...props }) });
  ComponentWithErrorBoundary.displayName = `withErrorBoundary(${displayName})`;
  return ComponentWithErrorBoundary;
}

// src/components/PermissionGate.tsx
import { jsx as jsx10 } from "react/jsx-runtime";
function PermissionGate({
  children,
  permission,
  permissions,
  requiredRole,
  roles,
  fallback = null,
  showLoading = false,
  loadingComponent
}) {
  const {
    hasPermission,
    hasAllPermissions,
    hasRole,
    hasAnyRole,
    isLoading,
    isAuthenticated
  } = useAuth();
  if (isLoading && showLoading) {
    if (loadingComponent !== void 0) {
      return loadingComponent;
    }
    return /* @__PURE__ */ jsx10(LoadingSpinner, { size: "small" });
  }
  if (!isAuthenticated) {
    return fallback;
  }
  if (permission !== void 0 && !hasPermission(permission)) {
    return fallback;
  }
  if (permissions !== void 0 && !hasAllPermissions(permissions)) {
    return fallback;
  }
  if (requiredRole !== void 0 && !hasRole(requiredRole)) {
    return fallback;
  }
  if (roles !== void 0 && !hasAnyRole(roles)) {
    return fallback;
  }
  return children;
}
function RequireAuth({
  children,
  fallback = null,
  showLoading = true,
  loadingComponent
}) {
  const { isLoading, isAuthenticated } = useAuth();
  if (isLoading && showLoading) {
    if (loadingComponent !== void 0) {
      return loadingComponent;
    }
    return /* @__PURE__ */ jsx10(LoadingSpinner, { size: "medium", label: "Checking authentication..." });
  }
  if (!isAuthenticated) {
    return fallback;
  }
  return children;
}
function RequireRole({
  requiredRole,
  roles,
  ...props
}) {
  return /* @__PURE__ */ jsx10(
    PermissionGate,
    {
      ...requiredRole !== void 0 ? { requiredRole } : {},
      ...roles !== void 0 ? { roles } : {},
      ...props
    }
  );
}
function RequirePermission({
  permission,
  permissions,
  ...props
}) {
  return /* @__PURE__ */ jsx10(
    PermissionGate,
    {
      ...permission !== void 0 ? { permission } : {},
      ...permissions !== void 0 ? { permissions } : {},
      ...props
    }
  );
}
function AdminOnly({
  children,
  fallback = null
}) {
  return /* @__PURE__ */ jsx10(
    PermissionGate,
    {
      roles: ["SYSTEM_ADMIN", "TENANT_ADMIN"],
      fallback,
      children
    }
  );
}
function SystemAdminOnly({
  children,
  fallback = null
}) {
  return /* @__PURE__ */ jsx10(PermissionGate, { requiredRole: "SYSTEM_ADMIN", fallback, children });
}

// src/components/TenantGuard.tsx
import { jsx as jsx11, jsxs as jsxs3 } from "react/jsx-runtime";
function TenantGuard({
  children,
  tenantSelector,
  noTenantFallback,
  loadingComponent,
  unauthenticatedFallback
}) {
  const { isAuthenticated, isLoading: authLoading } = useAuth();
  const {
    tenant,
    availableTenants,
    isLoading: tenantLoading,
    error
  } = useTenant();
  if (authLoading || tenantLoading) {
    if (loadingComponent !== void 0) {
      return loadingComponent;
    }
    return /* @__PURE__ */ jsx11(
      "div",
      {
        style: {
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          minHeight: "200px"
        },
        children: /* @__PURE__ */ jsx11(LoadingSpinner, { size: "large", label: "Loading tenant..." })
      }
    );
  }
  if (!isAuthenticated) {
    if (unauthenticatedFallback !== void 0) {
      return unauthenticatedFallback;
    }
    return null;
  }
  if (tenant !== null) {
    return children;
  }
  if (availableTenants.length > 1 && tenantSelector !== void 0) {
    return tenantSelector;
  }
  if (noTenantFallback !== void 0) {
    return noTenantFallback;
  }
  return /* @__PURE__ */ jsxs3(
    "div",
    {
      role: "alert",
      style: {
        padding: 24,
        margin: 16,
        borderRadius: 8,
        backgroundColor: "var(--plcy-warning-surface, #fef3c7)",
        border: `1px solid color-mix(in srgb, var(--plcy-warning-main, #ed6c02) 50%, transparent)`,
        fontFamily: "system-ui, sans-serif",
        textAlign: "center"
      },
      children: [
        /* @__PURE__ */ jsx11(
          "h2",
          {
            style: {
              margin: "0 0 8px 0",
              fontSize: 18,
              fontWeight: 600,
              color: "var(--plcy-warning-on-surface, #92400e)"
            },
            children: "No Tenant Access"
          }
        ),
        /* @__PURE__ */ jsx11(
          "p",
          {
            style: {
              margin: 0,
              color: "var(--plcy-warning-on-surface, #a16207)"
            },
            children: error?.message ?? "You do not have access to any tenants. Please contact your administrator."
          }
        )
      ]
    }
  );
}
function RequireTenant({
  children,
  fallback = null,
  showLoading = true
}) {
  const { tenant, isLoading } = useTenant();
  if (isLoading && showLoading) {
    return /* @__PURE__ */ jsx11(LoadingSpinner, { size: "medium", label: "Loading tenant..." });
  }
  if (tenant === null) {
    return fallback;
  }
  return children;
}
function TenantFeatureGate({
  children,
  feature,
  features,
  fallback = null,
  requireAll = false
}) {
  const { hasFeature, tenant, isLoading } = useTenant();
  if (isLoading) {
    return null;
  }
  if (tenant === null) {
    return fallback;
  }
  if (feature !== void 0 && !hasFeature(feature)) {
    return fallback;
  }
  if (features !== void 0 && features.length > 0) {
    const hasFeatures = requireAll ? features.every((f) => hasFeature(f)) : features.some((f) => hasFeature(f));
    if (!hasFeatures) {
      return fallback;
    }
  }
  return children;
}
function TenantTierGate({
  children,
  tier,
  minTier,
  fallback = null
}) {
  const { tenant, isLoading } = useTenant();
  if (isLoading) {
    return null;
  }
  if (tenant === null) {
    return fallback;
  }
  if (tier !== void 0 && tenant.tier !== tier) {
    return fallback;
  }
  if (minTier !== void 0) {
    const tierOrder = {
      basic: 1,
      professional: 2,
      enterprise: 3
    };
    const currentTierLevel = tierOrder[tenant.tier] ?? 0;
    const requiredTierLevel = tierOrder[minTier] ?? 0;
    if (currentTierLevel < requiredTierLevel) {
      return fallback;
    }
  }
  return children;
}

// src/components/DynamicFavicon.tsx
import { useEffect as useEffect9, useRef as useRef2 } from "react";
var DEFAULT_FAVICON = DEFAULT_BRANDING.faviconUrl ?? "/favicon.svg";
function getFaviconMimeType(url) {
  const pathname = url.split("?")[0] ?? url;
  const extension = pathname.split(".").pop()?.toLowerCase();
  switch (extension) {
    case "svg":
      return "image/svg+xml";
    case "ico":
      return "image/x-icon";
    case "png":
      return "image/png";
    case "gif":
      return "image/gif";
    case "jpg":
    case "jpeg":
      return "image/jpeg";
    case "webp":
      return "image/webp";
    default:
      return "image/x-icon";
  }
}
function isAbsoluteUrl(url) {
  return url.startsWith("http://") || url.startsWith("https://") || url.startsWith("//");
}
function getFullFaviconUrl(url) {
  if (isAbsoluteUrl(url)) {
    return url;
  }
  if (typeof window !== "undefined") {
    return new URL(url, window.location.origin).href;
  }
  return url;
}
var DynamicFavicon = () => {
  const { faviconUrl } = useTenantBranding();
  const linkRef = useRef2(null);
  useEffect9(() => {
    const urlToUse = faviconUrl ?? DEFAULT_FAVICON;
    const fullUrl = getFullFaviconUrl(urlToUse);
    const mimeType = getFaviconMimeType(urlToUse);
    if (linkRef.current === null) {
      const link = document.createElement("link");
      link.rel = "icon";
      link.setAttribute("data-dynamic", "true");
      document.head.appendChild(link);
      linkRef.current = link;
    }
    linkRef.current.href = fullUrl;
    linkRef.current.type = mimeType;
    return () => {
      if (linkRef.current !== null) {
        linkRef.current.remove();
        linkRef.current = null;
      }
    };
  }, [faviconUrl]);
  return null;
};

// src/components/BrandedLogo.tsx
import { jsx as jsx12 } from "react/jsx-runtime";
function getLogoUrl(variant, logoUrl, logoIconUrl, logoDarkUrl) {
  switch (variant) {
    case "icon":
      return logoIconUrl ?? DEFAULT_BRANDING.logoIconUrl ?? "/logo-icon.svg";
    case "dark":
      return logoDarkUrl ?? DEFAULT_BRANDING.logoDarkUrl ?? "/logo-dark.svg";
    case "full":
    default:
      return logoUrl ?? DEFAULT_BRANDING.logoUrl ?? "/logo.svg";
  }
}
var BrandedLogo = ({
  height = 40,
  variant = "full",
  className,
  alt,
  loading = "lazy"
}) => {
  const { logoUrl, logoIconUrl, logoDarkUrl, tenantName } = useTenantBranding();
  const logoSrc = getLogoUrl(variant, logoUrl, logoIconUrl, logoDarkUrl);
  const altText = alt ?? `${tenantName} logo`;
  const wrapperStyle = {
    display: "inline-flex",
    alignItems: "center",
    justifyContent: "center"
  };
  const imageStyle = {
    height: `${height}px`,
    width: "auto",
    objectFit: "contain"
  };
  return /* @__PURE__ */ jsx12("div", { style: wrapperStyle, className, children: /* @__PURE__ */ jsx12("img", { src: logoSrc, alt: altText, style: imageStyle, loading }) });
};

// src/components/PlcyLogo.tsx
import { jsx as jsx13, jsxs as jsxs4 } from "react/jsx-runtime";
var BRAND_BLUE = "#42a5f5";
var BRAND_PURPLE = "#ce93d8";
var WORDMARK_ON_DARK = "#ffffff";
var WORDMARK_ON_LIGHT = "#263238";
function PlcyLogo({
  height = 40,
  tone = "auto"
}) {
  const fills = resolveFills(tone);
  return /* @__PURE__ */ jsxs4(
    "svg",
    {
      xmlns: "http://www.w3.org/2000/svg",
      viewBox: "0 0 180 64",
      style: { height, width: "auto", display: "block" },
      role: "img",
      "aria-label": "Plcy.io",
      children: [
        /* @__PURE__ */ jsx13("rect", { x: "28", y: "6", width: "6", height: "6", rx: "1.5", fill: fills.arcFill }),
        /* @__PURE__ */ jsx13(
          "rect",
          {
            x: "18",
            y: "9",
            width: "6",
            height: "6",
            rx: "1.5",
            fill: fills.arcFill,
            fillOpacity: 0.95
          }
        ),
        /* @__PURE__ */ jsx13(
          "rect",
          {
            x: "10",
            y: "16",
            width: "6",
            height: "6",
            rx: "1.5",
            fill: fills.arcFill,
            fillOpacity: 0.9
          }
        ),
        /* @__PURE__ */ jsx13(
          "rect",
          {
            x: "6",
            y: "26",
            width: "6",
            height: "6",
            rx: "1.5",
            fill: fills.arcFill,
            fillOpacity: 0.85
          }
        ),
        /* @__PURE__ */ jsx13(
          "rect",
          {
            x: "6",
            y: "36",
            width: "6",
            height: "6",
            rx: "1.5",
            fill: fills.arcFill,
            fillOpacity: 0.8
          }
        ),
        /* @__PURE__ */ jsx13(
          "rect",
          {
            x: "10",
            y: "46",
            width: "6",
            height: "6",
            rx: "1.5",
            fill: fills.arcFill,
            fillOpacity: 0.75
          }
        ),
        /* @__PURE__ */ jsx13(
          "rect",
          {
            x: "30",
            y: "52",
            width: "6",
            height: "6",
            rx: "1.5",
            fill: fills.accentFill
          }
        ),
        /* @__PURE__ */ jsx13(
          "rect",
          {
            x: "40",
            y: "49",
            width: "6",
            height: "6",
            rx: "1.5",
            fill: fills.accentFill,
            fillOpacity: 0.95
          }
        ),
        /* @__PURE__ */ jsx13(
          "rect",
          {
            x: "48",
            y: "42",
            width: "6",
            height: "6",
            rx: "1.5",
            fill: fills.accentFill,
            fillOpacity: 0.9
          }
        ),
        /* @__PURE__ */ jsx13(
          "rect",
          {
            x: "52",
            y: "32",
            width: "6",
            height: "6",
            rx: "1.5",
            fill: fills.accentFill,
            fillOpacity: 0.85
          }
        ),
        /* @__PURE__ */ jsx13(
          "rect",
          {
            x: "52",
            y: "22",
            width: "6",
            height: "6",
            rx: "1.5",
            fill: fills.accentFill,
            fillOpacity: 0.8
          }
        ),
        /* @__PURE__ */ jsx13(
          "rect",
          {
            x: "48",
            y: "12",
            width: "6",
            height: "6",
            rx: "1.5",
            fill: fills.accentFill,
            fillOpacity: 0.75
          }
        ),
        /* @__PURE__ */ jsx13(
          "path",
          {
            d: "M24 20h12c5 0 8 3 8 8s-3 8-8 8h-8v8h-4V20zm4 12h8c2.5 0 4-1.5 4-4s-1.5-4-4-4h-8v8z",
            fill: fills.textFill
          }
        ),
        /* @__PURE__ */ jsxs4(
          "text",
          {
            x: "72",
            y: "40",
            fontFamily: "system-ui, sans-serif",
            fontSize: "24",
            fontWeight: "600",
            fill: fills.textFill,
            children: [
              "Plcy",
              /* @__PURE__ */ jsx13("tspan", { fill: fills.dotFill, children: "." }),
              /* @__PURE__ */ jsx13("tspan", { fill: fills.accentFill, children: "io" })
            ]
          }
        )
      ]
    }
  );
}
function resolveFills(tone) {
  const effectiveTone = tone === "auto" ? "onDark" : tone;
  if (effectiveTone === "onDark") {
    return {
      arcFill: BRAND_BLUE,
      textFill: WORDMARK_ON_DARK,
      accentFill: BRAND_PURPLE,
      dotFill: BRAND_BLUE
    };
  }
  return {
    arcFill: BRAND_BLUE,
    textFill: WORDMARK_ON_LIGHT,
    accentFill: BRAND_PURPLE,
    dotFill: BRAND_BLUE
  };
}

// src/components/AnimatedLogo.tsx
import { useEffect as useEffect10 } from "react";
import { createPortal } from "react-dom";
import { Fragment as Fragment2, jsx as jsx14, jsxs as jsxs5 } from "react/jsx-runtime";
function primaryLight() {
  return "var(--plcy-primary-light, #42a5f5)";
}
function secondaryLight() {
  return "var(--plcy-secondary-light, #ce93d8)";
}
var COLORS = {
  light: {
    fill: "var(--plcy-text-primary, #263238)"
  },
  dark: {
    fill: "var(--plcy-bg-paper, #ffffff)"
  }
};
var LOGO_KEYFRAMES = `
@keyframes plcy-logo-pulse {
  0% {
    filter: drop-shadow(0 0 4px color-mix(in srgb, var(--plcy-primary-main, #1976d2) 35%, transparent))
            drop-shadow(0 0 2px color-mix(in srgb, var(--plcy-secondary-main, #9c27b0) 10%, transparent));
  }
  50% {
    filter: drop-shadow(0 0 16px color-mix(in srgb, var(--plcy-primary-main, #1976d2) 75%, transparent))
            drop-shadow(0 0 36px color-mix(in srgb, var(--plcy-primary-main, #1976d2) 25%, transparent))
            drop-shadow(0 0 8px color-mix(in srgb, var(--plcy-secondary-main, #9c27b0) 30%, transparent));
  }
  100% {
    filter: drop-shadow(0 0 6px color-mix(in srgb, var(--plcy-secondary-main, #9c27b0) 50%, transparent))
            drop-shadow(0 0 24px color-mix(in srgb, var(--plcy-secondary-main, #9c27b0) 20%, transparent))
            drop-shadow(0 0 4px color-mix(in srgb, var(--plcy-primary-main, #1976d2) 25%, transparent));
  }
}

@keyframes plcy-dot-blue-to-purple {
  0%, 15%  { fill: var(--plcy-primary-light, #42a5f5); }
  50%, 65% { fill: var(--plcy-secondary-light, #ce93d8); }
  100%     { fill: var(--plcy-primary-light, #42a5f5); }
}

@keyframes plcy-dot-purple-to-blue {
  0%, 15%  { fill: var(--plcy-secondary-light, #ce93d8); }
  50%, 65% { fill: var(--plcy-primary-light, #42a5f5); }
  100%     { fill: var(--plcy-secondary-light, #ce93d8); }
}

@keyframes plcy-wordmark-dot-shift {
  0%, 20%  { fill: var(--plcy-primary-light, #42a5f5); }
  50%, 70% { fill: var(--plcy-secondary-light, #ce93d8); }
  100%     { fill: var(--plcy-primary-light, #42a5f5); }
}

@keyframes plcy-wordmark-io-shift {
  0%, 20%  { fill: var(--plcy-secondary-light, #ce93d8); }
  50%, 70% { fill: var(--plcy-primary-light, #42a5f5); }
  100%     { fill: var(--plcy-secondary-light, #ce93d8); }
}

@keyframes plcy-logo-slam-in {
  0% {
    opacity: 0;
    transform: scale(15) translateX(-30vw);
    filter: drop-shadow(0 0 80px color-mix(in srgb, var(--plcy-primary-main, #1976d2) 90%, transparent))
            drop-shadow(0 0 120px color-mix(in srgb, var(--plcy-secondary-main, #9c27b0) 60%, transparent));
  }
  30% { opacity: 1; }
  70% {
    transform: scale(0.9) translateX(0);
    filter: drop-shadow(0 0 20px color-mix(in srgb, var(--plcy-primary-main, #1976d2) 60%, transparent))
            drop-shadow(0 0 40px color-mix(in srgb, var(--plcy-secondary-main, #9c27b0) 30%, transparent));
  }
  85% { transform: scale(1.06) translateX(0); }
  92% { transform: scale(0.97) translateX(0); }
  100% {
    opacity: 1;
    transform: scale(1) translateX(0);
    filter: drop-shadow(0 0 4px color-mix(in srgb, var(--plcy-primary-main, #1976d2) 35%, transparent))
            drop-shadow(0 0 2px color-mix(in srgb, var(--plcy-secondary-main, #9c27b0) 10%, transparent));
  }
}

@keyframes plcy-slam-shockwave {
  0%  { opacity: 0; transform: scale(0); }
  40% { opacity: 0.4; }
  100% { opacity: 0; transform: scale(3); }
}

@keyframes plcy-shockwave-ring {
  0% {
    opacity: 0;
    transform: scale(0);
  }
  8% {
    opacity: var(--ring-opacity, 0.3);
  }
  50% {
    opacity: calc(var(--ring-opacity, 0.3) * 0.5);
  }
  100% {
    opacity: 0;
    transform: scale(1);
  }
}

@media (prefers-reduced-motion: reduce) {
  @keyframes plcy-logo-pulse { from, to { filter: drop-shadow(0 0 6px color-mix(in srgb, var(--plcy-primary-main, #1976d2) 50%, transparent)); } }
  @keyframes plcy-dot-blue-to-purple { from, to { fill: var(--plcy-primary-light, #42a5f5); } }
  @keyframes plcy-dot-purple-to-blue { from, to { fill: var(--plcy-secondary-light, #ce93d8); } }
  @keyframes plcy-wordmark-dot-shift { from, to { fill: var(--plcy-primary-light, #42a5f5); } }
  @keyframes plcy-wordmark-io-shift { from, to { fill: var(--plcy-secondary-light, #ce93d8); } }
  @keyframes plcy-logo-slam-in { from, to { opacity: 1; transform: none; filter: none; } }
  @keyframes plcy-slam-shockwave { from, to { opacity: 0; } }
  @keyframes plcy-shockwave-ring { from, to { opacity: 0; } }
}
`;
var keyframesInjected2 = false;
function injectKeyframes2() {
  if (keyframesInjected2 || typeof document === "undefined") {
    return;
  }
  const style = document.createElement("style");
  style.textContent = LOGO_KEYFRAMES;
  document.head.appendChild(style);
  keyframesInjected2 = true;
}
var DOT_DELAYS = [0, 0.6, 1.2, 1.8, 2.4, 3];
var UPPER_ARC = [
  { x: 28, y: 6 },
  { x: 18, y: 9 },
  { x: 10, y: 16 },
  { x: 6, y: 26 },
  { x: 6, y: 36 },
  { x: 10, y: 46 }
];
var LOWER_ARC = [
  { x: 30, y: 52 },
  { x: 40, y: 49 },
  { x: 48, y: 42 },
  { x: 52, y: 32 },
  { x: 52, y: 22 },
  { x: 48, y: 12 }
];
var EASE_STANDARD = "cubic-bezier(0.4, 0, 0.2, 1)";
var EASE_DECELERATE = "cubic-bezier(0, 0, 0.2, 1)";
var AnimatedLogo = ({
  height = 64,
  width,
  variant = "light",
  animation = "subtle",
  className,
  alt = "Plcy.io logo",
  shockwaveMode = "contained"
}) => {
  const reducedMotion = usePrefersReducedMotion();
  useEffect10(() => {
    injectKeyframes2();
  }, []);
  const animate = animation !== "none" && !reducedMotion;
  const isEntrance = animation === "entrance" && !reducedMotion;
  const c = COLORS[variant];
  const wrapperStyle = {
    display: "inline-flex",
    alignItems: "center",
    justifyContent: "center",
    position: "relative",
    ...isEntrance ? {
      opacity: 0,
      transform: "scale(15) translateX(-30vw)",
      animation: `plcy-logo-slam-in 1.4s cubic-bezier(0.16, 1, 0.3, 1) 600ms both`
    } : {}
  };
  const shockwaveStyle = {
    position: "absolute",
    top: "50%",
    left: "50%",
    width: 200,
    height: 200,
    marginTop: -100,
    marginLeft: -100,
    borderRadius: "50%",
    border: `2px solid color-mix(in srgb, var(--plcy-primary-light, #42a5f5) 40%, transparent)`,
    pointerEvents: "none",
    opacity: 0,
    animation: `plcy-slam-shockwave 800ms ${EASE_DECELERATE} 1700ms both`
  };
  const svgStyle = {
    width: width ?? "auto",
    height,
    maxWidth: "100%",
    display: "block",
    ...animate ? {
      animation: `plcy-logo-pulse 6s ${EASE_STANDARD} ${isEntrance ? "2200ms" : "0ms"} infinite alternate`
    } : {}
  };
  const blueDotStyle = (index) => animate ? {
    animation: `plcy-dot-blue-to-purple 8s ${EASE_STANDARD} ${DOT_DELAYS[index]}s infinite`
  } : {};
  const purpleDotStyle = (index) => animate ? {
    animation: `plcy-dot-purple-to-blue 8s ${EASE_STANDARD} ${DOT_DELAYS[index]}s infinite`
  } : {};
  const isFullpage = shockwaveMode === "fullpage" && isEntrance && !reducedMotion && typeof document !== "undefined";
  const RING_CONFIG = [
    { delay: 1700, duration: 1400, opacity: 0.3 },
    { delay: 1900, duration: 1600, opacity: 0.2 },
    { delay: 2100, duration: 1800, opacity: 0.12 }
  ];
  const fullpageRings = isFullpage ? createPortal(
    /* @__PURE__ */ jsx14(Fragment2, { children: RING_CONFIG.map((ring, i) => /* @__PURE__ */ jsx14(
      "div",
      {
        "aria-hidden": "true",
        style: {
          position: "fixed",
          top: "50%",
          left: "50%",
          width: "200vmax",
          height: "200vmax",
          marginTop: "-100vmax",
          marginLeft: "-100vmax",
          borderRadius: "50%",
          background: `radial-gradient(circle, transparent 42%, color-mix(in srgb, var(--plcy-primary-light, #42a5f5) 18%, transparent) 47%, color-mix(in srgb, var(--plcy-secondary-main, #9c27b0) 18%, transparent) 53%, transparent 58%)`,
          pointerEvents: "none",
          zIndex: 9999,
          opacity: 0,
          animation: `plcy-shockwave-ring ${String(ring.duration)}ms ease-out ${String(ring.delay)}ms both`,
          // CSS custom property for per-ring opacity
          ...{ "--ring-opacity": String(ring.opacity) }
        }
      },
      `shockwave-ring-${String(i)}`
    )) }),
    document.body
  ) : null;
  return /* @__PURE__ */ jsxs5("div", { style: wrapperStyle, className, children: [
    fullpageRings,
    isEntrance && shockwaveMode !== "fullpage" && /* @__PURE__ */ jsx14("div", { style: shockwaveStyle, "aria-hidden": "true" }),
    /* @__PURE__ */ jsxs5("svg", { viewBox: "-8 0 180 64", style: svgStyle, "aria-label": alt, role: "img", children: [
      UPPER_ARC.map((dot, i) => /* @__PURE__ */ jsx14(
        "rect",
        {
          x: dot.x,
          y: dot.y,
          width: 6,
          height: 6,
          rx: 1.5,
          fill: primaryLight(),
          style: blueDotStyle(i)
        },
        `upper-${String(i)}`
      )),
      LOWER_ARC.map((dot, i) => /* @__PURE__ */ jsx14(
        "rect",
        {
          x: dot.x,
          y: dot.y,
          width: 6,
          height: 6,
          rx: 1.5,
          fill: secondaryLight(),
          style: purpleDotStyle(i)
        },
        `lower-${String(i)}`
      )),
      /* @__PURE__ */ jsx14(
        "path",
        {
          d: "M24 20h12c5 0 8 3 8 8s-3 8-8 8h-8v8h-4V20zm4 12h8c2.5 0 4-1.5 4-4s-1.5-4-4-4h-8v8z",
          fill: c.fill
        }
      ),
      /* @__PURE__ */ jsxs5(
        "text",
        {
          x: 72,
          y: 40,
          fontFamily: "system-ui, -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif",
          fontSize: 24,
          fontWeight: 600,
          fill: c.fill,
          children: [
            "Plcy",
            /* @__PURE__ */ jsx14(
              "tspan",
              {
                fill: primaryLight(),
                style: animate ? {
                  animation: `plcy-wordmark-dot-shift 8s ${EASE_STANDARD} 1.4s infinite`
                } : void 0,
                children: "."
              }
            ),
            /* @__PURE__ */ jsx14(
              "tspan",
              {
                fill: secondaryLight(),
                style: animate ? {
                  animation: `plcy-wordmark-io-shift 8s ${EASE_STANDARD} 1.4s infinite`
                } : void 0,
                children: "io"
              }
            )
          ]
        }
      )
    ] })
  ] });
};

// src/components/AnimatedSlogan.tsx
import { useEffect as useEffect12, useState as useState10 } from "react";

// src/components/AnimatedSlogan.rotation.ts
import { useEffect as useEffect11, useRef as useRef3, useState as useState9 } from "react";

// src/components/AnimatedSlogan.effects.ts
var ROTATION_KEYFRAMES = `
/* ===== ENTRANCE EFFECTS ===== */

@keyframes plcy-rotation-slam-in {
  0% {
    opacity: 0;
    transform: scale(3);
    filter: drop-shadow(0 0 40px color-mix(in srgb, var(--plcy-primary-light, #42a5f5) 70%, transparent))
            drop-shadow(0 0 60px color-mix(in srgb, var(--plcy-secondary-main, #9c27b0) 40%, transparent));
  }
  30% { opacity: 1; }
  70% { transform: scale(0.95); filter: none; }
  85% { transform: scale(1.04); }
  93% { transform: scale(0.99); }
  100% { opacity: 1; transform: scale(1); filter: none; }
}

@keyframes plcy-rotation-slide-up-in {
  0% { opacity: 0; transform: translateY(30px); }
  100% { opacity: 1; transform: translateY(0); }
}

@keyframes plcy-rotation-slide-down-in {
  0% { opacity: 0; transform: translateY(-30px); }
  100% { opacity: 1; transform: translateY(0); }
}

@keyframes plcy-rotation-slide-left-in {
  0% { opacity: 0; transform: translateX(40px); }
  100% { opacity: 1; transform: translateX(0); }
}

@keyframes plcy-rotation-slide-right-in {
  0% { opacity: 0; transform: translateX(-40px); }
  100% { opacity: 1; transform: translateX(0); }
}

@keyframes plcy-rotation-flip-x-in {
  0% { opacity: 0; transform: perspective(600px) rotateX(90deg); }
  100% { opacity: 1; transform: perspective(600px) rotateX(0deg); }
}

@keyframes plcy-rotation-flip-y-in {
  0% { opacity: 0; transform: perspective(600px) rotateY(90deg); }
  100% { opacity: 1; transform: perspective(600px) rotateY(0deg); }
}

@keyframes plcy-rotation-blur-in {
  0% { opacity: 0; filter: blur(16px); }
  100% { opacity: 1; filter: blur(0); }
}

@keyframes plcy-rotation-bounce-in {
  0% { opacity: 0; transform: scale(0.3); }
  50% { opacity: 1; transform: scale(1.08); }
  70% { transform: scale(0.96); }
  100% { opacity: 1; transform: scale(1); }
}

@keyframes plcy-rotation-expand-in {
  0% { opacity: 0; transform: scaleX(0); }
  100% { opacity: 1; transform: scaleX(1); }
}

@keyframes plcy-rotation-glitch-in {
  0% { opacity: 0; transform: translate(0, 0); clip-path: inset(0 0 100% 0); }
  10% { opacity: 1; transform: translate(-2px, 2px); clip-path: inset(20% 0 50% 0); }
  20% { transform: translate(2px, -2px); clip-path: inset(50% 0 20% 0); }
  30% { transform: translate(-2px, 0); clip-path: inset(0 0 80% 0); }
  40% { transform: translate(2px, 2px); clip-path: inset(80% 0 0 0); }
  60% { transform: translate(-1px, -1px); clip-path: inset(0 0 0 0); }
  80% { transform: translate(1px, 0); }
  100% { opacity: 1; transform: translate(0, 0); clip-path: none; }
}

@keyframes plcy-rotation-reveal-in {
  0% { clip-path: inset(0 100% 0 0); opacity: 1; }
  100% { clip-path: inset(0 0% 0 0); opacity: 1; }
}

@keyframes plcy-rotation-morph-in {
  0% { opacity: 0; letter-spacing: 0.4em; }
  100% { opacity: 1; letter-spacing: normal; }
}

@keyframes plcy-rotation-drop-in {
  0% { opacity: 0; transform: translateY(-50px) scale(1.15); }
  60% { opacity: 1; transform: translateY(6px) scale(0.97); }
  80% { transform: translateY(-4px) scale(1.02); }
  100% { opacity: 1; transform: translateY(0) scale(1); }
}

@keyframes plcy-rotation-zoom-spin-in {
  0% { opacity: 0; transform: scale(0) rotate(180deg); }
  70% { opacity: 1; transform: scale(1.05) rotate(-5deg); }
  100% { opacity: 1; transform: scale(1) rotate(0deg); }
}

/* ===== EXIT EFFECTS ===== */

@keyframes plcy-rotation-shrink-out {
  0% { opacity: 1; transform: scale(1); }
  100% { opacity: 0; transform: scale(0.5); }
}

@keyframes plcy-rotation-slide-up-out {
  0% { opacity: 1; transform: translateY(0); }
  100% { opacity: 0; transform: translateY(-30px); }
}

@keyframes plcy-rotation-slide-down-out {
  0% { opacity: 1; transform: translateY(0); }
  100% { opacity: 0; transform: translateY(30px); }
}

@keyframes plcy-rotation-slide-left-out {
  0% { opacity: 1; transform: translateX(0); }
  100% { opacity: 0; transform: translateX(-40px); }
}

@keyframes plcy-rotation-slide-right-out {
  0% { opacity: 1; transform: translateX(0); }
  100% { opacity: 0; transform: translateX(40px); }
}

@keyframes plcy-rotation-flip-x-out {
  0% { opacity: 1; transform: perspective(600px) rotateX(0deg); }
  100% { opacity: 0; transform: perspective(600px) rotateX(90deg); }
}

@keyframes plcy-rotation-flip-y-out {
  0% { opacity: 1; transform: perspective(600px) rotateY(0deg); }
  100% { opacity: 0; transform: perspective(600px) rotateY(90deg); }
}

@keyframes plcy-rotation-blur-out {
  0% { opacity: 1; filter: blur(0); }
  100% { opacity: 0; filter: blur(16px); }
}

@keyframes plcy-rotation-bounce-out {
  0% { opacity: 1; transform: scale(1); }
  30% { transform: scale(1.1); }
  100% { opacity: 0; transform: scale(0); }
}

@keyframes plcy-rotation-collapse-out {
  0% { opacity: 1; transform: scaleX(1); }
  100% { opacity: 0; transform: scaleX(0); }
}

@keyframes plcy-rotation-glitch-out {
  0% { opacity: 1; transform: translate(0, 0); }
  15% { transform: translate(2px, -2px); }
  30% { transform: translate(-2px, 2px); }
  45% { transform: translate(2px, 0); }
  60% { opacity: 0.5; transform: translate(-2px, -1px); }
  80% { transform: translate(1px, 2px); }
  100% { opacity: 0; transform: translate(0, 0); }
}

@keyframes plcy-rotation-wipe-out {
  0% { clip-path: inset(0 0 0 0); opacity: 1; }
  100% { clip-path: inset(0 0 0 100%); opacity: 1; }
}

@keyframes plcy-rotation-morph-out {
  0% { opacity: 1; letter-spacing: normal; }
  100% { opacity: 0; letter-spacing: 0.4em; }
}

@keyframes plcy-rotation-rise-out {
  0% { opacity: 1; transform: translateY(0) scale(1); }
  100% { opacity: 0; transform: translateY(-40px) scale(0.8); }
}

@keyframes plcy-rotation-spin-out {
  0% { opacity: 1; transform: rotate(0deg) scale(1); }
  100% { opacity: 0; transform: rotate(180deg) scale(0); }
}

/* ===== REDUCED MOTION OVERRIDES ===== */

@media (prefers-reduced-motion: reduce) {
  @keyframes plcy-rotation-slam-in {
    from { opacity: 0; }
    to { opacity: 1; }
  }
  @keyframes plcy-rotation-slide-up-in {
    from { opacity: 0; }
    to { opacity: 1; }
  }
  @keyframes plcy-rotation-slide-down-in {
    from { opacity: 0; }
    to { opacity: 1; }
  }
  @keyframes plcy-rotation-slide-left-in {
    from { opacity: 0; }
    to { opacity: 1; }
  }
  @keyframes plcy-rotation-slide-right-in {
    from { opacity: 0; }
    to { opacity: 1; }
  }
  @keyframes plcy-rotation-flip-x-in {
    from { opacity: 0; }
    to { opacity: 1; }
  }
  @keyframes plcy-rotation-flip-y-in {
    from { opacity: 0; }
    to { opacity: 1; }
  }
  @keyframes plcy-rotation-blur-in {
    from { opacity: 0; }
    to { opacity: 1; }
  }
  @keyframes plcy-rotation-bounce-in {
    from { opacity: 0; }
    to { opacity: 1; }
  }
  @keyframes plcy-rotation-expand-in {
    from { opacity: 0; }
    to { opacity: 1; }
  }
  @keyframes plcy-rotation-glitch-in {
    from { opacity: 0; }
    to { opacity: 1; }
  }
  @keyframes plcy-rotation-reveal-in {
    from { opacity: 0; }
    to { opacity: 1; }
  }
  @keyframes plcy-rotation-morph-in {
    from { opacity: 0; }
    to { opacity: 1; }
  }
  @keyframes plcy-rotation-drop-in {
    from { opacity: 0; }
    to { opacity: 1; }
  }
  @keyframes plcy-rotation-zoom-spin-in {
    from { opacity: 0; }
    to { opacity: 1; }
  }

  @keyframes plcy-rotation-shrink-out {
    from { opacity: 1; }
    to { opacity: 0; }
  }
  @keyframes plcy-rotation-slide-up-out {
    from { opacity: 1; }
    to { opacity: 0; }
  }
  @keyframes plcy-rotation-slide-down-out {
    from { opacity: 1; }
    to { opacity: 0; }
  }
  @keyframes plcy-rotation-slide-left-out {
    from { opacity: 1; }
    to { opacity: 0; }
  }
  @keyframes plcy-rotation-slide-right-out {
    from { opacity: 1; }
    to { opacity: 0; }
  }
  @keyframes plcy-rotation-flip-x-out {
    from { opacity: 1; }
    to { opacity: 0; }
  }
  @keyframes plcy-rotation-flip-y-out {
    from { opacity: 1; }
    to { opacity: 0; }
  }
  @keyframes plcy-rotation-blur-out {
    from { opacity: 1; }
    to { opacity: 0; }
  }
  @keyframes plcy-rotation-bounce-out {
    from { opacity: 1; }
    to { opacity: 0; }
  }
  @keyframes plcy-rotation-collapse-out {
    from { opacity: 1; }
    to { opacity: 0; }
  }
  @keyframes plcy-rotation-glitch-out {
    from { opacity: 1; }
    to { opacity: 0; }
  }
  @keyframes plcy-rotation-wipe-out {
    from { opacity: 1; }
    to { opacity: 0; }
  }
  @keyframes plcy-rotation-morph-out {
    from { opacity: 1; }
    to { opacity: 0; }
  }
  @keyframes plcy-rotation-rise-out {
    from { opacity: 1; }
    to { opacity: 0; }
  }
  @keyframes plcy-rotation-spin-out {
    from { opacity: 1; }
    to { opacity: 0; }
  }
}
`;
var rotationKeyframesInjected = false;
function injectRotationKeyframes() {
  if (rotationKeyframesInjected || typeof document === "undefined") {
    return;
  }
  const style = document.createElement("style");
  style.textContent = ROTATION_KEYFRAMES;
  document.head.appendChild(style);
  rotationKeyframesInjected = true;
}
var ENTRANCE_EFFECTS = [
  { name: "slam-in", duration: 500, easing: "cubic-bezier(0.16, 1, 0.3, 1)" },
  {
    name: "slide-up-in",
    duration: 380,
    easing: "cubic-bezier(0.25, 0.46, 0.45, 0.94)"
  },
  {
    name: "slide-down-in",
    duration: 380,
    easing: "cubic-bezier(0.25, 0.46, 0.45, 0.94)"
  },
  {
    name: "slide-left-in",
    duration: 380,
    easing: "cubic-bezier(0.25, 0.46, 0.45, 0.94)"
  },
  {
    name: "slide-right-in",
    duration: 380,
    easing: "cubic-bezier(0.25, 0.46, 0.45, 0.94)"
  },
  { name: "flip-x-in", duration: 420, easing: "cubic-bezier(0.16, 1, 0.3, 1)" },
  { name: "flip-y-in", duration: 420, easing: "cubic-bezier(0.16, 1, 0.3, 1)" },
  { name: "blur-in", duration: 350, easing: "ease-out" },
  {
    name: "bounce-in",
    duration: 500,
    easing: "cubic-bezier(0.36, 0.07, 0.19, 0.97)"
  },
  { name: "expand-in", duration: 350, easing: "cubic-bezier(0.16, 1, 0.3, 1)" },
  { name: "glitch-in", duration: 480, easing: "steps(8, end)" },
  {
    name: "reveal-in",
    duration: 400,
    easing: "cubic-bezier(0.77, 0, 0.175, 1)"
  },
  { name: "morph-in", duration: 400, easing: "ease-out" },
  { name: "drop-in", duration: 500, easing: "cubic-bezier(0.16, 1, 0.3, 1)" },
  {
    name: "zoom-spin-in",
    duration: 480,
    easing: "cubic-bezier(0.16, 1, 0.3, 1)"
  }
];
var EXIT_EFFECTS = [
  { name: "shrink-out", duration: 300, easing: "ease-in" },
  { name: "slide-up-out", duration: 280, easing: "ease-in" },
  { name: "slide-down-out", duration: 280, easing: "ease-in" },
  { name: "slide-left-out", duration: 280, easing: "ease-in" },
  { name: "slide-right-out", duration: 280, easing: "ease-in" },
  { name: "flip-x-out", duration: 300, easing: "ease-in" },
  { name: "flip-y-out", duration: 300, easing: "ease-in" },
  { name: "blur-out", duration: 260, easing: "ease-in" },
  {
    name: "bounce-out",
    duration: 340,
    easing: "cubic-bezier(0.36, 0.07, 0.19, 0.97)"
  },
  { name: "collapse-out", duration: 280, easing: "ease-in" },
  { name: "glitch-out", duration: 360, easing: "steps(6, end)" },
  {
    name: "wipe-out",
    duration: 320,
    easing: "cubic-bezier(0.77, 0, 0.175, 1)"
  },
  { name: "morph-out", duration: 300, easing: "ease-in" },
  { name: "rise-out", duration: 280, easing: "ease-in" },
  { name: "spin-out", duration: 340, easing: "ease-in" }
];
function selectRandomEffect(effects, previousName) {
  if (effects.length === 0) {
    return {
      name: "slam-in",
      duration: 500,
      easing: "cubic-bezier(0.16, 1, 0.3, 1)"
    };
  }
  if (effects.length === 1) {
    return effects[0];
  }
  const pool = previousName !== void 0 ? effects.filter((e) => e.name !== previousName) : effects;
  const candidates = pool.length > 0 ? pool : effects;
  const index = Math.floor(Math.random() * candidates.length);
  return candidates[index];
}

// src/components/AnimatedSlogan.rotation.ts
function selectWeightedKeyword(keywords, recentTexts) {
  if (keywords.length === 0) {
    return "";
  }
  if (keywords.length === 1) {
    return keywords[0].text;
  }
  const totalWeight = keywords.reduce((sum, kw) => sum + (kw.weight ?? 1), 0);
  const pickOnce = () => {
    let remaining = Math.random() * totalWeight;
    for (const kw of keywords) {
      remaining -= kw.weight ?? 1;
      if (remaining <= 0) {
        return kw.text;
      }
    }
    return keywords[keywords.length - 1].text;
  };
  const recentSet = new Set(recentTexts);
  for (let attempt = 0; attempt < 10; attempt++) {
    const candidate = pickOnce();
    if (!recentSet.has(candidate)) {
      return candidate;
    }
  }
  for (const kw of keywords) {
    if (!recentSet.has(kw.text)) {
      return kw.text;
    }
  }
  return keywords[0].text;
}
var RECENT_HISTORY_SIZE = 3;
function useKeywordRotation(initialText, config, slamReady, reducedMotion) {
  const [currentText, setCurrentText] = useState9(initialText);
  const [animationStyle, setAnimationStyle] = useState9({});
  const [phase, setPhase] = useState9("idle");
  const recentTextsRef = useRef3([initialText]);
  const lastExitNameRef = useRef3(void 0);
  const lastEntranceNameRef = useRef3(void 0);
  useEffect11(() => {
    if (config !== void 0) {
      injectRotationKeyframes();
    }
  }, [config]);
  useEffect11(() => {
    if (!slamReady || config === void 0 || config.keywords.length === 0) {
      return void 0;
    }
    const displayDuration = config.displayDuration ?? 3500;
    const transitionDuration = config.transitionDuration ?? 450;
    const initialPause = config.initialPause ?? 2500;
    let cancelled = false;
    const startRotation = () => {
      if (cancelled) return;
      setPhase("displaying");
      const runCycle = () => {
        if (cancelled) return;
        setTimeout(() => {
          if (cancelled) return;
          const exitEffect = selectRandomEffect(
            EXIT_EFFECTS,
            lastExitNameRef.current
          );
          lastExitNameRef.current = exitEffect.name;
          const exitStyle = reducedMotion ? { transition: `opacity ${transitionDuration}ms ease`, opacity: 0 } : {
            animation: `plcy-rotation-${exitEffect.name} ${exitEffect.duration}ms ${exitEffect.easing} both`
          };
          setAnimationStyle(exitStyle);
          setPhase("exiting");
          setTimeout(() => {
            if (cancelled) return;
            const nextText = selectWeightedKeyword(
              config.keywords,
              recentTextsRef.current
            );
            recentTextsRef.current = [
              nextText,
              ...recentTextsRef.current
            ].slice(0, RECENT_HISTORY_SIZE);
            const entranceEffect = selectRandomEffect(
              ENTRANCE_EFFECTS,
              lastEntranceNameRef.current
            );
            lastEntranceNameRef.current = entranceEffect.name;
            const entranceStyle = reducedMotion ? {
              transition: `opacity ${transitionDuration}ms ease`,
              opacity: 1
            } : {
              animation: `plcy-rotation-${entranceEffect.name} ${entranceEffect.duration}ms ${entranceEffect.easing} both`
            };
            setCurrentText(nextText);
            setAnimationStyle(entranceStyle);
            setPhase("entering");
            setTimeout(() => {
              if (cancelled) return;
              setAnimationStyle({});
              setPhase("displaying");
              runCycle();
            }, transitionDuration);
          }, transitionDuration);
        }, displayDuration);
      };
      runCycle();
    };
    const pauseTimer = setTimeout(startRotation, initialPause);
    return () => {
      cancelled = true;
      clearTimeout(pauseTimer);
    };
  }, [slamReady, config, reducedMotion]);
  return { currentText, animationStyle, phase };
}

// src/components/AnimatedSlogan.tsx
import { Fragment as Fragment3, jsx as jsx15, jsxs as jsxs6 } from "react/jsx-runtime";
var COLORS2 = {
  light: {
    text: "color-mix(in srgb, var(--plcy-text-primary, rgba(55,71,79,1)) 92%, transparent)",
    cursor: "color-mix(in srgb, var(--plcy-text-primary, rgba(55,71,79,1)) 70%, transparent)"
  },
  dark: {
    text: "color-mix(in srgb, var(--plcy-bg-paper, #ffffff) 92%, transparent)",
    cursor: "color-mix(in srgb, var(--plcy-bg-paper, #ffffff) 70%, transparent)"
  }
};
var SLOGAN_KEYFRAMES = `
@keyframes plcy-slogan-blink {
  0%, 100% { opacity: 1; }
  50% { opacity: 0; }
}

@keyframes plcy-slogan-accent-slam {
  0% {
    opacity: 0;
    transform: scale(3);
    filter: drop-shadow(0 0 40px color-mix(in srgb, var(--plcy-primary-light, #42a5f5) 70%, transparent))
            drop-shadow(0 0 60px color-mix(in srgb, var(--plcy-secondary-main, #9c27b0) 40%, transparent));
  }
  30% { opacity: 1; }
  70% { transform: scale(0.95); }
  85% { transform: scale(1.04); }
  93% { transform: scale(0.99); }
  100% {
    opacity: 1;
    transform: scale(1);
    filter: none;
  }
}

@media (prefers-reduced-motion: reduce) {
  @keyframes plcy-slogan-blink { from, to { opacity: 1; } }
  @keyframes plcy-slogan-accent-slam { from, to { opacity: 1; transform: none; filter: none; } }
}
`;
var sloganKeyframesInjected = false;
function injectSloganKeyframes() {
  if (sloganKeyframesInjected || typeof document === "undefined") {
    return;
  }
  const style = document.createElement("style");
  style.textContent = SLOGAN_KEYFRAMES;
  document.head.appendChild(style);
  sloganKeyframesInjected = true;
}
var EASE_DECELERATE2 = "cubic-bezier(0.16, 1, 0.3, 1)";
var AnimatedSlogan = ({
  text,
  accentText,
  variant = "light",
  animation = "entrance",
  delay = 2200,
  typingSpeed = 55,
  accentDelay = 500,
  fontSize = "0.875rem",
  fontWeight = 300,
  accentFontWeight = 400,
  className,
  ariaLabel,
  rotation
}) => {
  const [charCount, setCharCount] = useState10(0);
  const [slamReady, setSlamReady] = useState10(false);
  const reducedMotion = usePrefersReducedMotion();
  const {
    currentText: displayedAccent,
    animationStyle: rotationStyle,
    phase: rotationPhase
  } = useKeywordRotation(accentText, rotation, slamReady, reducedMotion);
  useEffect12(() => {
    injectSloganKeyframes();
  }, []);
  useEffect12(() => {
    if (animation === "none" || reducedMotion) {
      setCharCount(text.length);
      setSlamReady(true);
      return void 0;
    }
    let intervalId = null;
    let pauseId = null;
    const startId = setTimeout(() => {
      let i = 0;
      intervalId = setInterval(() => {
        i++;
        setCharCount(i);
        if (i >= text.length) {
          if (intervalId) clearInterval(intervalId);
          if (animation === "entrance") {
            pauseId = setTimeout(() => setSlamReady(true), accentDelay);
          }
        }
      }, typingSpeed);
    }, delay);
    return () => {
      clearTimeout(startId);
      if (intervalId) clearInterval(intervalId);
      if (pauseId) clearTimeout(pauseId);
    };
  }, [animation, reducedMotion, text.length, delay, typingSpeed, accentDelay]);
  const longestAccent = rotation?.keywords ? [accentText, ...rotation.keywords.map((k) => k.text)].reduce(
    (a, b) => a.length > b.length ? a : b
  ) : accentText;
  const c = COLORS2[variant];
  const animate = animation !== "none" && !reducedMotion;
  const showCursor = animate && charCount > 0 && !slamReady;
  const wrapperStyle = {
    textAlign: "center",
    position: "relative"
  };
  const textStyle = {
    fontFamily: "'Inter', system-ui, -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif",
    fontSize,
    fontWeight,
    letterSpacing: "-0.025em",
    color: c.text,
    margin: 0,
    lineHeight: 1.3,
    whiteSpace: "nowrap"
  };
  const cursorStyle = {
    display: "inline-block",
    width: 2,
    height: "0.9em",
    verticalAlign: "text-bottom",
    background: c.cursor,
    marginLeft: 2,
    animation: "plcy-slogan-blink 530ms steps(1) infinite"
  };
  const isRotating = rotation !== void 0 && rotationPhase !== "idle";
  const accentStyle = {
    position: "absolute",
    left: 0,
    top: 0,
    fontWeight: accentFontWeight,
    background: `linear-gradient(135deg, var(--plcy-primary-light, #42a5f5) 0%, var(--plcy-secondary-light, #ce93d8) 100%)`,
    WebkitBackgroundClip: "text",
    backgroundClip: "text",
    WebkitTextFillColor: "transparent",
    color: "transparent",
    ...animate && !isRotating ? {
      animation: `plcy-slogan-accent-slam 0.8s ${EASE_DECELERATE2} both`
    } : {},
    ...isRotating ? rotationStyle : {}
  };
  return /* @__PURE__ */ jsxs6("div", { style: wrapperStyle, className, children: [
    /* @__PURE__ */ jsxs6("p", { style: { ...textStyle, visibility: "hidden" }, "aria-hidden": "true", children: [
      /* @__PURE__ */ jsx15("span", { children: text }),
      " ",
      /* @__PURE__ */ jsx15("span", { style: { fontWeight: accentFontWeight }, children: longestAccent })
    ] }),
    /* @__PURE__ */ jsxs6(
      "p",
      {
        style: {
          ...textStyle,
          position: "absolute",
          top: 0,
          left: 0,
          right: 0
        },
        "aria-label": ariaLabel ?? `${text} ${accentText}`,
        role: "status",
        children: [
          /* @__PURE__ */ jsx15("span", { children: text.slice(0, charCount) }),
          showCursor && /* @__PURE__ */ jsx15("span", { style: cursorStyle, "aria-hidden": "true" }),
          slamReady && /* @__PURE__ */ jsxs6(Fragment3, { children: [
            " ",
            /* @__PURE__ */ jsxs6("span", { style: { display: "inline-block", position: "relative" }, children: [
              /* @__PURE__ */ jsx15(
                "span",
                {
                  style: { visibility: "hidden", fontWeight: accentFontWeight },
                  children: longestAccent
                }
              ),
              /* @__PURE__ */ jsx15(
                "span",
                {
                  style: accentStyle,
                  children: displayedAccent
                },
                rotationPhase === "entering" ? displayedAccent : void 0
              )
            ] })
          ] })
        ]
      }
    )
  ] });
};

// src/components/CornerRibbon.tsx
import { useEffect as useEffect13, useState as useState11 } from "react";
import { jsx as jsx16, jsxs as jsxs7 } from "react/jsx-runtime";
var RIBBON_KEYFRAMES = `
@keyframes plcy-ribbon-fade-in {
  from {
    opacity: 0;
    transform: translateY(20px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
}

@keyframes plcy-ribbon-crossfade {
  from { opacity: 0; }
  to { opacity: 1; }
}

@keyframes plcy-ribbon-text-pulse {
  from { opacity: 0.85; }
  to { opacity: 1; }
}

@media (prefers-reduced-motion: reduce) {
  @keyframes plcy-ribbon-fade-in { from, to { opacity: 1; transform: none; } }
  @keyframes plcy-ribbon-crossfade { from, to { opacity: 0; } }
  @keyframes plcy-ribbon-text-pulse { from, to { opacity: 1; } }
}
`;
var ribbonKeyframesInjected = false;
function injectRibbonKeyframes() {
  if (ribbonKeyframesInjected || typeof document === "undefined") {
    return;
  }
  const style = document.createElement("style");
  style.textContent = RIBBON_KEYFRAMES;
  document.head.appendChild(style);
  ribbonKeyframesInjected = true;
}
function computeDimensions(textLength) {
  const fontSize = textLength <= 12 ? 18 : textLength <= 20 ? 15 : 12;
  const letterSpacing = textLength <= 12 ? 0.28 : textLength <= 20 ? 0.2 : 0.15;
  const padding = textLength <= 12 ? 18 : textLength <= 20 ? 14 : 10;
  const charWidth = fontSize * 0.56;
  const spacingWidth = fontSize * letterSpacing;
  const textWidth = textLength * (charWidth + spacingWidth);
  const ribbonWidth = Math.max(320, Math.ceil(textWidth + 160));
  const cornerSize = Math.ceil(ribbonWidth * 0.85);
  return { fontSize, letterSpacing, ribbonWidth, cornerSize, padding };
}
var EASE_DECELERATE3 = "cubic-bezier(0, 0, 0.2, 1)";
var CornerRibbon = ({
  text,
  delay = 2500,
  animation = "entrance",
  className
}) => {
  const [reducedMotion, setReducedMotion] = useState11(
    () => typeof window !== "undefined" && window.matchMedia("(prefers-reduced-motion: reduce)").matches
  );
  useEffect13(() => {
    injectRibbonKeyframes();
  }, []);
  useEffect13(() => {
    if (typeof window === "undefined") {
      return void 0;
    }
    const mq = window.matchMedia("(prefers-reduced-motion: reduce)");
    const handler = (e) => setReducedMotion(e.matches);
    mq.addEventListener("change", handler);
    return () => mq.removeEventListener("change", handler);
  }, []);
  const animate = animation !== "none" && !reducedMotion;
  const dims = computeDimensions(text.length);
  const cornerStyle = {
    position: "fixed",
    top: 0,
    right: 0,
    width: dims.cornerSize,
    height: dims.cornerSize,
    overflow: "hidden",
    zIndex: 10,
    pointerEvents: "none",
    ...animate ? {
      opacity: 0,
      animation: `plcy-ribbon-fade-in 800ms ${EASE_DECELERATE3} ${String(delay)}ms both`
    } : {}
  };
  const ribbonBase = {
    position: "absolute",
    top: "30%",
    left: "70%",
    width: dims.ribbonWidth,
    padding: `${String(dims.padding)}px 0`,
    textAlign: "center",
    transform: "translate(-50%, -50%) rotate(45deg)",
    background: `linear-gradient(135deg, color-mix(in srgb, var(--plcy-primary-main, #1976d2) 85%, transparent) 0%, color-mix(in srgb, var(--plcy-secondary-main, #9c27b0) 85%, transparent) 100%)`,
    boxShadow: `0 4px 20px rgba(0,0,0,0.7), 0 0 40px color-mix(in srgb, var(--plcy-primary-main, #1976d2) 60%, transparent), 0 0 40px color-mix(in srgb, var(--plcy-secondary-main, #9c27b0) 50%, transparent), 0 0 80px color-mix(in srgb, var(--plcy-primary-main, #1976d2) 30%, transparent)`
  };
  const ribbonOverlay = {
    position: "absolute",
    inset: 0,
    background: `linear-gradient(135deg, color-mix(in srgb, var(--plcy-secondary-main, #9c27b0) 85%, transparent) 0%, color-mix(in srgb, var(--plcy-primary-main, #1976d2) 85%, transparent) 100%)`,
    opacity: 0,
    ...animate ? {
      animation: `plcy-ribbon-crossfade 5s ease-in-out infinite alternate`
    } : {}
  };
  const textStyle = {
    position: "relative",
    fontFamily: "'Inter', system-ui, -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif",
    fontSize: dims.fontSize,
    fontWeight: 700,
    letterSpacing: `${String(dims.letterSpacing)}em`,
    textTransform: "uppercase",
    color: "var(--plcy-primary-contrast, #ffffff)",
    textShadow: `0 0 8px color-mix(in srgb, var(--plcy-primary-contrast, #ffffff) 60%, transparent), 0 0 20px color-mix(in srgb, var(--plcy-primary-light, #42a5f5) 50%, transparent), 0 0 40px color-mix(in srgb, var(--plcy-secondary-main, #9c27b0) 30%, transparent)`,
    ...animate ? {
      animation: `plcy-ribbon-text-pulse 4s ease-in-out infinite alternate`
    } : {}
  };
  return /* @__PURE__ */ jsx16("div", { style: cornerStyle, className, "aria-hidden": "true", children: /* @__PURE__ */ jsxs7("div", { style: ribbonBase, role: "status", "aria-label": text, children: [
    /* @__PURE__ */ jsx16("div", { style: ribbonOverlay }),
    /* @__PURE__ */ jsx16("span", { style: textStyle, children: text })
  ] }) });
};

// src/components/CrossAppLink.tsx
import { forwardRef } from "react";
import { jsx as jsx17 } from "react/jsx-runtime";
var DEFAULT_BASE_PATHS = {
  admin: "/system/admin",
  watchtower: "/watchtower",
  developer: "/developer",
  workflows: "/workflows"
};
function resolveBasePath(app) {
  const envKey = `VITE_${app.toUpperCase()}_BASE_PATH`;
  const envValue = typeof window !== "undefined" ? window[`__${envKey}__`] : void 0;
  return envValue ?? DEFAULT_BASE_PATHS[app];
}
function buildCrossAppPath(app, path = "/") {
  const basePath = resolveBasePath(app);
  const normalizedBase = basePath.endsWith("/") ? basePath.slice(0, -1) : basePath;
  const normalizedPath = path.startsWith("/") ? path : `/${path}`;
  return `${normalizedBase}${normalizedPath}`;
}
var CrossAppLink = forwardRef(
  function CrossAppLink2({ app, path = "/", children, newTab = false, ...anchorProps }, ref) {
    const href = buildCrossAppPath(app, path);
    return /* @__PURE__ */ jsx17(
      "a",
      {
        ref,
        href,
        target: newTab ? "_blank" : void 0,
        rel: newTab ? "noopener noreferrer" : void 0,
        ...anchorProps,
        children
      }
    );
  }
);

// src/components/PasswordRequirements.tsx
import CancelIcon from "@mui/icons-material/Cancel";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import {
  Box,
  LinearProgress,
  List,
  ListItem,
  ListItemIcon,
  ListItemText
} from "@mui/material";
import { useMemo as useMemo9 } from "react";

// src/utils/passwordRequirements.ts
var PASSWORD_REQUIREMENTS = [
  {
    id: "length",
    label: "At least 8 characters",
    test: (password) => password.length >= 8
  },
  {
    id: "uppercase",
    label: "One uppercase letter",
    test: (password) => /[A-Z]/.test(password)
  },
  {
    id: "lowercase",
    label: "One lowercase letter",
    test: (password) => /[a-z]/.test(password)
  },
  {
    id: "number",
    label: "One number",
    test: (password) => /\d/.test(password)
  },
  {
    id: "special",
    label: "One special character (!@#$%^&*)",
    test: (password) => /[!@#$%^&*()_+\-=[\]{};':"\\|,.<>/?]/.test(password)
  }
];
function getMetPasswordRequirements(password) {
  return PASSWORD_REQUIREMENTS.map((req) => req.test(password));
}
function isPasswordValid(password) {
  return PASSWORD_REQUIREMENTS.every((req) => req.test(password));
}

// src/components/PasswordRequirements.tsx
import { jsx as jsx18, jsxs as jsxs8 } from "react/jsx-runtime";
function getStrengthColor(metCount) {
  if (metCount <= 2) {
    return "error";
  }
  if (metCount <= 4) {
    return "warning";
  }
  return "success";
}
function PasswordRequirements({
  password
}) {
  const metRequirements = useMemo9(
    () => getMetPasswordRequirements(password),
    [password]
  );
  const metCount = metRequirements.filter(Boolean).length;
  const strengthPercentage = metCount / PASSWORD_REQUIREMENTS.length * 100;
  const strengthColor = getStrengthColor(metCount);
  return /* @__PURE__ */ jsxs8(Box, { "data-testid": "password-requirements", children: [
    /* @__PURE__ */ jsx18(
      LinearProgress,
      {
        variant: "determinate",
        value: strengthPercentage,
        color: strengthColor,
        sx: { mb: 1, height: 8, borderRadius: 1 },
        role: "progressbar",
        "aria-valuenow": strengthPercentage,
        "aria-valuemin": 0,
        "aria-valuemax": 100,
        "aria-label": "Password strength"
      }
    ),
    /* @__PURE__ */ jsx18(List, { dense: true, children: PASSWORD_REQUIREMENTS.map((requirement, index) => {
      const isMet = metRequirements[index] ?? false;
      return /* @__PURE__ */ jsxs8(
        ListItem,
        {
          disableGutters: true,
          sx: { py: 0.25 },
          "data-met": isMet.toString(),
          children: [
            /* @__PURE__ */ jsx18(ListItemIcon, { sx: { minWidth: 28 }, children: isMet ? /* @__PURE__ */ jsx18(CheckCircleIcon, { fontSize: "small", color: "success" }) : /* @__PURE__ */ jsx18(CancelIcon, { fontSize: "small", color: "error" }) }),
            /* @__PURE__ */ jsx18(
              ListItemText,
              {
                primary: requirement.label,
                slotProps: {
                  primary: {
                    variant: "body2",
                    color: isMet ? "success.main" : "text.secondary"
                  }
                }
              }
            )
          ]
        },
        requirement.id
      );
    }) })
  ] });
}

// src/components/LoginPage.tsx
import {
  Google as GoogleIcon,
  Visibility,
  VisibilityOff
} from "@mui/icons-material";
import ErrorOutlineIcon from "@mui/icons-material/ErrorOutline";
import WarningAmberIcon from "@mui/icons-material/WarningAmber";
import {
  Alert,
  Box as Box2,
  Button,
  Card,
  CardContent,
  CircularProgress,
  Divider,
  IconButton,
  InputAdornment,
  Link,
  Paper,
  TextField,
  Typography,
  useTheme
} from "@mui/material";
import { useCallback as useCallback6, useEffect as useEffect14, useMemo as useMemo10, useRef as useRef4, useState as useState12 } from "react";
import {
  Link as RouterLink,
  useLocation,
  useSearchParams
} from "react-router-dom";
import { Fragment as Fragment4, jsx as jsx19, jsxs as jsxs9 } from "react/jsx-runtime";
var EMAIL_REGEX = /\S+@\S+\.\S+/;
var CODE_LENGTH = 6;
var RESEND_COOLDOWN_SECONDS = 60;
var DEFAULT_RETRY_AFTER_SECONDS = 60;
var BRAND_SLOGAN_TEXT = "It\u2019s time for something...";
var BRAND_SLOGAN_ACCENT = "different.";
function resolveRedirectTarget(state, fallback) {
  if (state !== null && typeof state === "object" && "from" in state) {
    const { from } = state;
    if (typeof from === "string" && from.length > 0) {
      return from;
    }
    if (from !== null && typeof from === "object" && "pathname" in from) {
      const { pathname } = from;
      if (typeof pathname === "string" && pathname.length > 0) {
        const { search } = from;
        return typeof search === "string" ? `${pathname}${search}` : pathname;
      }
    }
  }
  return fallback;
}
function getResponseHeader(headers, name) {
  if (headers === null || typeof headers !== "object") {
    return void 0;
  }
  const value = headers[name.toLowerCase()];
  return typeof value === "string" ? value : void 0;
}
async function postAuth(path, body) {
  const response = await apiClient.post(`/api/v1/auth${path}`, body, {
    validateStatus: () => true
  });
  const { status, data, headers } = response;
  return { status, data, headers };
}
var MFA_METHOD_LABELS = {
  TOTP: "Authenticator app",
  SMS: "Text message",
  RECOVERY: "Recovery code"
};
var CODE_SLOTS = Array.from({ length: CODE_LENGTH }, (_, position) => ({
  id: `mfa-digit-slot-${String(position)}`,
  position
}));
var LOGIN_KEYFRAMES = `
@keyframes plcy-login-shake {
  10%, 90% { transform: translateX(-1px); }
  20%, 80% { transform: translateX(2px); }
  30%, 50%, 70% { transform: translateX(-4px); }
  40%, 60% { transform: translateX(4px); }
}
@keyframes plcy-login-step-in {
  from { opacity: 0; transform: translateY(4px); }
  to { opacity: 1; transform: translateY(0); }
}
@keyframes plcy-login-check-in {
  from { opacity: 0; transform: scale(0.7); }
  to { opacity: 1; transform: scale(1); }
}
`;
var loginKeyframesInjected = false;
function injectLoginKeyframes() {
  if (loginKeyframesInjected || typeof document === "undefined") {
    return;
  }
  const style = document.createElement("style");
  style.textContent = LOGIN_KEYFRAMES;
  document.head.appendChild(style);
  loginKeyframesInjected = true;
}
function MfaCodeInput({
  value,
  onChange,
  onComplete,
  disabled,
  hasError,
  shakeToken,
  reducedMotion
}) {
  const inputRefs = useRef4([]);
  const focusBox = (position) => {
    inputRefs.current[position]?.focus();
  };
  useEffect14(() => {
    inputRefs.current[0]?.focus();
  }, []);
  const handleChange = (position, raw) => {
    const digit = raw.replace(/\D/g, "").slice(-1);
    const next = [...value];
    next[position] = digit;
    onChange(next);
    if (digit !== "" && position < CODE_LENGTH - 1) {
      focusBox(position + 1);
    }
    if (next.every((d) => d !== "")) {
      onComplete(next.join(""));
    }
  };
  const handleKeyDown = (position, event) => {
    if (event.key === "Backspace" && (value[position] ?? "") === "" && position > 0) {
      focusBox(position - 1);
    } else if (event.key === "ArrowLeft" && position > 0) {
      event.preventDefault();
      focusBox(position - 1);
    } else if (event.key === "ArrowRight" && position < CODE_LENGTH - 1) {
      event.preventDefault();
      focusBox(position + 1);
    }
  };
  const handlePaste = (event) => {
    const pasted = event.clipboardData.getData("text").replace(/\D/g, "").slice(0, CODE_LENGTH);
    if (pasted.length === 0) {
      return;
    }
    event.preventDefault();
    const next = CODE_SLOTS.map((slot) => pasted[slot.position] ?? "");
    onChange(next);
    if (pasted.length === CODE_LENGTH) {
      onComplete(pasted);
    } else {
      focusBox(Math.min(pasted.length, CODE_LENGTH - 1));
    }
  };
  return /* @__PURE__ */ jsx19(
    Box2,
    {
      role: "group",
      "aria-label": "Verification code",
      sx: {
        display: "flex",
        gap: 1,
        justifyContent: "center",
        my: 2,
        ...reducedMotion || shakeToken === 0 ? {} : { animation: "plcy-login-shake 300ms" }
      },
      children: CODE_SLOTS.map((slot) => /* @__PURE__ */ jsx19(
        TextField,
        {
          inputRef: (el) => {
            inputRefs.current[slot.position] = el;
          },
          value: value[slot.position] ?? "",
          onChange: (event) => {
            handleChange(slot.position, event.target.value);
          },
          onKeyDown: (event) => {
            handleKeyDown(slot.position, event);
          },
          onPaste: handlePaste,
          disabled,
          error: hasError,
          slotProps: {
            htmlInput: {
              inputMode: "numeric",
              autoComplete: slot.position === 0 ? "one-time-code" : "off",
              "aria-label": `Digit ${String(slot.position + 1)} of ${String(CODE_LENGTH)}`,
              maxLength: 1,
              style: {
                textAlign: "center",
                fontSize: "1.25rem",
                width: 32,
                height: 32,
                padding: "8px"
              }
            }
          }
        },
        slot.id
      ))
    },
    shakeToken
  );
}
function LoginPage(props) {
  const {
    portal,
    defaultRedirectPath,
    onSuccess,
    samlConfigured = false,
    tenantSlug,
    forgotPasswordPath = "/forgot-password"
  } = props;
  const { navigate: navigateProp } = props;
  const navigate = useMemo10(
    () => navigateProp ?? ((url) => {
      window.location.assign(url);
    }),
    [navigateProp]
  );
  const preset = PORTAL_PRESETS[portal];
  const theme = useTheme();
  const reducedMotion = usePrefersReducedMotion();
  const location = useLocation();
  const [searchParams, setSearchParams] = useSearchParams();
  useEffect14(() => {
    injectLoginKeyframes();
  }, []);
  const [ssoErrorBanner, setSsoErrorBanner] = useState12(
    () => searchParams.get("error")
  );
  useEffect14(() => {
    if (searchParams.has("error")) {
      const next = new URLSearchParams(searchParams);
      next.delete("error");
      setSearchParams(next, { replace: true });
    }
  }, [searchParams, setSearchParams]);
  const [step, setStep] = useState12("credentials");
  const [email, setEmail] = useState12("");
  const [password, setPassword] = useState12("");
  const [showPassword, setShowPassword] = useState12(false);
  const [fieldErrors, setFieldErrors] = useState12({});
  const [serverError, setServerError] = useState12(null);
  const [isSubmitting, setIsSubmitting] = useState12(false);
  const [credShakeToken, setCredShakeToken] = useState12(0);
  const triggerCredShake = useCallback6(() => {
    setCredShakeToken((prev) => prev + 1);
  }, []);
  const activeRetryAfterSeconds = serverError?.kind === "rateLimited" ? serverError.retryAfterSeconds ?? 0 : 0;
  useEffect14(() => {
    if (activeRetryAfterSeconds <= 0) {
      return void 0;
    }
    const timer = window.setInterval(() => {
      setServerError((prev) => {
        if (prev?.kind !== "rateLimited") {
          return prev;
        }
        const remaining = (prev.retryAfterSeconds ?? 1) - 1;
        return remaining <= 0 ? null : { ...prev, retryAfterSeconds: remaining };
      });
    }, 1e3);
    return () => window.clearInterval(timer);
  }, [activeRetryAfterSeconds]);
  const [challenge, setChallenge] = useState12(null);
  const [selectedMethod, setSelectedMethod] = useState12(null);
  const [focusedMethodIndex, setFocusedMethodIndex] = useState12(0);
  const methodCardRefs = useRef4([]);
  const [code, setCode] = useState12(
    Array(CODE_LENGTH).fill("")
  );
  const [mfaError, setMfaError] = useState12(null);
  const [mfaTerminalError, setMfaTerminalError] = useState12(null);
  const [isVerifying, setIsVerifying] = useState12(false);
  const [mfaShakeToken, setMfaShakeToken] = useState12(0);
  const [isResending, setIsResending] = useState12(false);
  const [resendCooldown, setResendCooldown] = useState12(0);
  const [resendAnnouncement, setResendAnnouncement] = useState12(
    null
  );
  useEffect14(() => {
    if (resendCooldown <= 0) {
      return void 0;
    }
    const timer = window.setInterval(() => {
      setResendCooldown((prev) => prev <= 1 ? 0 : prev - 1);
    }, 1e3);
    return () => window.clearInterval(timer);
  }, [resendCooldown]);
  const [showSuccessCheck, setShowSuccessCheck] = useState12(false);
  const redirectTarget = useMemo10(
    () => resolveRedirectTarget(
      location.state,
      defaultRedirectPath ?? preset.defaultRedirect
    ),
    [location.state, defaultRedirectPath, preset.defaultRedirect]
  );
  const finalizeSuccess = useCallback6(() => {
    onSuccess?.();
    setStep("redirecting");
  }, [onSuccess]);
  useEffect14(() => {
    if (step !== "redirecting") {
      return void 0;
    }
    const checkDelay = reducedMotion ? 0 : 400;
    const navigateDelay = reducedMotion ? 0 : 800;
    const checkTimer = window.setTimeout(
      () => setShowSuccessCheck(true),
      checkDelay
    );
    const navTimer = window.setTimeout(() => {
      navigate(redirectTarget);
    }, navigateDelay);
    return () => {
      window.clearTimeout(checkTimer);
      window.clearTimeout(navTimer);
    };
  }, [step, reducedMotion, redirectTarget, navigate]);
  const handleCredentialsSubmit = async (event) => {
    event.preventDefault();
    setServerError(null);
    const nextFieldErrors = {};
    if (!EMAIL_REGEX.test(email)) {
      nextFieldErrors.email = "Enter a valid email address.";
    }
    if (password.length === 0) {
      nextFieldErrors.password = "Enter your password.";
    }
    if (Object.keys(nextFieldErrors).length > 0) {
      setFieldErrors(nextFieldErrors);
      triggerCredShake();
      return;
    }
    setFieldErrors({});
    setIsSubmitting(true);
    try {
      const { status, data, headers } = await postAuth("/login", { email, password });
      if (status === 200) {
        finalizeSuccess();
        return;
      }
      if (status === 202) {
        const mfa = data;
        if (mfa.methods.length === 0) {
          setServerError({
            kind: "noFactorConfigured",
            message: "A second factor is required for this account, but none is configured. Contact your administrator."
          });
          return;
        }
        setChallenge({
          challengeId: mfa.challengeId,
          methods: mfa.methods,
          smsDestinationMasked: mfa.smsDestinationMasked ?? null
        });
        setCode(Array(CODE_LENGTH).fill(""));
        setMfaError(null);
        setMfaTerminalError(null);
        if (mfa.methods.length === 1) {
          setSelectedMethod(mfa.methods[0] ?? null);
          setStep("mfa-code-entry");
        } else {
          setFocusedMethodIndex(0);
          setStep("mfa-method-select");
        }
        return;
      }
      if (status === 401) {
        setServerError({
          kind: "invalid",
          message: data.message
        });
        setPassword("");
        triggerCredShake();
        return;
      }
      if (status === 423) {
        setServerError({
          kind: "locked",
          message: data.message
        });
        return;
      }
      if (status === 429) {
        const retryAfterHeader = getResponseHeader(headers, "retry-after");
        const parsedRetryAfter = retryAfterHeader !== void 0 ? Number(retryAfterHeader) : Number.NaN;
        const retryAfterSeconds = Number.isNaN(parsedRetryAfter) ? DEFAULT_RETRY_AFTER_SECONDS : parsedRetryAfter;
        setServerError({
          kind: "rateLimited",
          message: data.message,
          retryAfterSeconds
        });
        return;
      }
      setServerError({
        kind: "generic",
        message: "Something went wrong. Please try again."
      });
    } catch {
      setServerError({
        kind: "network",
        message: "Network error \u2014 check your connection and try again."
      });
    } finally {
      setIsSubmitting(false);
    }
  };
  const handleConfirmMethod = (method) => {
    setSelectedMethod(method);
    setCode(Array(CODE_LENGTH).fill(""));
    setMfaError(null);
    setStep("mfa-code-entry");
  };
  const submitMfaCode = useCallback6(
    async (fullCode) => {
      if (challenge === null || selectedMethod === null || isVerifying) {
        return;
      }
      setIsVerifying(true);
      setMfaError(null);
      try {
        const { status, data } = await postAuth("/mfa/verify", {
          challengeId: challenge.challengeId,
          code: fullCode,
          method: selectedMethod
        });
        if (status === 200) {
          finalizeSuccess();
          return;
        }
        if (status === 401) {
          setMfaError(data.message);
          setCode(Array(CODE_LENGTH).fill(""));
          setMfaShakeToken((prev) => prev + 1);
          return;
        }
        setMfaTerminalError(data.message);
      } catch {
        setMfaError("Network error \u2014 check your connection and try again.");
      } finally {
        setIsVerifying(false);
      }
    },
    [challenge, selectedMethod, isVerifying, finalizeSuccess]
  );
  const handleResend = async () => {
    if (challenge === null || resendCooldown > 0 || isResending) {
      return;
    }
    setIsResending(true);
    setResendAnnouncement(null);
    try {
      const { status, data } = await postAuth("/mfa/resend", {
        challengeId: challenge.challengeId,
        method: "SMS"
      });
      if (status === 202) {
        setResendCooldown(RESEND_COOLDOWN_SECONDS);
        setResendAnnouncement("A new code has been sent.");
      } else {
        setMfaTerminalError(data.message);
      }
    } catch {
      setMfaError("Network error \u2014 could not resend the code.");
    } finally {
      setIsResending(false);
    }
  };
  const handleUseDifferentMethod = () => {
    setSelectedMethod(null);
    setCode(Array(CODE_LENGTH).fill(""));
    setMfaError(null);
    setStep("mfa-method-select");
  };
  const resetToCredentials = () => {
    setChallenge(null);
    setSelectedMethod(null);
    setCode(Array(CODE_LENGTH).fill(""));
    setMfaError(null);
    setMfaTerminalError(null);
    setStep("credentials");
  };
  const handleGoogleSso = () => {
    navigate("/api/v1/auth/oauth/google");
  };
  const handleSamlSso = () => {
    const query = tenantSlug !== void 0 ? `?tenantSlug=${encodeURIComponent(tenantSlug)}` : "";
    navigate(`/api/v1/auth/sso/saml/login${query}`);
  };
  const logoVariant = theme.palette.mode === "dark" ? "dark" : "light";
  const stepTransitionSx = reducedMotion ? {} : { animation: "plcy-login-step-in 300ms ease-out" };
  const isRateLimitedAndWaiting = activeRetryAfterSeconds > 0;
  const cardSx = {
    maxWidth: 440,
    width: "100%",
    borderRadius: 3,
    overflow: "hidden",
    position: "relative",
    "&::before": {
      content: '""',
      position: "absolute",
      top: 0,
      left: 0,
      right: 0,
      height: 4,
      background: preset.accentColor
    }
  };
  const primaryButtonSx = {
    py: 1.5,
    minHeight: 44,
    borderRadius: 1.5,
    fontWeight: 600,
    textTransform: "none",
    color: preset.accentContrastText,
    backgroundColor: preset.accentColor,
    "&:hover": {
      backgroundColor: preset.accentColor,
      filter: "brightness(0.92)"
    }
  };
  if (serverError?.kind === "noFactorConfigured") {
    return /* @__PURE__ */ jsx19(
      Box2,
      {
        sx: {
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          minHeight: "100vh",
          p: 2
        },
        children: /* @__PURE__ */ jsx19(Card, { elevation: 4, sx: cardSx, children: /* @__PURE__ */ jsxs9(CardContent, { sx: { p: 4, textAlign: "center" }, children: [
          /* @__PURE__ */ jsx19(ErrorOutlineIcon, { color: "error", sx: { fontSize: 48, mb: 2 } }),
          /* @__PURE__ */ jsx19(Typography, { variant: "h6", component: "h1", gutterBottom: true, children: "Second factor required" }),
          /* @__PURE__ */ jsx19(
            Alert,
            {
              severity: "error",
              role: "alert",
              "aria-live": "assertive",
              sx: { textAlign: "left" },
              children: serverError.message
            }
          )
        ] }) })
      }
    );
  }
  return /* @__PURE__ */ jsxs9(
    Box2,
    {
      sx: {
        minHeight: "100vh",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        p: 2,
        background: `linear-gradient(135deg, ${preset.accentColor}0d 0%, ${preset.accentColor}1a 50%, ${preset.accentColor}08 100%)`
      },
      children: [
        /* @__PURE__ */ jsx19(
          Box2,
          {
            role: "alert",
            "aria-live": "assertive",
            sx: {
              position: "absolute",
              width: 1,
              height: 1,
              overflow: "hidden",
              clip: "rect(0,0,0,0)"
            },
            children: serverError?.message ?? mfaError ?? mfaTerminalError ?? ""
          }
        ),
        /* @__PURE__ */ jsxs9(
          Box2,
          {
            role: "status",
            "aria-live": "polite",
            sx: {
              position: "absolute",
              width: 1,
              height: 1,
              overflow: "hidden",
              clip: "rect(0,0,0,0)"
            },
            children: [
              step === "mfa-method-select" && "Choose a second-factor method.",
              step === "mfa-code-entry" && selectedMethod !== null && `Enter the code from your ${MFA_METHOD_LABELS[selectedMethod].toLowerCase()}.`,
              step === "redirecting" && "Signed in. Redirecting\u2026",
              resendAnnouncement ?? ""
            ]
          }
        ),
        /* @__PURE__ */ jsx19(Card, { elevation: 8, sx: { ...cardSx, boxShadow: theme.shadows[8] }, children: /* @__PURE__ */ jsxs9(CardContent, { sx: { p: 4 }, children: [
          /* @__PURE__ */ jsxs9(
            Box2,
            {
              sx: {
                display: "flex",
                flexDirection: "column",
                alignItems: "center",
                mb: 3
              },
              children: [
                /* @__PURE__ */ jsx19(
                  AnimatedLogo,
                  {
                    variant: logoVariant,
                    animation: "subtle",
                    height: 64
                  }
                ),
                /* @__PURE__ */ jsx19(Box2, { sx: { mt: 1 }, children: /* @__PURE__ */ jsx19(
                  AnimatedSlogan,
                  {
                    text: BRAND_SLOGAN_TEXT,
                    accentText: BRAND_SLOGAN_ACCENT,
                    variant: logoVariant,
                    animation: "none",
                    fontSize: "0.8125rem",
                    fontWeight: 400
                  }
                ) }),
                /* @__PURE__ */ jsx19(
                  Typography,
                  {
                    variant: "h6",
                    component: "h1",
                    sx: {
                      position: "absolute",
                      width: 1,
                      height: 1,
                      overflow: "hidden",
                      clip: "rect(0,0,0,0)"
                    },
                    children: preset.displayName
                  }
                )
              ]
            }
          ),
          ssoErrorBanner !== null && /* @__PURE__ */ jsx19(
            Alert,
            {
              severity: "error",
              icon: /* @__PURE__ */ jsx19(ErrorOutlineIcon, {}),
              role: "alert",
              sx: { mb: 2, borderRadius: 1.5 },
              onClose: () => setSsoErrorBanner(null),
              children: ssoErrorBanner
            }
          ),
          step === "credentials" && /* @__PURE__ */ jsxs9(Box2, { sx: stepTransitionSx, children: [
            serverError !== null && /* @__PURE__ */ jsxs9(
              Alert,
              {
                severity: serverError.kind === "invalid" || serverError.kind === "network" ? "error" : "warning",
                icon: serverError.kind === "invalid" || serverError.kind === "network" ? /* @__PURE__ */ jsx19(ErrorOutlineIcon, {}) : /* @__PURE__ */ jsx19(WarningAmberIcon, {}),
                role: "alert",
                sx: { mb: 2, borderRadius: 1.5 },
                children: [
                  serverError.message,
                  isRateLimitedAndWaiting && ` Try again in ${String(activeRetryAfterSeconds)}s.`
                ]
              }
            ),
            /* @__PURE__ */ jsxs9(
              Box2,
              {
                component: "form",
                noValidate: true,
                onSubmit: (e) => {
                  void handleCredentialsSubmit(e);
                },
                sx: reducedMotion || credShakeToken === 0 ? {} : { animation: "plcy-login-shake 300ms" },
                children: [
                  /* @__PURE__ */ jsx19(
                    TextField,
                    {
                      fullWidth: true,
                      label: "Email",
                      type: "email",
                      value: email,
                      onChange: (e) => {
                        setEmail(e.target.value);
                        setFieldErrors((prev) => ({ ...prev, email: void 0 }));
                      },
                      error: fieldErrors.email !== void 0,
                      helperText: fieldErrors.email ?? " ",
                      disabled: isSubmitting,
                      required: true,
                      autoComplete: "email",
                      sx: { mb: 1 }
                    }
                  ),
                  /* @__PURE__ */ jsx19(
                    TextField,
                    {
                      fullWidth: true,
                      label: "Password",
                      type: showPassword ? "text" : "password",
                      value: password,
                      onChange: (e) => {
                        setPassword(e.target.value);
                        setFieldErrors((prev) => ({
                          ...prev,
                          password: void 0
                        }));
                      },
                      error: fieldErrors.password !== void 0,
                      helperText: fieldErrors.password ?? " ",
                      disabled: isSubmitting,
                      required: true,
                      autoComplete: "current-password",
                      slotProps: {
                        input: {
                          endAdornment: /* @__PURE__ */ jsx19(InputAdornment, { position: "end", children: /* @__PURE__ */ jsx19(
                            IconButton,
                            {
                              "aria-label": showPassword ? "Hide password" : "Show password",
                              onClick: () => setShowPassword((prev) => !prev),
                              edge: "end",
                              sx: { minWidth: 44, minHeight: 44 },
                              children: showPassword ? /* @__PURE__ */ jsx19(VisibilityOff, {}) : /* @__PURE__ */ jsx19(Visibility, {})
                            }
                          ) })
                        }
                      },
                      sx: { mb: 1 }
                    }
                  ),
                  /* @__PURE__ */ jsx19(
                    Box2,
                    {
                      sx: { display: "flex", justifyContent: "flex-end", mb: 2 },
                      children: /* @__PURE__ */ jsx19(
                        Link,
                        {
                          component: RouterLink,
                          to: forgotPasswordPath,
                          underline: "hover",
                          variant: "body2",
                          children: "Forgot password?"
                        }
                      )
                    }
                  ),
                  /* @__PURE__ */ jsx19(
                    Button,
                    {
                      type: "submit",
                      variant: "contained",
                      fullWidth: true,
                      size: "large",
                      disabled: isSubmitting || isRateLimitedAndWaiting,
                      "aria-label": "Sign in",
                      "aria-busy": isSubmitting,
                      sx: { ...primaryButtonSx, mb: 2 },
                      children: isSubmitting ? /* @__PURE__ */ jsx19(
                        CircularProgress,
                        {
                          size: 22,
                          "aria-label": "Signing in",
                          sx: { color: "inherit" }
                        }
                      ) : "Sign in"
                    }
                  )
                ]
              },
              credShakeToken
            ),
            /* @__PURE__ */ jsx19(Divider, { sx: { my: 2 }, children: /* @__PURE__ */ jsx19(Typography, { variant: "caption", color: "text.secondary", children: "or" }) }),
            /* @__PURE__ */ jsxs9(Box2, { sx: { display: "flex", flexDirection: "column", gap: 1.5 }, children: [
              /* @__PURE__ */ jsx19(
                Button,
                {
                  variant: "outlined",
                  color: "inherit",
                  fullWidth: true,
                  startIcon: /* @__PURE__ */ jsx19(GoogleIcon, {}),
                  onClick: handleGoogleSso,
                  sx: { minHeight: 44, textTransform: "none" },
                  children: "Continue with Google"
                }
              ),
              samlConfigured && /* @__PURE__ */ jsx19(
                Button,
                {
                  variant: "outlined",
                  color: "inherit",
                  fullWidth: true,
                  onClick: handleSamlSso,
                  sx: { minHeight: 44, textTransform: "none" },
                  children: "Continue with SSO"
                }
              )
            ] })
          ] }),
          step === "mfa-method-select" && challenge !== null && /* @__PURE__ */ jsxs9(Box2, { sx: stepTransitionSx, children: [
            /* @__PURE__ */ jsx19(Typography, { variant: "body1", sx: { mb: 2 }, children: "Choose how you'd like to verify it's you." }),
            /* @__PURE__ */ jsx19(
              Box2,
              {
                role: "radiogroup",
                "aria-label": "Second-factor method",
                sx: {
                  display: "flex",
                  flexDirection: "column",
                  gap: 1.5,
                  mb: 3
                },
                onKeyDown: (e) => {
                  const { methods } = challenge;
                  if (e.key === "ArrowDown" || e.key === "ArrowRight") {
                    e.preventDefault();
                    const nextIndex = (focusedMethodIndex + 1) % methods.length;
                    setFocusedMethodIndex(nextIndex);
                    methodCardRefs.current[nextIndex]?.focus();
                  } else if (e.key === "ArrowUp" || e.key === "ArrowLeft") {
                    e.preventDefault();
                    const nextIndex = (focusedMethodIndex - 1 + methods.length) % methods.length;
                    setFocusedMethodIndex(nextIndex);
                    methodCardRefs.current[nextIndex]?.focus();
                  }
                },
                children: challenge.methods.map((method, index) => {
                  const selected = selectedMethod === method;
                  return /* @__PURE__ */ jsx19(
                    Paper,
                    {
                      ref: (el) => {
                        methodCardRefs.current[index] = el;
                      },
                      role: "radio",
                      "aria-checked": selected,
                      tabIndex: focusedMethodIndex === index ? 0 : -1,
                      onFocus: () => setFocusedMethodIndex(index),
                      onClick: () => handleConfirmMethod(method),
                      onKeyDown: (e) => {
                        if (e.key === "Enter" || e.key === " ") {
                          e.preventDefault();
                          handleConfirmMethod(method);
                        }
                      },
                      variant: "outlined",
                      sx: {
                        minHeight: 56,
                        display: "flex",
                        alignItems: "center",
                        gap: 1.5,
                        p: 2,
                        cursor: "pointer",
                        borderWidth: selected ? 2 : 1,
                        borderColor: selected ? preset.accentColor : "divider"
                      },
                      children: /* @__PURE__ */ jsxs9(Box2, { children: [
                        /* @__PURE__ */ jsx19(Typography, { variant: "body1", fontWeight: 500, children: MFA_METHOD_LABELS[method] }),
                        method === "SMS" && challenge.smsDestinationMasked !== null && /* @__PURE__ */ jsxs9(
                          Typography,
                          {
                            variant: "caption",
                            color: "text.secondary",
                            children: [
                              "Text message to ",
                              challenge.smsDestinationMasked
                            ]
                          }
                        )
                      ] })
                    },
                    method
                  );
                })
              }
            ),
            /* @__PURE__ */ jsx19(
              Button,
              {
                variant: "text",
                onClick: resetToCredentials,
                sx: { textTransform: "none", minHeight: 44 },
                children: "Back to sign in"
              }
            )
          ] }),
          step === "mfa-code-entry" && challenge !== null && selectedMethod !== null && /* @__PURE__ */ jsx19(Box2, { sx: stepTransitionSx, children: mfaTerminalError !== null ? /* @__PURE__ */ jsxs9(Fragment4, { children: [
            /* @__PURE__ */ jsx19(
              Alert,
              {
                severity: "error",
                icon: /* @__PURE__ */ jsx19(ErrorOutlineIcon, {}),
                role: "alert",
                sx: { mb: 2, borderRadius: 1.5 },
                children: mfaTerminalError
              }
            ),
            /* @__PURE__ */ jsx19(
              Button,
              {
                variant: "contained",
                fullWidth: true,
                onClick: resetToCredentials,
                sx: primaryButtonSx,
                children: "Back to sign in"
              }
            )
          ] }) : /* @__PURE__ */ jsxs9(Fragment4, { children: [
            /* @__PURE__ */ jsxs9(Typography, { variant: "body1", align: "center", sx: { mb: 1 }, children: [
              "Enter the code from your",
              " ",
              MFA_METHOD_LABELS[selectedMethod].toLowerCase(),
              "."
            ] }),
            mfaError !== null && /* @__PURE__ */ jsx19(
              Alert,
              {
                severity: "error",
                icon: /* @__PURE__ */ jsx19(ErrorOutlineIcon, {}),
                role: "alert",
                sx: { mb: 1, borderRadius: 1.5 },
                children: mfaError
              }
            ),
            /* @__PURE__ */ jsx19(
              MfaCodeInput,
              {
                value: code,
                onChange: setCode,
                onComplete: (fullCode) => {
                  void submitMfaCode(fullCode);
                },
                disabled: isVerifying,
                hasError: mfaError !== null,
                shakeToken: mfaShakeToken,
                reducedMotion
              }
            ),
            isVerifying && /* @__PURE__ */ jsx19(
              Box2,
              {
                sx: {
                  display: "flex",
                  justifyContent: "center",
                  mb: 2
                },
                children: /* @__PURE__ */ jsx19(
                  CircularProgress,
                  {
                    size: 22,
                    "aria-label": "Verifying code"
                  }
                )
              }
            ),
            /* @__PURE__ */ jsxs9(
              Box2,
              {
                sx: {
                  display: "flex",
                  flexDirection: "column",
                  alignItems: "center",
                  gap: 1
                },
                children: [
                  selectedMethod === "SMS" && /* @__PURE__ */ jsx19(
                    Button,
                    {
                      variant: "text",
                      disabled: resendCooldown > 0 || isResending,
                      onClick: () => {
                        void handleResend();
                      },
                      sx: { textTransform: "none", minHeight: 44 },
                      children: resendCooldown > 0 ? `Resend code (${String(resendCooldown)}s)` : isResending ? "Sending\u2026" : "Resend code"
                    }
                  ),
                  challenge.methods.length > 1 && /* @__PURE__ */ jsx19(
                    Button,
                    {
                      variant: "text",
                      onClick: handleUseDifferentMethod,
                      sx: { textTransform: "none", minHeight: 44 },
                      children: "Use a different method"
                    }
                  ),
                  /* @__PURE__ */ jsx19(
                    Button,
                    {
                      variant: "text",
                      color: "inherit",
                      onClick: resetToCredentials,
                      sx: { textTransform: "none", minHeight: 44 },
                      children: "Back to sign in"
                    }
                  )
                ]
              }
            )
          ] }) }),
          step === "redirecting" && /* @__PURE__ */ jsxs9(
            Box2,
            {
              sx: {
                display: "flex",
                flexDirection: "column",
                alignItems: "center",
                justifyContent: "center",
                minHeight: 160,
                gap: 2
              },
              children: [
                showSuccessCheck ? /* @__PURE__ */ jsx19(
                  Box2,
                  {
                    sx: {
                      ...reducedMotion ? {} : { animation: "plcy-login-check-in 400ms ease-out" },
                      color: "success.main",
                      fontSize: 48
                    },
                    "aria-hidden": "true",
                    children: "\u2713"
                  }
                ) : /* @__PURE__ */ jsx19(
                  CircularProgress,
                  {
                    size: 40,
                    "aria-label": "Redirecting",
                    sx: { color: preset.accentColor }
                  }
                ),
                /* @__PURE__ */ jsx19(Typography, { variant: "body2", color: "text.secondary", children: "Signed in \u2014 redirecting\u2026" })
              ]
            }
          )
        ] }) })
      ]
    }
  );
}

// src/components/ProtectedRoute.tsx
import { Navigate, Outlet, useLocation as useLocation2 } from "react-router-dom";

// src/components/AccessDenied.tsx
import LockOutlinedIcon from "@mui/icons-material/LockOutlined";
import { Box as Box3, Button as Button2, Paper as Paper2, Typography as Typography2 } from "@mui/material";
import { useNavigate } from "react-router-dom";
import { jsx as jsx20, jsxs as jsxs10 } from "react/jsx-runtime";
function describeMissingGrant(missingRoles, missingPermissions, missingCapability) {
  if (missingRoles !== void 0 && missingRoles.length > 0) {
    return `This page requires the ${missingRoles.join(" or ")} role.`;
  }
  if (missingPermissions !== void 0 && missingPermissions.length > 0) {
    return `This page requires the ${missingPermissions.join(", ")} permission${missingPermissions.length > 1 ? "s" : ""}.`;
  }
  if (missingCapability !== void 0) {
    return `This page requires ${missingCapability.action} access to ${missingCapability.module}.`;
  }
  return "You do not have access to this page.";
}
function AccessDenied({
  portal,
  missingRoles,
  missingPermissions,
  missingCapability,
  homePath
}) {
  const navigate = useNavigate();
  const preset = portal !== void 0 ? PORTAL_PRESETS[portal] : void 0;
  const accentColor = preset?.accentColor ?? "#f59e0b";
  const target = homePath ?? preset?.defaultRedirect ?? "/";
  const reason = describeMissingGrant(
    missingRoles,
    missingPermissions,
    missingCapability
  );
  const handleGoBack = () => {
    void navigate(-1);
  };
  const handleGoHome = () => {
    void navigate(target, { replace: true });
  };
  return /* @__PURE__ */ jsx20(
    Box3,
    {
      role: "alert",
      "aria-live": "assertive",
      sx: {
        display: "flex",
        justifyContent: "center",
        alignItems: "center",
        minHeight: "100vh",
        bgcolor: "background.default",
        p: 3
      },
      children: /* @__PURE__ */ jsxs10(
        Paper2,
        {
          elevation: 3,
          sx: {
            p: 4,
            maxWidth: 480,
            width: "100%",
            textAlign: "center",
            borderRadius: 3,
            overflow: "hidden",
            position: "relative",
            "&::before": {
              content: '""',
              position: "absolute",
              top: 0,
              left: 0,
              right: 0,
              height: 4,
              background: accentColor
            }
          },
          children: [
            /* @__PURE__ */ jsx20(
              Box3,
              {
                sx: {
                  display: "inline-flex",
                  alignItems: "center",
                  justifyContent: "center",
                  width: 64,
                  height: 64,
                  borderRadius: "50%",
                  bgcolor: "warning.light",
                  mb: 2
                },
                children: /* @__PURE__ */ jsx20(LockOutlinedIcon, { sx: { fontSize: 32, color: "warning.dark" } })
              }
            ),
            /* @__PURE__ */ jsx20(Typography2, { variant: "h5", component: "h1", fontWeight: 600, gutterBottom: true, children: "Access denied" }),
            /* @__PURE__ */ jsx20(Typography2, { variant: "body1", color: "text.secondary", sx: { mb: 1 }, children: reason }),
            /* @__PURE__ */ jsx20(Typography2, { variant: "body2", color: "text.secondary", sx: { mb: 3 }, children: "If you believe this is an error, contact your administrator to request access." }),
            /* @__PURE__ */ jsxs10(Box3, { sx: { display: "flex", gap: 2, justifyContent: "center" }, children: [
              /* @__PURE__ */ jsx20(
                Button2,
                {
                  variant: "outlined",
                  onClick: handleGoBack,
                  sx: { minWidth: 100, minHeight: 44 },
                  children: "Go back"
                }
              ),
              /* @__PURE__ */ jsx20(
                Button2,
                {
                  variant: "contained",
                  onClick: handleGoHome,
                  sx: {
                    minWidth: 100,
                    minHeight: 44,
                    backgroundColor: accentColor,
                    "&:hover": {
                      backgroundColor: accentColor,
                      filter: "brightness(0.92)"
                    }
                  },
                  children: "Go to dashboard"
                }
              )
            ] })
          ]
        }
      )
    }
  );
}

// src/components/ProtectedRoute.tsx
import { jsx as jsx21 } from "react/jsx-runtime";
var NO_REQUIRED_PERMISSIONS = [];
function ProtectedRoute({
  children,
  requireAuth = true,
  requiredRoles,
  requiredPermissions,
  requiredCapability,
  portal,
  fallbackPath = "/login",
  unauthorizedFallback,
  loadingFallback
}) {
  const { isAuthenticated, isLoading, hasAnyRole } = useAuth();
  const location = useLocation2();
  const permissionCheck = usePermissions(
    requiredPermissions ?? NO_REQUIRED_PERMISSIONS
  );
  const { hasCapability } = useCapabilities();
  if (requireAuth && isLoading) {
    return loadingFallback ?? /* @__PURE__ */ jsx21(FullPageSpinner, { label: "Checking authentication\u2026" });
  }
  if (requireAuth && !isAuthenticated) {
    return /* @__PURE__ */ jsx21(Navigate, { to: fallbackPath, replace: true, state: { from: location } });
  }
  const roleCheckPassed = requiredRoles === void 0 || requiredRoles.length === 0 || hasAnyRole(requiredRoles);
  const permissionCheckPassed = requiredPermissions === void 0 || requiredPermissions.length === 0 || permissionCheck.allowed;
  const capabilityCheckPassed = requiredCapability === void 0 || hasCapability(requiredCapability.module, requiredCapability.action);
  const authorized = roleCheckPassed && permissionCheckPassed && capabilityCheckPassed;
  if (!authorized) {
    if (unauthorizedFallback !== void 0) {
      return unauthorizedFallback;
    }
    return /* @__PURE__ */ jsx21(
      AccessDenied,
      {
        portal,
        missingRoles: !roleCheckPassed ? requiredRoles : void 0,
        missingPermissions: roleCheckPassed && !permissionCheckPassed ? requiredPermissions : void 0,
        missingCapability: roleCheckPassed && permissionCheckPassed && !capabilityCheckPassed ? requiredCapability : void 0
      }
    );
  }
  return children ?? /* @__PURE__ */ jsx21(Outlet, {});
}

// src/components/PasswordResetRequestPage.tsx
import {
  Alert as Alert2,
  Box as Box4,
  Button as Button3,
  Card as Card2,
  CardContent as CardContent2,
  CircularProgress as CircularProgress2,
  Link as Link2,
  TextField as TextField2,
  Typography as Typography3
} from "@mui/material";
import { useCallback as useCallback7, useState as useState13 } from "react";
import { Link as RouterLink2 } from "react-router-dom";
import { jsx as jsx22, jsxs as jsxs11 } from "react/jsx-runtime";
var EMAIL_REGEX2 = /\S+@\S+\.\S+/;
function PasswordResetRequestPage({
  portal,
  loginPath = "/login"
}) {
  const preset = PORTAL_PRESETS[portal];
  const [email, setEmail] = useState13("");
  const [isLoading, setIsLoading] = useState13(false);
  const [isSubmitted, setIsSubmitted] = useState13(false);
  const [validationError, setValidationError] = useState13(null);
  const [apiError, setApiError] = useState13(null);
  const handleEmailChange = useCallback7(
    (event) => {
      setEmail(event.target.value);
      setValidationError(null);
      setApiError(null);
    },
    []
  );
  const handleSubmit = async (event) => {
    event.preventDefault();
    if (!EMAIL_REGEX2.test(email)) {
      setValidationError("Enter a valid email address.");
      return;
    }
    setIsLoading(true);
    setApiError(null);
    try {
      await apiClient.post("/api/v1/auth/password/reset", { email });
      setIsSubmitted(true);
    } catch (error) {
      setApiError(
        isApiError(error) ? error.message : "Something went wrong. Please try again."
      );
    } finally {
      setIsLoading(false);
    }
  };
  const cardSx = {
    maxWidth: 440,
    width: "100%",
    borderRadius: 3,
    overflow: "hidden",
    position: "relative",
    "&::before": {
      content: '""',
      position: "absolute",
      top: 0,
      left: 0,
      right: 0,
      height: 4,
      background: preset.accentColor
    }
  };
  const pageWrapperSx = {
    display: "flex",
    justifyContent: "center",
    alignItems: "center",
    minHeight: "100vh",
    p: 2,
    background: `linear-gradient(135deg, ${preset.accentColor}0d 0%, ${preset.accentColor}1a 50%, ${preset.accentColor}08 100%)`
  };
  if (isSubmitted) {
    return /* @__PURE__ */ jsx22(Box4, { sx: pageWrapperSx, children: /* @__PURE__ */ jsx22(Card2, { elevation: 4, sx: cardSx, children: /* @__PURE__ */ jsxs11(CardContent2, { sx: { p: { xs: 3, sm: 4 } }, children: [
      /* @__PURE__ */ jsx22(
        Typography3,
        {
          variant: "h5",
          component: "h1",
          align: "center",
          fontWeight: 600,
          gutterBottom: true,
          children: "Check your email"
        }
      ),
      /* @__PURE__ */ jsxs11(
        Alert2,
        {
          severity: "success",
          role: "status",
          "aria-live": "polite",
          sx: { mb: 3, borderRadius: 1.5 },
          "data-testid": "reset-email-sent",
          children: [
            "If an account exists for ",
            email,
            ", you will receive a password reset link shortly."
          ]
        }
      ),
      /* @__PURE__ */ jsx22(
        Typography3,
        {
          variant: "body2",
          align: "center",
          color: "text.secondary",
          sx: { mb: 3 },
          children: "Didn't receive the email? Check your spam folder or try again."
        }
      ),
      /* @__PURE__ */ jsx22(Box4, { sx: { display: "flex", justifyContent: "center" }, children: /* @__PURE__ */ jsx22(Link2, { component: RouterLink2, to: loginPath, underline: "hover", children: "Back to sign in" }) })
    ] }) }) });
  }
  return /* @__PURE__ */ jsx22(Box4, { sx: pageWrapperSx, children: /* @__PURE__ */ jsx22(Card2, { elevation: 4, sx: cardSx, children: /* @__PURE__ */ jsxs11(CardContent2, { sx: { p: { xs: 3, sm: 4 } }, children: [
    /* @__PURE__ */ jsx22(
      Typography3,
      {
        variant: "h5",
        component: "h1",
        align: "center",
        fontWeight: 600,
        gutterBottom: true,
        children: "Forgot your password?"
      }
    ),
    /* @__PURE__ */ jsxs11(
      Typography3,
      {
        variant: "body2",
        align: "center",
        color: "text.secondary",
        sx: { mb: 3 },
        children: [
          "Enter the email address for your ",
          preset.displayName,
          " account and we'll send you a link to reset your password."
        ]
      }
    ),
    apiError !== null && /* @__PURE__ */ jsx22(
      Alert2,
      {
        severity: "error",
        role: "alert",
        "aria-live": "assertive",
        sx: { mb: 2, borderRadius: 1.5 },
        children: apiError
      }
    ),
    /* @__PURE__ */ jsxs11(
      Box4,
      {
        component: "form",
        noValidate: true,
        onSubmit: (e) => {
          void handleSubmit(e);
        },
        children: [
          /* @__PURE__ */ jsx22(
            TextField2,
            {
              fullWidth: true,
              label: "Email address",
              type: "email",
              value: email,
              onChange: handleEmailChange,
              error: validationError !== null,
              helperText: validationError ?? " ",
              margin: "normal",
              autoComplete: "email",
              disabled: isLoading,
              slotProps: { htmlInput: { "aria-label": "Email address" } }
            }
          ),
          /* @__PURE__ */ jsx22(
            Button3,
            {
              type: "submit",
              fullWidth: true,
              variant: "contained",
              size: "large",
              disabled: isLoading,
              sx: {
                mt: 2,
                mb: 2,
                py: 1.5,
                minHeight: 44,
                borderRadius: 1.5,
                fontWeight: 600,
                textTransform: "none",
                backgroundColor: preset.accentColor,
                "&:hover": {
                  backgroundColor: preset.accentColor,
                  filter: "brightness(0.92)"
                }
              },
              children: isLoading ? /* @__PURE__ */ jsx22(CircularProgress2, { size: 22, sx: { color: "inherit" } }) : "Send reset link"
            }
          ),
          /* @__PURE__ */ jsx22(Box4, { sx: { display: "flex", justifyContent: "center" }, children: /* @__PURE__ */ jsx22(Link2, { component: RouterLink2, to: loginPath, underline: "hover", children: "Back to sign in" }) })
        ]
      }
    )
  ] }) }) });
}

// src/components/PasswordResetConfirmPage.tsx
import { Visibility as Visibility2, VisibilityOff as VisibilityOff2 } from "@mui/icons-material";
import {
  Alert as Alert3,
  Box as Box5,
  Button as Button4,
  Card as Card3,
  CardContent as CardContent3,
  CircularProgress as CircularProgress3,
  IconButton as IconButton2,
  InputAdornment as InputAdornment2,
  Link as Link3,
  TextField as TextField3,
  Typography as Typography4
} from "@mui/material";
import { useCallback as useCallback8, useState as useState14 } from "react";
import {
  Link as RouterLink3,
  useParams,
  useSearchParams as useSearchParams2
} from "react-router-dom";
import { jsx as jsx23, jsxs as jsxs12 } from "react/jsx-runtime";
function PasswordResetConfirmPage({
  portal,
  loginPath = "/login"
}) {
  const preset = PORTAL_PRESETS[portal];
  const params = useParams();
  const [searchParams] = useSearchParams2();
  const token = params.token ?? searchParams.get("token");
  const [password, setPassword] = useState14("");
  const [confirmPassword, setConfirmPassword] = useState14("");
  const [showPassword, setShowPassword] = useState14(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState14(false);
  const [isLoading, setIsLoading] = useState14(false);
  const [isSuccess, setIsSuccess] = useState14(false);
  const [passwordError, setPasswordError] = useState14(null);
  const [confirmError, setConfirmError] = useState14(null);
  const [apiError, setApiError] = useState14(null);
  const handlePasswordChange = useCallback8(
    (event) => {
      setPassword(event.target.value);
      setPasswordError(null);
      setApiError(null);
    },
    []
  );
  const handleConfirmPasswordChange = useCallback8(
    (event) => {
      setConfirmPassword(event.target.value);
      setConfirmError(null);
      setApiError(null);
    },
    []
  );
  const handleSubmit = async (event) => {
    event.preventDefault();
    if (!isPasswordValid(password)) {
      setPasswordError("Password does not meet all requirements.");
      return;
    }
    if (password !== confirmPassword) {
      setConfirmError("Passwords do not match.");
      return;
    }
    if (token === null) {
      return;
    }
    setIsLoading(true);
    setApiError(null);
    try {
      await apiClient.post(`/api/v1/auth/password/reset/${token}`, {
        newPassword: password
      });
      setIsSuccess(true);
    } catch (error) {
      setApiError(
        isApiError(error) ? error.message : "Something went wrong. Please try again."
      );
    } finally {
      setIsLoading(false);
    }
  };
  const cardSx = {
    maxWidth: 440,
    width: "100%",
    borderRadius: 3,
    overflow: "hidden",
    position: "relative",
    "&::before": {
      content: '""',
      position: "absolute",
      top: 0,
      left: 0,
      right: 0,
      height: 4,
      background: preset.accentColor
    }
  };
  const pageWrapperSx = {
    display: "flex",
    justifyContent: "center",
    alignItems: "center",
    minHeight: "100vh",
    p: 2,
    background: `linear-gradient(135deg, ${preset.accentColor}0d 0%, ${preset.accentColor}1a 50%, ${preset.accentColor}08 100%)`
  };
  if (token === null) {
    return /* @__PURE__ */ jsx23(Box5, { sx: pageWrapperSx, children: /* @__PURE__ */ jsx23(Card3, { elevation: 4, sx: cardSx, children: /* @__PURE__ */ jsxs12(CardContent3, { sx: { p: { xs: 3, sm: 4 } }, children: [
      /* @__PURE__ */ jsx23(
        Typography4,
        {
          variant: "h5",
          component: "h1",
          fontWeight: 600,
          gutterBottom: true,
          children: "Invalid link"
        }
      ),
      /* @__PURE__ */ jsx23(
        Alert3,
        {
          severity: "error",
          role: "alert",
          sx: { mb: 3, borderRadius: 1.5 },
          children: "This reset link is invalid or missing its token. Please request a new password reset link."
        }
      ),
      /* @__PURE__ */ jsx23(Box5, { sx: { display: "flex", justifyContent: "center" }, children: /* @__PURE__ */ jsx23(Link3, { component: RouterLink3, to: loginPath, underline: "hover", children: "Back to sign in" }) })
    ] }) }) });
  }
  if (isSuccess) {
    return /* @__PURE__ */ jsx23(Box5, { sx: pageWrapperSx, children: /* @__PURE__ */ jsx23(Card3, { elevation: 4, sx: cardSx, children: /* @__PURE__ */ jsxs12(CardContent3, { sx: { p: { xs: 3, sm: 4 } }, children: [
      /* @__PURE__ */ jsx23(
        Typography4,
        {
          variant: "h5",
          component: "h1",
          fontWeight: 600,
          gutterBottom: true,
          children: "Password reset successful"
        }
      ),
      /* @__PURE__ */ jsx23(
        Alert3,
        {
          severity: "success",
          role: "status",
          "aria-live": "polite",
          sx: { mb: 3, borderRadius: 1.5 },
          children: "Your password has been reset. You can now sign in with your new password."
        }
      ),
      /* @__PURE__ */ jsx23(Box5, { sx: { display: "flex", justifyContent: "center" }, children: /* @__PURE__ */ jsx23(
        Button4,
        {
          component: RouterLink3,
          to: loginPath,
          variant: "contained",
          size: "large",
          sx: {
            minHeight: 44,
            textTransform: "none",
            backgroundColor: preset.accentColor,
            "&:hover": {
              backgroundColor: preset.accentColor,
              filter: "brightness(0.92)"
            }
          },
          children: "Go to sign in"
        }
      ) })
    ] }) }) });
  }
  return /* @__PURE__ */ jsx23(Box5, { sx: pageWrapperSx, children: /* @__PURE__ */ jsx23(Card3, { elevation: 4, sx: cardSx, children: /* @__PURE__ */ jsxs12(CardContent3, { sx: { p: { xs: 3, sm: 4 } }, children: [
    /* @__PURE__ */ jsx23(
      Typography4,
      {
        variant: "h5",
        component: "h1",
        align: "center",
        fontWeight: 600,
        gutterBottom: true,
        children: "Reset your password"
      }
    ),
    /* @__PURE__ */ jsxs12(
      Typography4,
      {
        variant: "body2",
        align: "center",
        color: "text.secondary",
        sx: { mb: 3 },
        children: [
          "Choose a new password for your ",
          preset.displayName,
          " account."
        ]
      }
    ),
    apiError !== null && /* @__PURE__ */ jsx23(
      Alert3,
      {
        severity: "error",
        role: "alert",
        "aria-live": "assertive",
        sx: { mb: 2, borderRadius: 1.5 },
        children: apiError
      }
    ),
    /* @__PURE__ */ jsxs12(
      Box5,
      {
        component: "form",
        noValidate: true,
        onSubmit: (e) => {
          void handleSubmit(e);
        },
        children: [
          /* @__PURE__ */ jsx23(
            TextField3,
            {
              fullWidth: true,
              label: "New password",
              type: showPassword ? "text" : "password",
              value: password,
              onChange: handlePasswordChange,
              error: passwordError !== null,
              helperText: passwordError ?? " ",
              margin: "normal",
              autoComplete: "new-password",
              disabled: isLoading,
              slotProps: {
                input: {
                  endAdornment: /* @__PURE__ */ jsx23(InputAdornment2, { position: "end", children: /* @__PURE__ */ jsx23(
                    IconButton2,
                    {
                      "aria-label": showPassword ? "Hide password" : "Show password",
                      onClick: () => setShowPassword((prev) => !prev),
                      edge: "end",
                      disabled: isLoading,
                      sx: { minWidth: 44, minHeight: 44 },
                      children: showPassword ? /* @__PURE__ */ jsx23(VisibilityOff2, {}) : /* @__PURE__ */ jsx23(Visibility2, {})
                    }
                  ) })
                }
              }
            }
          ),
          /* @__PURE__ */ jsx23(
            TextField3,
            {
              fullWidth: true,
              label: "Confirm password",
              type: showConfirmPassword ? "text" : "password",
              value: confirmPassword,
              onChange: handleConfirmPasswordChange,
              error: confirmError !== null,
              helperText: confirmError ?? " ",
              margin: "normal",
              autoComplete: "new-password",
              disabled: isLoading,
              slotProps: {
                input: {
                  endAdornment: /* @__PURE__ */ jsx23(InputAdornment2, { position: "end", children: /* @__PURE__ */ jsx23(
                    IconButton2,
                    {
                      "aria-label": showConfirmPassword ? "Hide password" : "Show password",
                      onClick: () => setShowConfirmPassword((prev) => !prev),
                      edge: "end",
                      disabled: isLoading,
                      sx: { minWidth: 44, minHeight: 44 },
                      children: showConfirmPassword ? /* @__PURE__ */ jsx23(VisibilityOff2, {}) : /* @__PURE__ */ jsx23(Visibility2, {})
                    }
                  ) })
                }
              }
            }
          ),
          /* @__PURE__ */ jsx23(Box5, { sx: { mt: 2, mb: 1 }, children: /* @__PURE__ */ jsx23(PasswordRequirements, { password }) }),
          /* @__PURE__ */ jsx23(
            Button4,
            {
              type: "submit",
              fullWidth: true,
              variant: "contained",
              size: "large",
              disabled: isLoading,
              sx: {
                mt: 2,
                mb: 2,
                py: 1.5,
                minHeight: 44,
                borderRadius: 1.5,
                fontWeight: 600,
                textTransform: "none",
                backgroundColor: preset.accentColor,
                "&:hover": {
                  backgroundColor: preset.accentColor,
                  filter: "brightness(0.92)"
                }
              },
              children: isLoading ? /* @__PURE__ */ jsx23(CircularProgress3, { size: 22, sx: { color: "inherit" } }) : "Reset password"
            }
          ),
          /* @__PURE__ */ jsx23(Box5, { sx: { display: "flex", justifyContent: "center" }, children: /* @__PURE__ */ jsx23(Link3, { component: RouterLink3, to: loginPath, underline: "hover", children: "Back to sign in" }) })
        ]
      }
    )
  ] }) }) });
}

// src/components/TenantSwitcher.tsx
import CheckIcon from "@mui/icons-material/Check";
import DomainIcon from "@mui/icons-material/Domain";
import ErrorOutlineIcon2 from "@mui/icons-material/ErrorOutline";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import {
  Box as Box6,
  Button as Button5,
  ButtonBase,
  Chip,
  CircularProgress as CircularProgress4,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  Menu,
  MenuItem,
  TextField as TextField4,
  Typography as Typography5,
  useTheme as useTheme2
} from "@mui/material";
import { useQueryClient as useQueryClient2 } from "@tanstack/react-query";
import { useCallback as useCallback9, useId as useId2, useMemo as useMemo11, useState as useState15 } from "react";
import { jsx as jsx24, jsxs as jsxs13 } from "react/jsx-runtime";
var SEARCH_THRESHOLD = 7;
var TIER_LABELS = {
  basic: "Basic",
  professional: "Professional",
  enterprise: "Enterprise"
};
var TIER_CHIP_COLOR = {
  basic: "default",
  professional: "info",
  enterprise: "warning"
};
function formatRoleLabel(role) {
  return role.split("_").map(
    (word) => word.length > 0 ? word.charAt(0).toUpperCase() + word.slice(1).toLowerCase() : word
  ).join(" ");
}
var VISUALLY_HIDDEN_SX = {
  position: "absolute",
  width: 1,
  height: 1,
  overflow: "hidden",
  clip: "rect(0,0,0,0)"
};
function TierBadge({ tier }) {
  const theme = useTheme2();
  const isFilled = tier !== "basic";
  return /* @__PURE__ */ jsx24(
    Chip,
    {
      size: "small",
      label: TIER_LABELS[tier],
      color: TIER_CHIP_COLOR[tier],
      variant: isFilled ? "filled" : "outlined",
      sx: {
        height: 20,
        fontSize: "0.6875rem",
        fontWeight: 600,
        ...isFilled && { color: theme.palette.common.black }
      }
    }
  );
}
function TenantSwitcher({
  onSwitched
}) {
  const { tenant: currentTenant, canSwitchTenants, switchTenant } = useTenant();
  const queryClient = useQueryClient2();
  const reducedMotion = usePrefersReducedMotion();
  const { hasUnsavedChanges } = useUnsavedChangesGuard(false);
  const theme = useTheme2();
  const membershipQuery = useApiQuery(
    ["tenants", "available"],
    "/api/v1/tenants/available",
    { enabled: canSwitchTenants, staleTime: 0 }
  );
  const [anchorEl, setAnchorEl] = useState15(null);
  const [searchQuery, setSearchQuery] = useState15("");
  const [switchState, setSwitchState] = useState15({
    status: "idle"
  });
  const [pendingTenantId, setPendingTenantId] = useState15(null);
  const [statusMessage, setStatusMessage] = useState15("");
  const [alertMessage, setAlertMessage] = useState15("");
  const menuId = useId2();
  const dialogTitleId = useId2();
  const dialogDescriptionId = useId2();
  const membershipData = membershipQuery.data;
  const rows = useMemo11(() => membershipData?.tenants ?? [], [membershipData]);
  const normalizedSearch = searchQuery.trim().toLowerCase();
  const filteredRows = useMemo11(
    () => normalizedSearch.length === 0 ? rows : rows.filter(
      (row) => row.name.toLowerCase().includes(normalizedSearch) || row.slug.toLowerCase().includes(normalizedSearch)
    ),
    [rows, normalizedSearch]
  );
  const open = anchorEl !== null;
  const isBusy = switchState.status === "switching";
  const performSwitch = useCallback9(
    async (tenantId) => {
      setSwitchState({ status: "switching", tenantId });
      setStatusMessage("Switching tenant\u2026");
      setAlertMessage("");
      const result = await switchTenant(tenantId);
      if (result.success) {
        queryClient.clear();
        if (typeof window !== "undefined") {
          window.dispatchEvent(
            new CustomEvent("plcy:tenant-switched", {
              detail: { tenantId }
            })
          );
        }
        setSwitchState({ status: "idle" });
        setStatusMessage(`Switched to ${result.data.name}.`);
        setSearchQuery("");
        setAnchorEl(null);
        onSwitched?.(result.data);
        return;
      }
      const { error } = result;
      const forbidden = isApiError(error) && error.status === 403;
      const message = forbidden ? error.message : "Network error \u2014 check your connection and try again.";
      setSwitchState({
        status: "error",
        tenantId,
        kind: forbidden ? "forbidden" : "network",
        message
      });
      setStatusMessage("");
      setAlertMessage(message);
    },
    [switchTenant, queryClient, onSwitched]
  );
  const handleRowClick = (row) => {
    if (row.id === currentTenant?.id || isBusy) {
      return;
    }
    if (hasUnsavedChanges) {
      setPendingTenantId(row.id);
      return;
    }
    void performSwitch(row.id);
  };
  const handleCancelGuard = () => {
    setPendingTenantId(null);
  };
  const handleConfirmDiscard = () => {
    const tenantId = pendingTenantId;
    setPendingTenantId(null);
    if (tenantId !== null) {
      void performSwitch(tenantId);
    }
  };
  const handleRetry = (event, tenantId) => {
    event.stopPropagation();
    void performSwitch(tenantId);
  };
  if (!canSwitchTenants) {
    return null;
  }
  return /* @__PURE__ */ jsxs13(Box6, { sx: { display: "inline-flex" }, children: [
    /* @__PURE__ */ jsx24(Box6, { role: "status", "aria-live": "polite", sx: VISUALLY_HIDDEN_SX, children: statusMessage }),
    /* @__PURE__ */ jsx24(Box6, { role: "alert", "aria-live": "assertive", sx: VISUALLY_HIDDEN_SX, children: alertMessage }),
    /* @__PURE__ */ jsxs13(
      ButtonBase,
      {
        onClick: (event) => setAnchorEl(event.currentTarget),
        disabled: isBusy,
        "aria-haspopup": "menu",
        "aria-expanded": open,
        "aria-controls": open ? menuId : void 0,
        "aria-label": currentTenant !== null ? `Switch tenant \u2014 current tenant: ${currentTenant.name}` : "Switch tenant",
        sx: {
          minHeight: 44,
          minWidth: 44,
          display: "flex",
          alignItems: "center",
          gap: 1,
          px: 1.25,
          borderRadius: 1.5,
          "&:hover": { backgroundColor: "action.hover" }
        },
        children: [
          /* @__PURE__ */ jsx24(DomainIcon, { fontSize: "small", sx: { color: "text.secondary" } }),
          /* @__PURE__ */ jsx24(
            Typography5,
            {
              variant: "body2",
              sx: {
                maxWidth: 160,
                overflow: "hidden",
                textOverflow: "ellipsis",
                whiteSpace: "nowrap"
              },
              children: currentTenant?.name ?? "Select tenant"
            }
          ),
          currentTenant !== null && /* @__PURE__ */ jsx24(TierBadge, { tier: currentTenant.tier }),
          isBusy ? /* @__PURE__ */ jsx24(CircularProgress4, { size: 16 }) : /* @__PURE__ */ jsx24(
            ExpandMoreIcon,
            {
              fontSize: "small",
              sx: {
                transform: open ? "rotate(180deg)" : "none",
                transition: reducedMotion ? "none" : "transform 150ms"
              }
            }
          )
        ]
      }
    ),
    /* @__PURE__ */ jsxs13(
      Menu,
      {
        id: menuId,
        anchorEl,
        open,
        onClose: () => setAnchorEl(null),
        MenuListProps: { "aria-label": "Switch Tenant" },
        transitionDuration: reducedMotion ? 0 : void 0,
        slotProps: { paper: { sx: { minWidth: 320, maxWidth: "90vw" } } },
        children: [
          rows.length > SEARCH_THRESHOLD && /* @__PURE__ */ jsx24(Box6, { sx: { px: 2, py: 1 }, onKeyDown: (e) => e.stopPropagation(), children: /* @__PURE__ */ jsx24(
            TextField4,
            {
              size: "small",
              fullWidth: true,
              label: "Search",
              value: searchQuery,
              onChange: (event) => setSearchQuery(event.target.value)
            }
          ) }),
          membershipQuery.isLoading && /* @__PURE__ */ jsxs13(MenuItem, { disabled: true, children: [
            /* @__PURE__ */ jsx24(CircularProgress4, { size: 16, sx: { mr: 1 } }),
            "Loading tenants\u2026"
          ] }),
          !membershipQuery.isLoading && filteredRows.length === 0 && /* @__PURE__ */ jsx24(MenuItem, { disabled: true, children: "No tenants match your search." }),
          filteredRows.map((row) => {
            const isCurrent = row.id === currentTenant?.id;
            const rowSwitching = switchState.status === "switching" && switchState.tenantId === row.id;
            const rowError = switchState.status === "error" && switchState.tenantId === row.id ? switchState : null;
            return /* @__PURE__ */ jsxs13(
              MenuItem,
              {
                disabled: isCurrent || isBusy,
                "aria-current": isCurrent ? "true" : void 0,
                onClick: () => handleRowClick(row),
                sx: {
                  minHeight: 48,
                  alignItems: "flex-start",
                  flexDirection: "column",
                  gap: 0.25
                },
                children: [
                  /* @__PURE__ */ jsxs13(
                    Box6,
                    {
                      sx: {
                        display: "flex",
                        alignItems: "center",
                        width: "100%",
                        gap: 1
                      },
                      children: [
                        isCurrent && /* @__PURE__ */ jsx24(CheckIcon, { fontSize: "small", color: "primary" }),
                        rowSwitching && /* @__PURE__ */ jsx24(CircularProgress4, { size: 16 }),
                        /* @__PURE__ */ jsxs13(Box6, { sx: { flexGrow: 1, minWidth: 0 }, children: [
                          /* @__PURE__ */ jsx24(
                            Typography5,
                            {
                              variant: "body2",
                              noWrap: true,
                              fontWeight: isCurrent ? 600 : 400,
                              children: row.name
                            }
                          ),
                          /* @__PURE__ */ jsxs13(Typography5, { variant: "caption", color: "text.secondary", noWrap: true, children: [
                            row.slug,
                            " \xB7 ",
                            formatRoleLabel(row.role)
                          ] })
                        ] }),
                        /* @__PURE__ */ jsx24(TierBadge, { tier: row.tier })
                      ]
                    }
                  ),
                  rowError !== null && /* @__PURE__ */ jsxs13(
                    Box6,
                    {
                      sx: {
                        display: "flex",
                        alignItems: "center",
                        gap: 1,
                        mt: 0.5,
                        width: "100%"
                      },
                      children: [
                        /* @__PURE__ */ jsx24(
                          ErrorOutlineIcon2,
                          {
                            fontSize: "small",
                            sx: { color: theme.palette.error.main }
                          }
                        ),
                        /* @__PURE__ */ jsx24(
                          Typography5,
                          {
                            variant: "caption",
                            sx: { color: theme.palette.error.main, flexGrow: 1 },
                            children: rowError.message
                          }
                        ),
                        /* @__PURE__ */ jsx24(
                          Button5,
                          {
                            size: "small",
                            onClick: (event) => handleRetry(event, row.id),
                            sx: { textTransform: "none", minHeight: 32 },
                            children: "Retry"
                          }
                        )
                      ]
                    }
                  )
                ]
              },
              row.id
            );
          })
        ]
      }
    ),
    /* @__PURE__ */ jsxs13(
      Dialog,
      {
        open: pendingTenantId !== null,
        onClose: handleCancelGuard,
        "aria-labelledby": dialogTitleId,
        "aria-describedby": dialogDescriptionId,
        children: [
          /* @__PURE__ */ jsx24(DialogTitle, { id: dialogTitleId, children: "Discard unsaved changes?" }),
          /* @__PURE__ */ jsx24(DialogContent, { children: /* @__PURE__ */ jsx24(DialogContentText, { id: dialogDescriptionId, children: "You have unsaved changes. Switching tenants now will discard them." }) }),
          /* @__PURE__ */ jsxs13(DialogActions, { children: [
            /* @__PURE__ */ jsx24(Button5, { onClick: handleCancelGuard, sx: { textTransform: "none" }, children: "Cancel" }),
            /* @__PURE__ */ jsx24(
              Button5,
              {
                onClick: handleConfirmDiscard,
                color: "error",
                variant: "contained",
                sx: { textTransform: "none" },
                children: "Discard"
              }
            )
          ] })
        ]
      }
    )
  ] });
}

// src/rbac/RbacConsole.tsx
import { Permission } from "@plcy-io/types";
import ExpandLessIcon from "@mui/icons-material/ExpandLess";
import ExpandMoreIcon2 from "@mui/icons-material/ExpandMore";
import InboxIcon from "@mui/icons-material/Inbox";
import LockIcon from "@mui/icons-material/Lock";
import SearchIcon from "@mui/icons-material/Search";
import SearchOffIcon from "@mui/icons-material/SearchOff";
import ShieldIcon from "@mui/icons-material/Shield";
import {
  Alert as Alert4,
  Avatar,
  Box as Box7,
  Chip as Chip2,
  CircularProgress as CircularProgress5,
  Collapse,
  Dialog as Dialog2,
  DialogActions as DialogActions2,
  DialogContent as DialogContent2,
  DialogContentText as DialogContentText2,
  DialogTitle as DialogTitle2,
  Divider as Divider2,
  FormControl,
  InputAdornment as InputAdornment3,
  InputLabel,
  List as List2,
  ListItemButton,
  ListItemAvatar,
  ListItemText as ListItemText2,
  MenuItem as MenuItem2,
  Button as Button6,
  Popover,
  Select,
  Snackbar,
  Stack,
  Switch,
  TextField as TextField5,
  Tooltip,
  Typography as Typography6
} from "@mui/material";
import Fuse from "fuse.js";
import { useCallback as useCallback10, useEffect as useEffect15, useMemo as useMemo12, useRef as useRef5, useState as useState16 } from "react";
import { List as VirtualList } from "react-window";

// src/rbac/service-labels.ts
var RBAC_SERVICE_LABELS = {
  "auth-service": "Authentication",
  "monitoring-service": "Monitoring & Observability",
  "platform-service": "Platform & Gateway",
  "workflow-service": "Workflows",
  "websocket-gateway": "Realtime Gateway",
  core: "Platform Core"
};
function getRbacServiceLabel(serviceId) {
  return RBAC_SERVICE_LABELS[serviceId] ?? serviceId;
}

// src/rbac/useRbacConsole.ts
import {
  useMutation as useMutation2,
  useQuery as useQuery2,
  useQueryClient as useQueryClient3
} from "@tanstack/react-query";

// src/rbac/rbac-graphql-client.ts
import { GraphQLClient, gql } from "graphql-request";
var logger6 = new FrontendLogger({
  component: "frontend-common:rbac-graphql"
});
var DEFAULT_GRAPHQL_PATH = "/graphql";
function resolveGraphqlUrl() {
  if (typeof window !== "undefined" && window.location) {
    return `${window.location.origin}${DEFAULT_GRAPHQL_PATH}`;
  }
  return DEFAULT_GRAPHQL_PATH;
}
var graphqlUrl = resolveGraphqlUrl();
logger6.debug("RBAC Console GraphQL client initialized", { url: graphqlUrl });
var rbacGraphqlClient = new GraphQLClient(graphqlUrl, {
  credentials: "include",
  requestMiddleware: (request) => {
    const tenantId = getTenantId();
    return {
      ...request,
      headers: {
        ...request.headers,
        ...tenantId !== null ? { "X-Tenant-ID": tenantId } : {}
      }
    };
  }
});

// src/rbac/useRbacConsole.ts
var ROLE_FRAGMENT = gql`
  fragment RoleFields on Role {
    id
    name
    serviceId
    moduleId
    isSystem
    description
    permissions {
      id
      name
      resourceType
      action
      serviceId
    }
  }
`;
var PERMISSION_FRAGMENT = gql`
  fragment PermissionFields on Permission {
    id
    name
    resourceType
    action
    serviceId
  }
`;
var ROLES_BY_SERVICE_QUERY = gql`
  ${ROLE_FRAGMENT}
  query RolesByService($serviceId: String) {
    rolesByService(serviceId: $serviceId) {
      ...RoleFields
    }
  }
`;
var USER_SEARCH_QUERY = gql`
  ${ROLE_FRAGMENT}
  ${PERMISSION_FRAGMENT}
  query UserSearch($tenantId: String, $search: String) {
    users(tenantId: $tenantId, search: $search) {
      id
      email
      name
      tenantId
      tenantSlug
      mfaEnabled
      roles {
        ...RoleFields
      }
      directPermissions {
        ...PermissionFields
      }
      effectivePermissions
    }
  }
`;
var USER_ACCESS_QUERY = gql`
  ${ROLE_FRAGMENT}
  ${PERMISSION_FRAGMENT}
  query UserAccess($userId: ID!) {
    user(userId: $userId) {
      id
      roles {
        ...RoleFields
      }
      directPermissions {
        ...PermissionFields
      }
      effectivePermissions
      mfaEnabled
    }
  }
`;
function useRolesByService(serviceId) {
  return useQuery2({
    queryKey: ["rbac", "rolesByService", serviceId],
    queryFn: async () => {
      const data = await rbacGraphqlClient.request(ROLES_BY_SERVICE_QUERY, { serviceId: serviceId ?? null });
      return data.rolesByService;
    },
    staleTime: 5 * 60 * 1e3
  });
}
function useRbacUserSearch(tenantId, search, options = {}) {
  return useQuery2({
    queryKey: ["rbac", "users", tenantId, search],
    queryFn: async () => {
      const data = await rbacGraphqlClient.request(
        USER_SEARCH_QUERY,
        { tenantId: tenantId ?? null, search }
      );
      return data.users;
    },
    staleTime: 30 * 1e3,
    enabled: options.enabled ?? true
  });
}
function useRbacUserAccess(userId) {
  return useQuery2({
    queryKey: ["rbac", "user", userId, "access"],
    queryFn: async () => {
      const data = await rbacGraphqlClient.request(
        USER_ACCESS_QUERY,
        { userId }
      );
      return data.user;
    },
    staleTime: 0,
    enabled: Boolean(userId)
  });
}
function recomputeEffectivePermissions(roles, directPermissions) {
  const effective = /* @__PURE__ */ new Set();
  for (const role of roles) {
    for (const permission of role.permissions) {
      effective.add(permission.name);
    }
  }
  for (const permission of directPermissions) {
    effective.add(permission.name);
  }
  return Array.from(effective).sort();
}
var TARGET_TENANT_HEADER = "X-Target-Tenant-ID";
function assignmentUrl(userId, roleId) {
  const base = `/api/v1/admin/rbac/users/${userId}/roles`;
  return roleId === void 0 ? base : `${base}/${roleId}`;
}
async function extractErrorMessage(response, fallback) {
  const body = await response.json().catch(() => ({}));
  return body.error ?? fallback;
}
async function postAssignRole(userId, roleId, targetTenantId) {
  const response = await fetch(assignmentUrl(userId), {
    method: "POST",
    credentials: "include",
    headers: {
      "Content-Type": "application/json",
      ...targetTenantId !== void 0 ? { [TARGET_TENANT_HEADER]: targetTenantId } : {}
    },
    body: JSON.stringify({ roleId })
  });
  if (!response.ok) {
    throw new Error(
      await extractErrorMessage(
        response,
        `Failed to assign role (${response.status.toString()})`
      )
    );
  }
}
async function deleteRevokeRole(userId, roleId, targetTenantId) {
  const response = await fetch(assignmentUrl(userId, roleId), {
    method: "DELETE",
    credentials: "include",
    ...targetTenantId !== void 0 ? { headers: { [TARGET_TENANT_HEADER]: targetTenantId } } : {}
  });
  if (!response.ok) {
    throw new Error(
      await extractErrorMessage(
        response,
        `Failed to revoke role (${response.status.toString()})`
      )
    );
  }
}
function useAssignRbacRole() {
  const queryClient = useQueryClient3();
  return useMutation2({
    mutationFn: async ({
      userId,
      role,
      targetTenantId
    }) => {
      await postAssignRole(userId, role.id, targetTenantId);
    },
    onMutate: async ({ userId, role }) => {
      const accessKey = ["rbac", "user", userId, "access"];
      await queryClient.cancelQueries({ queryKey: accessKey });
      const previous = queryClient.getQueryData(accessKey);
      if (previous) {
        const alreadyAssigned = previous.roles.some((r) => r.id === role.id);
        const nextRoles = alreadyAssigned ? previous.roles : [...previous.roles, role];
        queryClient.setQueryData(accessKey, {
          ...previous,
          roles: nextRoles,
          effectivePermissions: recomputeEffectivePermissions(
            nextRoles,
            previous.directPermissions
          )
        });
      }
      return { previous };
    },
    onError: (_error, { userId }, context) => {
      if (context?.previous) {
        queryClient.setQueryData(
          ["rbac", "user", userId, "access"],
          context.previous
        );
      }
    },
    onSettled: (_data, _error, { userId }) => {
      void queryClient.invalidateQueries({
        queryKey: ["rbac", "user", userId, "access"]
      });
      void queryClient.invalidateQueries({ queryKey: ["rbac", "users"] });
    }
  });
}
function useRevokeRbacRole() {
  const queryClient = useQueryClient3();
  return useMutation2({
    mutationFn: async ({
      userId,
      role,
      targetTenantId
    }) => {
      await deleteRevokeRole(userId, role.id, targetTenantId);
    },
    onMutate: async ({ userId, role }) => {
      const accessKey = ["rbac", "user", userId, "access"];
      await queryClient.cancelQueries({ queryKey: accessKey });
      const previous = queryClient.getQueryData(accessKey);
      if (previous) {
        const nextRoles = previous.roles.filter((r) => r.id !== role.id);
        queryClient.setQueryData(accessKey, {
          ...previous,
          roles: nextRoles,
          effectivePermissions: recomputeEffectivePermissions(
            nextRoles,
            previous.directPermissions
          )
        });
      }
      return { previous };
    },
    onError: (_error, { userId }, context) => {
      if (context?.previous) {
        queryClient.setQueryData(
          ["rbac", "user", userId, "access"],
          context.previous
        );
      }
    },
    onSettled: (_data, _error, { userId }) => {
      void queryClient.invalidateQueries({
        queryKey: ["rbac", "user", userId, "access"]
      });
      void queryClient.invalidateQueries({ queryKey: ["rbac", "users"] });
    }
  });
}

// src/rbac/RbacConsole.tsx
import { jsx as jsx25, jsxs as jsxs14 } from "react/jsx-runtime";
var DEBOUNCE_MS = 300;
var VIRTUALIZATION_THRESHOLD = 250;
var DIALOG_REASON_SYSTEM = "system-role";
var DIALOG_REASON_LOCKOUT = "self-lockout";
var PERMISSION_PULSE_MS = 1200;
var SUCCESS_TOAST_AUTO_HIDE_MS = 4e3;
var USER_SEARCH_FUZZY_THRESHOLD = 0.35;
var srOnlySx = {
  position: "absolute",
  width: 1,
  height: 1,
  padding: 0,
  margin: -1,
  overflow: "hidden",
  clip: "rect(0, 0, 0, 0)",
  whiteSpace: "nowrap",
  border: 0
};
function isModuleDisabled(serviceId) {
  return getRbacServiceLabel(serviceId) === serviceId;
}
function permissionRowHighlightSx(row, justAdded, justRemoved) {
  if (justAdded.has(row.name)) {
    return {
      bgcolor: "success.light",
      transition: "background-color 400ms ease"
    };
  }
  if (justRemoved.has(row.name)) {
    return {
      bgcolor: "error.light",
      opacity: 0.7,
      transition: "background-color 400ms ease"
    };
  }
  return {};
}
function RbacEmptyState({
  icon,
  title,
  description
}) {
  return /* @__PURE__ */ jsxs14(
    Box7,
    {
      role: "status",
      "aria-live": "polite",
      sx: {
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        justifyContent: "center",
        textAlign: "center",
        py: 4,
        px: 3
      },
      children: [
        /* @__PURE__ */ jsx25(Box7, { sx: { mb: 2, opacity: 0.6 }, children: icon }),
        /* @__PURE__ */ jsx25(
          Typography6,
          {
            variant: "h6",
            component: "h3",
            sx: { fontWeight: 600, color: "text.primary", mb: 1 },
            children: title
          }
        ),
        /* @__PURE__ */ jsx25(
          Typography6,
          {
            variant: "body2",
            color: "text.secondary",
            sx: { maxWidth: 480, lineHeight: 1.6 },
            children: description
          }
        )
      ]
    }
  );
}
function PermissionRow({
  ariaAttributes,
  index,
  style,
  rows,
  justAdded,
  justRemoved
}) {
  const row = rows[index];
  if (!row) {
    return /* @__PURE__ */ jsx25(Box7, { style, ...ariaAttributes });
  }
  return /* @__PURE__ */ jsxs14(
    Box7,
    {
      style,
      ...ariaAttributes,
      sx: {
        display: "flex",
        alignItems: "center",
        justifyContent: "space-between",
        px: 2,
        borderBottom: "1px solid",
        borderColor: "divider",
        ...permissionRowHighlightSx(row, justAdded, justRemoved)
      },
      children: [
        /* @__PURE__ */ jsx25(Typography6, { variant: "body2", sx: { fontFamily: "monospace" }, children: row.name }),
        /* @__PURE__ */ jsx25(Chip2, { label: row.sourceLabel, size: "small", variant: "outlined" })
      ]
    }
  );
}
function RbacConsole({ config }) {
  return /* @__PURE__ */ jsx25(
    PermissionGate,
    {
      permission: Permission.ROLE_READ,
      fallback: /* @__PURE__ */ jsx25(AccessDenied, { missingPermissions: [Permission.ROLE_READ] }),
      children: /* @__PURE__ */ jsx25(RbacConsoleContent, { config })
    }
  );
}
function RbacConsoleContent({ config }) {
  const { user } = useAuth();
  const { tenant: ownTenant } = useTenant();
  const { allowed: canAssignRole } = usePermission(Permission.ROLE_ASSIGN);
  const reducedMotion = usePrefersReducedMotion();
  const [searchInput, setSearchInput] = useState16("");
  const debouncedSearch = useDebouncedValue(searchInput, DEBOUNCE_MS);
  const [tenantFilter, setTenantFilter] = useState16(
    void 0
  );
  const [selectedUserId, setSelectedUserId] = useState16(null);
  const [autoSelectedForUserId, setAutoSelectedForUserId] = useState16(null);
  const [collapsedGroups, setCollapsedGroups] = useState16(
    /* @__PURE__ */ new Set()
  );
  const [confirmRevoke, setConfirmRevoke] = useState16(null);
  const [announcement, setAnnouncement] = useState16("");
  const [permissionPopover, setPermissionPopover] = useState16(null);
  const [successToast, setSuccessToast] = useState16(null);
  const [panelError, setPanelError] = useState16(null);
  const [justAddedNames, setJustAddedNames] = useState16(
    /* @__PURE__ */ new Set()
  );
  const [justRemovedRows, setJustRemovedRows] = useState16(/* @__PURE__ */ new Map());
  const previousPermissionRowsRef = useRef5(
    /* @__PURE__ */ new Map()
  );
  const awaitingMutationRefreshRef = useRef5(false);
  const pulseFrameRef = useRef5(null);
  const effectiveTenantId = config.showTenantSelector ? tenantFilter : ownTenant?.id ?? user?.tenantId;
  const tenantDirectoryQuery = useRbacUserSearch(void 0, "", {
    enabled: config.showTenantSelector
  });
  const tenantOptions = useMemo12(() => {
    const seen = /* @__PURE__ */ new Map();
    for (const directoryUser of tenantDirectoryQuery.data ?? []) {
      seen.set(directoryUser.tenantId, directoryUser.tenantSlug);
    }
    return Array.from(seen.entries()).map(([tenantId, tenantSlug]) => ({
      tenantId,
      tenantSlug
    }));
  }, [tenantDirectoryQuery.data]);
  const usersQuery = useRbacUserSearch(effectiveTenantId, "");
  const rolesQuery = useRolesByService();
  const accessQuery = useRbacUserAccess(selectedUserId ?? void 0);
  const assignMutation = useAssignRbacRole();
  const revokeMutation = useRevokeRbacRole();
  const users = useMemo12(() => usersQuery.data ?? [], [usersQuery.data]);
  const userFuse = useMemo12(
    () => new Fuse(users, {
      keys: ["name", "email"],
      threshold: USER_SEARCH_FUZZY_THRESHOLD
    }),
    [users]
  );
  const filteredUsers = useMemo12(() => {
    const query = debouncedSearch.trim();
    if (query === "") {
      return users;
    }
    return userFuse.search(query).map((result) => result.item);
  }, [debouncedSearch, users, userFuse]);
  const selectedUser = useMemo12(
    () => users.find((candidate) => candidate.id === selectedUserId) ?? null,
    [users, selectedUserId]
  );
  const firstUserId = filteredUsers[0]?.id ?? null;
  if (selectedUserId === null && firstUserId !== null && autoSelectedForUserId !== firstUserId) {
    setAutoSelectedForUserId(firstUserId);
    setSelectedUserId(firstUserId);
  }
  const groupedRoles = useMemo12(() => {
    const groups = /* @__PURE__ */ new Map();
    for (const role of rolesQuery.data ?? []) {
      const bucket = groups.get(role.serviceId) ?? [];
      bucket.push(role);
      groups.set(role.serviceId, bucket);
    }
    return Array.from(groups.entries()).sort(
      ([a], [b]) => getRbacServiceLabel(a).localeCompare(getRbacServiceLabel(b))
    );
  }, [rolesQuery.data]);
  const isRoleAssigned = useCallback10(
    (roleId) => accessQuery.data?.roles.some((role) => role.id === roleId) ?? false,
    [accessQuery.data]
  );
  const handleToggleGroup = useCallback10((serviceId) => {
    setCollapsedGroups((previous) => {
      const next = new Set(previous);
      if (next.has(serviceId)) {
        next.delete(serviceId);
      } else {
        next.add(serviceId);
      }
      return next;
    });
  }, []);
  const handleSearchChange = useCallback10(
    (event) => {
      setSearchInput(event.target.value);
    },
    []
  );
  const handleTenantChange = useCallback10((event) => {
    setTenantFilter(event.target.value === "" ? void 0 : event.target.value);
    setSelectedUserId(null);
  }, []);
  const handleOpenPermissionPopover = useCallback10(
    (event, role) => {
      setPermissionPopover({ anchorEl: event.currentTarget, role });
    },
    []
  );
  const handleClosePermissionPopover = useCallback10(() => {
    setPermissionPopover(null);
  }, []);
  const handleAssign = useCallback10(
    (role) => {
      if (selectedUserId === null) {
        return;
      }
      awaitingMutationRefreshRef.current = true;
      assignMutation.mutate(
        {
          userId: selectedUserId,
          role,
          // Cross-tenant mode only (Phase C): send the selected tenant so the
          // server takes the CROSS_TENANT:WRITE path. Own-tenant mode omits the
          // property entirely (exactOptionalPropertyTypes).
          ...config.showTenantSelector && effectiveTenantId != null ? { targetTenantId: effectiveTenantId } : {}
        },
        {
          onSuccess: () => {
            const message = `${role.name} assigned. ${role.permissions.length.toString()} permission(s) added.`;
            setAnnouncement(message);
            setPanelError(null);
            setSuccessToast(message);
          },
          onError: (error) => {
            const message = `Failed to assign ${role.name}: ${error.message}`;
            setAnnouncement(message);
            setPanelError(message);
          }
        }
      );
    },
    [
      selectedUserId,
      assignMutation,
      config.showTenantSelector,
      effectiveTenantId
    ]
  );
  const handleRevoke = useCallback10(
    (role) => {
      if (selectedUserId === null) {
        return;
      }
      awaitingMutationRefreshRef.current = true;
      revokeMutation.mutate(
        {
          userId: selectedUserId,
          role,
          ...config.showTenantSelector && effectiveTenantId != null ? { targetTenantId: effectiveTenantId } : {}
        },
        {
          onSuccess: () => {
            const message = `${role.name} revoked.`;
            setAnnouncement(message);
            setPanelError(null);
            setSuccessToast(message);
          },
          onError: (error) => {
            const message = `Failed to revoke ${role.name}: ${error.message}`;
            setAnnouncement(message);
            setPanelError(message);
          }
        }
      );
    },
    [
      selectedUserId,
      revokeMutation,
      config.showTenantSelector,
      effectiveTenantId
    ]
  );
  const handleCloseSuccessToast = useCallback10(
    (_event, reason) => {
      if (reason === "clickaway") {
        return;
      }
      setSuccessToast(null);
    },
    []
  );
  const handleDismissPanelError = useCallback10(() => {
    setPanelError(null);
    assignMutation.reset();
    revokeMutation.reset();
  }, [assignMutation, revokeMutation]);
  const wouldSelfLockout = useCallback10(
    (role) => {
      const access = accessQuery.data;
      if (!access || selectedUserId !== (user?.id ?? null)) {
        return false;
      }
      const nextRoles = access.roles.filter(
        (candidate) => candidate.id !== role.id
      );
      const nextEffective = recomputeEffectivePermissions(
        nextRoles,
        access.directPermissions
      );
      return !nextEffective.includes(Permission.ROLE_READ);
    },
    [accessQuery.data, selectedUserId, user?.id]
  );
  const handleToggleRole = useCallback10(
    (role, assigned) => {
      if (!assigned) {
        handleAssign(role);
        return;
      }
      if (role.isSystem) {
        setConfirmRevoke({ role, reason: DIALOG_REASON_SYSTEM });
        return;
      }
      if (wouldSelfLockout(role)) {
        setConfirmRevoke({ role, reason: DIALOG_REASON_LOCKOUT });
        return;
      }
      handleRevoke(role);
    },
    [handleAssign, handleRevoke, wouldSelfLockout]
  );
  const handleConfirmRevoke = useCallback10(() => {
    if (confirmRevoke) {
      handleRevoke(confirmRevoke.role);
      setConfirmRevoke(null);
    }
  }, [confirmRevoke, handleRevoke]);
  const handleCancelRevoke = useCallback10(() => {
    setConfirmRevoke(null);
  }, []);
  const effectivePermissionRows = useMemo12(() => {
    const access = accessQuery.data;
    if (!access) {
      return [];
    }
    const rolesByPermission = /* @__PURE__ */ new Map();
    for (const role of access.roles) {
      for (const permission of role.permissions) {
        const existing = rolesByPermission.get(permission.name) ?? [];
        existing.push(role.name);
        rolesByPermission.set(permission.name, existing);
      }
    }
    const directNames = new Set(
      access.directPermissions.map((permission) => permission.name)
    );
    return access.effectivePermissions.map((name) => {
      const grantingRoles = rolesByPermission.get(name) ?? [];
      const sources = directNames.has(name) ? [...grantingRoles, "Direct Grant"] : grantingRoles;
      const overflow = sources.length - 1;
      const primary = sources[0] ?? "Direct Grant";
      let sourceType = "role";
      if (sources.length > 1) {
        sourceType = "multiple";
      } else if (primary === "Direct Grant") {
        sourceType = "direct";
      }
      return {
        name,
        sourceLabel: overflow > 0 ? `${primary} +${overflow.toString()}` : primary,
        sourceType
      };
    });
  }, [accessQuery.data]);
  const effectivePermissionCount = effectivePermissionRows.length;
  useEffect15(() => {
    const currentByName = new Map(
      effectivePermissionRows.map((row) => [row.name, row])
    );
    const previousByName = previousPermissionRowsRef.current;
    previousPermissionRowsRef.current = currentByName;
    if (!awaitingMutationRefreshRef.current) {
      return void 0;
    }
    awaitingMutationRefreshRef.current = false;
    if (reducedMotion) {
      return void 0;
    }
    const added = /* @__PURE__ */ new Set();
    for (const name of currentByName.keys()) {
      if (!previousByName.has(name)) {
        added.add(name);
      }
    }
    const removed = /* @__PURE__ */ new Map();
    for (const [name, row] of previousByName) {
      if (!currentByName.has(name)) {
        removed.set(name, row);
      }
    }
    if (added.size === 0 && removed.size === 0) {
      return void 0;
    }
    pulseFrameRef.current = requestAnimationFrame(() => {
      pulseFrameRef.current = null;
      setJustAddedNames(added);
      setJustRemovedRows(removed);
    });
    const timer = setTimeout(() => {
      setJustAddedNames(/* @__PURE__ */ new Set());
      setJustRemovedRows(/* @__PURE__ */ new Map());
    }, PERMISSION_PULSE_MS);
    return () => {
      if (pulseFrameRef.current !== null) {
        cancelAnimationFrame(pulseFrameRef.current);
        pulseFrameRef.current = null;
      }
      clearTimeout(timer);
    };
  }, [effectivePermissionRows, reducedMotion]);
  const justRemovedNames = useMemo12(
    () => new Set(justRemovedRows.keys()),
    [justRemovedRows]
  );
  const displayPermissionRows = useMemo12(() => {
    if (justRemovedRows.size === 0) {
      return effectivePermissionRows;
    }
    return [...effectivePermissionRows, ...justRemovedRows.values()];
  }, [effectivePermissionRows, justRemovedRows]);
  const displayPermissionRowCount = displayPermissionRows.length;
  const confirmMessage = confirmRevoke?.reason === DIALOG_REASON_SYSTEM ? `${confirmRevoke.role.name} is a system role. Revoking it may remove privileges other users rely on. Continue?` : `Revoking ${confirmRevoke?.role.name ?? ""} will remove your own Role Read access. You will lose the ability to manage roles until another admin restores it. Continue?`;
  return /* @__PURE__ */ jsxs14(Box7, { sx: { display: "flex", flexDirection: "column", height: "100%" }, children: [
    /* @__PURE__ */ jsx25("span", { "aria-live": "polite", role: "status", style: srOnlySx, children: announcement }),
    /* @__PURE__ */ jsxs14(Box7, { sx: { px: 3, pt: 3, pb: 2 }, children: [
      /* @__PURE__ */ jsx25(Typography6, { variant: "h5", component: "h1", sx: { fontWeight: 600 }, children: "Security Operations" }),
      /* @__PURE__ */ jsx25(Typography6, { variant: "body2", color: "text.secondary", sx: { mt: 0.5 }, children: "Search users, assign or revoke module roles, and review effective permissions." })
    ] }),
    /* @__PURE__ */ jsxs14(
      Box7,
      {
        sx: {
          display: "flex",
          flexDirection: { xs: "column", md: "row" },
          gap: 2,
          px: 3,
          pb: 3,
          flex: 1,
          minHeight: 0
        },
        children: [
          /* @__PURE__ */ jsxs14(
            Box7,
            {
              sx: {
                flex: { xs: "1 1 auto", md: "0 0 30%" },
                display: "flex",
                flexDirection: "column",
                border: "1px solid",
                borderColor: "divider",
                borderRadius: 2,
                minHeight: 0
              },
              children: [
                /* @__PURE__ */ jsxs14(Box7, { sx: { p: 2 }, children: [
                  /* @__PURE__ */ jsx25(
                    TextField5,
                    {
                      fullWidth: true,
                      size: "small",
                      label: "Search",
                      placeholder: "Search by name or email",
                      value: searchInput,
                      onChange: handleSearchChange,
                      slotProps: {
                        input: {
                          startAdornment: /* @__PURE__ */ jsx25(InputAdornment3, { position: "start", children: /* @__PURE__ */ jsx25(SearchIcon, { fontSize: "small" }) })
                        }
                      }
                    }
                  ),
                  config.showTenantSelector ? /* @__PURE__ */ jsxs14(FormControl, { fullWidth: true, size: "small", sx: { mt: 1.5 }, children: [
                    /* @__PURE__ */ jsx25(InputLabel, { id: "rbac-console-tenant-filter-label", children: "Tenant" }),
                    /* @__PURE__ */ jsxs14(
                      Select,
                      {
                        labelId: "rbac-console-tenant-filter-label",
                        label: "Tenant",
                        value: tenantFilter ?? "",
                        onChange: handleTenantChange,
                        children: [
                          /* @__PURE__ */ jsx25(MenuItem2, { value: "", children: "All tenants" }),
                          tenantOptions.map((option) => /* @__PURE__ */ jsx25(MenuItem2, { value: option.tenantId, children: option.tenantSlug }, option.tenantId))
                        ]
                      }
                    )
                  ] }) : null
                ] }),
                /* @__PURE__ */ jsx25(Divider2, {}),
                /* @__PURE__ */ jsx25(Box7, { sx: { flex: 1, overflow: "auto", minHeight: 200 }, children: usersQuery.isLoading ? /* @__PURE__ */ jsx25(Box7, { sx: { display: "flex", justifyContent: "center", p: 4 }, children: /* @__PURE__ */ jsx25(CircularProgress5, { size: 28 }) }) : filteredUsers.length === 0 ? /* @__PURE__ */ jsx25(
                  RbacEmptyState,
                  {
                    icon: /* @__PURE__ */ jsx25(
                      SearchOffIcon,
                      {
                        sx: { fontSize: 64, color: "text.disabled" }
                      }
                    ),
                    title: "No users found",
                    description: "Try a different search term or tenant."
                  }
                ) : /* @__PURE__ */ jsx25(
                  List2,
                  {
                    "aria-label": "Users",
                    role: "listbox",
                    dense: true,
                    "data-testid": "rbac-console-user-list",
                    children: filteredUsers.map((candidate) => /* @__PURE__ */ jsxs14(
                      ListItemButton,
                      {
                        role: "option",
                        "aria-selected": candidate.id === selectedUserId,
                        selected: candidate.id === selectedUserId,
                        onClick: () => {
                          setSelectedUserId(candidate.id);
                        },
                        sx: { minHeight: 44 },
                        children: [
                          /* @__PURE__ */ jsx25(ListItemAvatar, { children: /* @__PURE__ */ jsx25(Avatar, { sx: { width: 32, height: 32, fontSize: 14 }, children: candidate.name.split(" ").map((part) => part.charAt(0)).join("").slice(0, 2).toUpperCase() }) }),
                          /* @__PURE__ */ jsx25(
                            ListItemText2,
                            {
                              primary: candidate.name,
                              secondary: candidate.email
                            }
                          ),
                          /* @__PURE__ */ jsxs14(Stack, { direction: "row", spacing: 0.5, alignItems: "center", children: [
                            candidate.mfaEnabled ? /* @__PURE__ */ jsx25(Tooltip, { title: "MFA enabled", children: /* @__PURE__ */ jsx25(ShieldIcon, { fontSize: "small", color: "success" }) }) : null,
                            /* @__PURE__ */ jsx25(
                              Chip2,
                              {
                                label: candidate.roles.length,
                                size: "small",
                                "aria-label": `${candidate.roles.length.toString()} roles`
                              }
                            ),
                            config.showTenantSelector ? /* @__PURE__ */ jsx25(
                              Chip2,
                              {
                                label: candidate.tenantSlug,
                                size: "small",
                                variant: "outlined"
                              }
                            ) : null
                          ] })
                        ]
                      },
                      candidate.id
                    ))
                  }
                ) })
              ]
            }
          ),
          /* @__PURE__ */ jsxs14(
            Box7,
            {
              sx: {
                flex: { xs: "1 1 auto", md: "0 0 45%" },
                display: "flex",
                flexDirection: "column",
                border: "1px solid",
                borderColor: "divider",
                borderRadius: 2,
                minHeight: 0,
                overflow: "auto",
                ...panelError ? {
                  outline: "2px solid",
                  outlineColor: "error.main",
                  outlineOffset: -1
                } : {},
                ...panelError && !reducedMotion ? {
                  animation: "rbacConsolePanelShake 300ms ease-in-out",
                  "@keyframes rbacConsolePanelShake": {
                    "0%": { transform: "translateX(0)" },
                    "20%": { transform: "translateX(-6px)" },
                    "40%": { transform: "translateX(6px)" },
                    "60%": { transform: "translateX(-4px)" },
                    "80%": { transform: "translateX(4px)" },
                    "100%": { transform: "translateX(0)" }
                  }
                } : {}
              },
              "data-testid": "rbac-console-role-list",
              "data-error-active": panelError != null ? "true" : void 0,
              children: [
                panelError ? /* @__PURE__ */ jsx25(Box7, { sx: { p: 2, pb: 0 }, children: /* @__PURE__ */ jsx25(
                  Alert4,
                  {
                    severity: "error",
                    role: "alert",
                    onClose: handleDismissPanelError,
                    children: panelError
                  }
                ) }) : null,
                rolesQuery.isLoading || selectedUserId !== null && accessQuery.isLoading ? /* @__PURE__ */ jsx25(Box7, { sx: { display: "flex", justifyContent: "center", p: 4 }, children: /* @__PURE__ */ jsx25(CircularProgress5, { size: 28 }) }) : groupedRoles.length === 0 ? /* @__PURE__ */ jsx25(
                  RbacEmptyState,
                  {
                    icon: /* @__PURE__ */ jsx25(InboxIcon, { sx: { fontSize: 64, color: "text.disabled" } }),
                    title: "No roles registered for this module yet",
                    description: "Assign a role to get started once one becomes available."
                  }
                ) : selectedUserId === null ? /* @__PURE__ */ jsx25(
                  RbacEmptyState,
                  {
                    icon: /* @__PURE__ */ jsx25(InboxIcon, { sx: { fontSize: 64, color: "text.disabled" } }),
                    title: "Select a user",
                    description: "Choose a user from the list to view and manage their roles."
                  }
                ) : /* @__PURE__ */ jsx25(Box7, { role: "listbox", "aria-label": "Roles by service", children: groupedRoles.map(([serviceId, roles]) => {
                  const expanded = !collapsedGroups.has(serviceId);
                  const groupHeaderId = `rbac-console-group-${serviceId}`;
                  return /* @__PURE__ */ jsxs14(Box7, { children: [
                    /* @__PURE__ */ jsxs14(
                      ListItemButton,
                      {
                        id: groupHeaderId,
                        onClick: () => {
                          handleToggleGroup(serviceId);
                        },
                        "aria-expanded": expanded,
                        sx: {
                          position: "sticky",
                          top: 0,
                          bgcolor: "background.paper",
                          zIndex: 1,
                          minHeight: 44
                        },
                        children: [
                          /* @__PURE__ */ jsx25(
                            ListItemText2,
                            {
                              primary: /* @__PURE__ */ jsxs14(
                                Stack,
                                {
                                  direction: "row",
                                  spacing: 1,
                                  alignItems: "center",
                                  children: [
                                    /* @__PURE__ */ jsx25(Typography6, { sx: { fontWeight: 600 }, children: getRbacServiceLabel(serviceId) }),
                                    isModuleDisabled(serviceId) ? /* @__PURE__ */ jsx25(
                                      Chip2,
                                      {
                                        label: "Module disabled",
                                        size: "small",
                                        color: "warning",
                                        variant: "outlined"
                                      }
                                    ) : null
                                  ]
                                }
                              )
                            }
                          ),
                          expanded ? /* @__PURE__ */ jsx25(ExpandLessIcon, {}) : /* @__PURE__ */ jsx25(ExpandMoreIcon2, {})
                        ]
                      }
                    ),
                    /* @__PURE__ */ jsx25(
                      Collapse,
                      {
                        in: expanded,
                        timeout: reducedMotion ? 0 : 200,
                        unmountOnExit: true,
                        children: /* @__PURE__ */ jsx25(
                          Box7,
                          {
                            role: "group",
                            "aria-labelledby": groupHeaderId,
                            sx: { pl: 2 },
                            children: roles.map((role) => {
                              const assigned = isRoleAssigned(role.id);
                              const systemLocked = role.isSystem && !config.canEditSystemRoles;
                              const toggleDisabled = !canAssignRole || systemLocked || selectedUserId === null;
                              const toggleLabel = `${assigned ? "Revoke" : "Assign"} ${role.name} ${assigned ? "from" : "to"} ${selectedUser?.name ?? "user"}`;
                              return /* @__PURE__ */ jsxs14(
                                Box7,
                                {
                                  role: "option",
                                  "aria-selected": assigned,
                                  sx: {
                                    display: "flex",
                                    alignItems: "center",
                                    justifyContent: "space-between",
                                    gap: 1,
                                    py: 1,
                                    pr: 2,
                                    borderBottom: "1px solid",
                                    borderColor: "divider"
                                  },
                                  children: [
                                    /* @__PURE__ */ jsxs14(Box7, { sx: { minWidth: 0, flex: 1 }, children: [
                                      /* @__PURE__ */ jsxs14(
                                        Stack,
                                        {
                                          direction: "row",
                                          spacing: 0.75,
                                          alignItems: "center",
                                          children: [
                                            /* @__PURE__ */ jsx25(
                                              Typography6,
                                              {
                                                variant: "body2",
                                                sx: {
                                                  fontFamily: "monospace",
                                                  fontWeight: 600
                                                },
                                                children: role.name
                                              }
                                            ),
                                            role.isSystem ? /* @__PURE__ */ jsx25(Tooltip, { title: "System role", children: /* @__PURE__ */ jsx25(
                                              Chip2,
                                              {
                                                icon: /* @__PURE__ */ jsx25(LockIcon, { fontSize: "small" }),
                                                label: "System",
                                                size: "small"
                                              }
                                            ) }) : null
                                          ]
                                        }
                                      ),
                                      role.description ? /* @__PURE__ */ jsx25(Tooltip, { title: role.description, children: /* @__PURE__ */ jsx25(
                                        Typography6,
                                        {
                                          variant: "caption",
                                          color: "text.secondary",
                                          noWrap: true,
                                          sx: { display: "block", maxWidth: 320 },
                                          children: role.description
                                        }
                                      ) }) : null
                                    ] }),
                                    /* @__PURE__ */ jsx25(
                                      Button6,
                                      {
                                        size: "small",
                                        onClick: (event) => {
                                          handleOpenPermissionPopover(event, role);
                                        },
                                        "aria-label": `${role.permissions.length.toString()} permissions in ${role.name}`,
                                        children: role.permissions.length
                                      }
                                    ),
                                    /* @__PURE__ */ jsx25(
                                      Tooltip,
                                      {
                                        title: systemLocked ? "Only a system RBAC admin can assign system roles" : "",
                                        children: /* @__PURE__ */ jsx25("span", { children: /* @__PURE__ */ jsx25(
                                          Switch,
                                          {
                                            checked: assigned,
                                            disabled: toggleDisabled,
                                            onChange: () => {
                                              handleToggleRole(role, assigned);
                                            },
                                            slotProps: {
                                              input: {
                                                role: "switch",
                                                "aria-label": toggleLabel
                                              }
                                            },
                                            sx: { minWidth: 44, minHeight: 44 }
                                          }
                                        ) })
                                      }
                                    )
                                  ]
                                },
                                role.id
                              );
                            })
                          }
                        )
                      }
                    )
                  ] }, serviceId);
                }) })
              ]
            }
          ),
          /* @__PURE__ */ jsxs14(
            Box7,
            {
              sx: {
                flex: { xs: "1 1 auto", md: "0 0 25%" },
                display: "flex",
                flexDirection: "column",
                border: "1px solid",
                borderColor: "divider",
                borderRadius: 2,
                minHeight: 0,
                overflow: "auto"
              },
              children: [
                /* @__PURE__ */ jsxs14(Box7, { sx: { p: 2 }, children: [
                  /* @__PURE__ */ jsx25(Typography6, { variant: "overline", color: "text.secondary", children: "Effective permissions" }),
                  /* @__PURE__ */ jsx25(
                    Typography6,
                    {
                      variant: "h4",
                      "data-testid": "rbac-console-effective-permission-count",
                      sx: {
                        fontWeight: 700,
                        ...reducedMotion ? {} : {
                          animation: "rbacConsoleCountPop 600ms ease-out",
                          "@keyframes rbacConsoleCountPop": {
                            "0%": { transform: "scale(0.85)", opacity: 0.4 },
                            "100%": { transform: "scale(1)", opacity: 1 }
                          }
                        }
                      },
                      children: effectivePermissionCount
                    },
                    effectivePermissionCount
                  )
                ] }),
                /* @__PURE__ */ jsx25(Divider2, {}),
                selectedUserId !== null && accessQuery.isLoading ? /* @__PURE__ */ jsx25(Box7, { sx: { display: "flex", justifyContent: "center", p: 4 }, children: /* @__PURE__ */ jsx25(CircularProgress5, { size: 28 }) }) : displayPermissionRowCount === 0 ? /* @__PURE__ */ jsx25(
                  RbacEmptyState,
                  {
                    icon: /* @__PURE__ */ jsx25(InboxIcon, { sx: { fontSize: 64, color: "text.disabled" } }),
                    title: "This user has no permissions",
                    description: "Assign a role to grant access."
                  }
                ) : displayPermissionRowCount > VIRTUALIZATION_THRESHOLD ? /* @__PURE__ */ jsx25(
                  Box7,
                  {
                    role: "list",
                    "aria-label": "Effective permissions",
                    sx: { height: 480 },
                    children: /* @__PURE__ */ jsx25(
                      VirtualList,
                      {
                        rowComponent: PermissionRow,
                        rowCount: displayPermissionRows.length,
                        rowHeight: 40,
                        rowProps: {
                          rows: displayPermissionRows,
                          justAdded: justAddedNames,
                          justRemoved: justRemovedNames
                        },
                        style: { height: 480 }
                      }
                    )
                  }
                ) : /* @__PURE__ */ jsx25(List2, { dense: true, "aria-label": "Effective permissions", children: displayPermissionRows.map((row) => /* @__PURE__ */ jsxs14(
                  Box7,
                  {
                    role: "option",
                    "aria-selected": false,
                    sx: {
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "space-between",
                      px: 2,
                      py: 0.75,
                      ...permissionRowHighlightSx(
                        row,
                        justAddedNames,
                        justRemovedNames
                      )
                    },
                    children: [
                      /* @__PURE__ */ jsx25(Typography6, { variant: "body2", sx: { fontFamily: "monospace" }, children: row.name }),
                      /* @__PURE__ */ jsx25(
                        Chip2,
                        {
                          label: row.sourceLabel,
                          size: "small",
                          variant: "outlined",
                          color: row.sourceType === "direct" ? "default" : "primary"
                        }
                      )
                    ]
                  },
                  row.name
                )) })
              ]
            }
          )
        ]
      }
    ),
    /* @__PURE__ */ jsx25(
      Popover,
      {
        open: permissionPopover !== null,
        anchorEl: permissionPopover?.anchorEl,
        onClose: handleClosePermissionPopover,
        anchorOrigin: { vertical: "bottom", horizontal: "left" },
        children: /* @__PURE__ */ jsxs14(Box7, { sx: { p: 2, maxWidth: 320 }, children: [
          /* @__PURE__ */ jsxs14(Typography6, { variant: "subtitle2", sx: { mb: 1 }, children: [
            permissionPopover?.role.name,
            " permissions"
          ] }),
          /* @__PURE__ */ jsx25(Stack, { spacing: 0.5, children: permissionPopover?.role.permissions.map((permission) => /* @__PURE__ */ jsx25(
            Typography6,
            {
              variant: "caption",
              sx: { fontFamily: "monospace" },
              children: permission.name
            },
            permission.id
          )) })
        ] })
      }
    ),
    /* @__PURE__ */ jsxs14(Dialog2, { open: confirmRevoke !== null, onClose: handleCancelRevoke, children: [
      /* @__PURE__ */ jsx25(DialogTitle2, { children: "Confirm role revocation" }),
      /* @__PURE__ */ jsx25(DialogContent2, { children: /* @__PURE__ */ jsx25(DialogContentText2, { children: confirmMessage }) }),
      /* @__PURE__ */ jsxs14(DialogActions2, { children: [
        /* @__PURE__ */ jsx25(Button6, { onClick: handleCancelRevoke, children: "Cancel" }),
        /* @__PURE__ */ jsx25(
          Button6,
          {
            onClick: handleConfirmRevoke,
            color: "error",
            variant: "contained",
            children: "Revoke"
          }
        )
      ] })
    ] }),
    /* @__PURE__ */ jsx25(
      Snackbar,
      {
        open: successToast !== null,
        autoHideDuration: SUCCESS_TOAST_AUTO_HIDE_MS,
        onClose: handleCloseSuccessToast,
        anchorOrigin: { vertical: "bottom", horizontal: "right" },
        children: /* @__PURE__ */ jsx25(
          Alert4,
          {
            severity: "success",
            variant: "filled",
            onClose: handleCloseSuccessToast,
            children: successToast
          }
        )
      }
    )
  ] });
}

// src/rbac/types.ts
var ROLE_AUTHORING_ENDPOINTS = {
  "tenant-scoped": "/api/v1/admin/rbac/tenant-roles",
  "system-and-tenant": "/api/v1/admin/rbac/roles"
};
function resolveRoleAuthoringEndpoint(config) {
  return ROLE_AUTHORING_ENDPOINTS[config.roleAuthoring];
}

// src/types/index.ts
import { z } from "@plcy-io/validation";

// src/types/module-manifest.ts
function createNavPath(basePath, itemPath) {
  if (itemPath === "" || itemPath === "/") {
    return basePath;
  }
  return `${basePath}${itemPath}`;
}
function sortModulesByOrder(modules) {
  return [...modules].sort((a, b) => (a.order ?? 100) - (b.order ?? 100));
}
function filterModulesByPermissions(modules, userPermissions) {
  return modules.filter((module) => {
    if (!module.requiredPermission) return true;
    return userPermissions.includes(module.requiredPermission);
  });
}
function groupModulesByCategory(modules) {
  const groups = {
    main: [],
    financial: [],
    analytics: [],
    admin: []
  };
  for (const module of modules) {
    const category = module.category ?? "main";
    groups[category] ??= [];
    groups[category].push(module);
  }
  return groups;
}

// src/types/index.ts
var UserSchema = z.object({
  id: z.string().min(1),
  email: z.string().email(),
  name: z.string(),
  roles: z.array(z.string()),
  permissions: z.array(z.string()).optional(),
  tenantId: z.string().min(1).optional()
});
var hexColorRegex = /^#([0-9A-Fa-f]{3}|[0-9A-Fa-f]{6})$/;
var TenantSchema = z.object({
  id: z.string().min(1),
  name: z.string().min(1),
  slug: z.string().min(1),
  status: z.enum([
    "active",
    "inactive",
    "suspended",
    "provisioning",
    "pending_deletion",
    "deleted"
  ]),
  tier: z.enum(["basic", "professional", "enterprise"]),
  features: z.array(z.string()),
  settings: z.record(z.string(), z.unknown()),
  // Branding fields
  logoUrl: z.string().url().nullable().optional(),
  logoIconUrl: z.string().url().nullable().optional(),
  logoDarkUrl: z.string().url().nullable().optional(),
  faviconUrl: z.string().url().nullable().optional(),
  primaryColor: z.string().regex(hexColorRegex).nullable().optional(),
  secondaryColor: z.string().regex(hexColorRegex).nullable().optional()
});
var LoginCredentialsSchema = z.object({
  email: z.string().email({ message: "Invalid email address" }),
  password: z.string().min(8, "Password must be at least 8 characters")
});
function isValidUser(value) {
  return UserSchema.safeParse(value).success;
}
function isValidTenant(value) {
  return TenantSchema.safeParse(value).success;
}
function isValidLoginCredentials(value) {
  return LoginCredentialsSchema.safeParse(value).success;
}
export {
  AccessDenied,
  AdminOnly,
  AnimatedLogo,
  AnimatedSlogan,
  ApiError,
  AppProviders,
  AuthProvider,
  BrandedLogo,
  CornerRibbon,
  CrossAppLink,
  DEFAULT_BRANDING,
  DynamicFavicon,
  ErrorBoundary,
  FrontendLogger,
  FullPageSpinner,
  LoadingSpinner,
  LoginCredentialsSchema,
  LoginPage,
  MockWebSocketTransport,
  PASSWORD_REQUIREMENTS,
  PORTAL_PRESETS,
  PasswordRequirements,
  PasswordResetConfirmPage,
  PasswordResetRequestPage,
  PermissionGate,
  PersonaContext,
  PersonaProvider,
  PlcyLogo,
  ProtectedRoute,
  RBAC_SERVICE_LABELS,
  RbacConsole,
  RequireAuth,
  RequirePermission,
  RequireRole,
  RequireTenant,
  StorageError,
  SystemAdminOnly,
  TenantBrandingProvider,
  TenantFeatureGate,
  TenantGuard,
  TenantProvider,
  TenantSchema,
  TenantSwitcher,
  TenantTierGate,
  UnsavedChangesProvider,
  UserSchema,
  WebSocketTransportProvider,
  apiClient,
  apiClientPort,
  buildCrossAppPath,
  clearAllStorage,
  createApiClient,
  createApiClientPort,
  createModuleQueryClient,
  createNavPath,
  createQueryClient,
  createStoryQueryClient,
  filterModulesByPermissions,
  getCorrelationId,
  getErrorMessage,
  getFieldError,
  getFieldErrors,
  getMetPasswordRequirements,
  getRbacServiceLabel,
  getSidebarOpen,
  getTenantId,
  getThemeMode,
  groupModulesByCategory,
  isApiError,
  isAuthError,
  isNetworkError,
  isNotFoundError,
  isPasswordValid,
  isPermissionError,
  isValidLoginCredentials,
  isValidTenant,
  isValidUser,
  isValidationError,
  normalizeError,
  recomputeEffectivePermissions,
  refreshCorrelationId,
  removeTenantId,
  resolveRoleAuthoringEndpoint,
  setSidebarOpen,
  setTenantId,
  setThemeMode,
  sortModulesByOrder,
  useApiError,
  useApiMutation,
  useApiQuery,
  useAssignRbacRole,
  useAuth,
  useAuthState,
  useCurrentTenant,
  useDebouncedValue,
  useIsAdmin,
  useIsSystemAdmin,
  useIsTenantAdmin,
  usePaginatedQuery,
  usePermission,
  usePermissions,
  usePersonaContext,
  usePrefersReducedMotion,
  useRbacUserAccess,
  useRbacUserSearch,
  useRevokeRbacRole,
  useRole,
  useRoles,
  useRolesByService,
  useTenant,
  useTenantBranding,
  useTenantFeature,
  useTenantState,
  useUnsavedChanges,
  useUnsavedChangesGuard,
  useUser,
  useWebSocketSubscription,
  useWebSocketTransport,
  withErrorBoundary
};
//# sourceMappingURL=index.js.map